import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Camera,
  Check,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Cloud,
  CreditCard,
  Crown,
  Database,
  Download,
  ExternalLink,
  FolderKanban,
  Heart,
  Loader2,
  LogOut,
  Layers3,
  MessageSquare,
  Minus,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  ScanLine,
  Search,
  Send,
  Sparkles,
  Trash2,
  TriangleAlert,
  Trophy,
  UserRound,
  X,
} from 'lucide-react'
import './App.css'
import { SportsDetailsEditor } from './SportsDetailsEditor'
import { refreshCandidatePrice, searchCards } from './api'
import { cardVaultAppVersion } from './appMetadata'
import {
  buildCoachCardInput,
  recordGooglePlayCoachPurchase,
  recordMicrosoftStoreCoachPurchase,
  requestCoach,
  requestCoachCreditStatus,
  requestCommanderSuggestions,
  type CoachCardRef,
  type CoachCommanderSuggestion,
  type CoachCreditStatus,
  type CoachMode,
  type CoachRecommendation,
  type CoachRecommendationGroup,
  type CoachResult,
  type CoachScope,
} from './coach'
import { getApiUrl } from './runtime'
import {
  createAccount,
  loginAccount,
  logoutAccount,
  prepareAccountData,
  restoreAccountSession,
  synchronizeAccount,
  type AccountSession,
} from './account'
import { db } from './db'
import { getAvailableQuantity } from './deckAvailability'
import { formatDate, formatMoney } from './format'
import { matchesCollectionSearch } from './collectionSearch'
import { rankMtgPrintingsBySetDetails } from './printingMatch'
import { recognizeCardCanvas, recognizeMtgPrintingDetails } from './ocr'
import { recognizeSportsCard } from './sportsOcr'
import {
  searchSportsCards,
  isCardSightId,
  fetchSportsPriceUpdates,
  getSportsPricingStatus,
  type SportsPricingMode,
  type SportsPricingStatus,
} from './sports'
import type {
  CardCandidate,
  CollectionListKind,
  CollectionItem,
  Condition,
  Deck,
  DeckCard,
  GameFilter,
  ScanIssueReport,
  ScanResult,
} from './types'

type AppTab = 'scan' | GameFilter
type CollectionGroup = 'color' | 'set' | 'condition' | 'category'
type CollectionSort = 'name' | 'value' | 'quantity' | 'newest'
type CardVaultSkin =
  | 'vault'
  | 'mana'
  | 'pokemon'
  | 'binder'
  | 'arcade'
  | 'parchment'
  | 'midnight'
type EditCollectionDraft = {
  addedAt: string
  category: string
  collectorNumber: string
  colors: string
  condition: Condition
  game: GameFilter
  id: number
  imageUrl: string
  lastPriceCheck: string
  name: string
  notes: string
  priceLabel: string
  priceUsd: string
  quantity: string
  rarity: string
  setCode: string
  setName: string
  sourceId: string
  sourceUrl: string
  updatedAt: string
  listKind: CollectionListKind
  sport: string
  manufacturer: string
  releaseName: string
  releaseYear: string
  parallelId: string
  parallelName: string
  numberedTo: string
  gradingCompanyId: string
  gradingCompany: string
  gradeId: string
  grade: string
  certificationNumber: string
  gradeQualifier: string
  autographGrade: string
}
type ScannerState = 'idle' | 'starting' | 'watching' | 'capturing' | 'reading'
type PersistedUiState = {
  activeTab?: AppTab
  game?: GameFilter
  collectionGroup?: CollectionGroup
  collectionSort?: CollectionSort
  collapsedGroups?: Record<string, boolean>
}
type SaveDeckDraft = {
  candidate: CardCandidate
  addToDeck: boolean
  collectionItemId?: number
  deckId: string
  newDeckName: string
  quantity: string
  isSaving: boolean
}
type DeckSaveTarget = {
  deckId?: number
  newDeckName?: string
}
type SaveDeckDialogOptions = {
  addToDeck?: boolean
  collectionItemId?: number
  createNewDeck?: boolean
}

type ScanIssueDraft = {
  correctName: string
  notes: string
  wrongCandidateSourceId: string
  isSaving: boolean
}
type ScanIssueSyncResponse = {
  ok: boolean
  reportId?: string
  error?: string
}
type CoachDraft = {
  deckId: number | null
  game: GameFilter
  scope: CoachScope
  mode: CoachMode
  format: string
  commanderName: string
  goal: string
}
type CoachChatMessage = {
  id: string
  role: 'user' | 'assistant'
  text: string
  result?: CoachResult
}
type CoachQuickPrompt = {
  label: string
  mode: CoachMode
  question: string
}
type CoachComboKind = 'synergy' | 'two-card' | 'engine' | 'win'
type CoachComboArea = 'deck' | 'collection'
type CoachComboRefinement = {
  prompt: CoachQuickPrompt
  colors: string[]
  kind: CoachComboKind
  area: CoachComboArea
}
type CoachBuildStyle =
  | 'balanced'
  | 'aggressive'
  | 'control'
  | 'creatures'
  | 'artifacts'
  | 'combo'
type CoachBuildRefinement = {
  prompt: CoachQuickPrompt
  colors: string[]
  style: CoachBuildStyle
}
type AccountMode = 'login' | 'create'
type AccountDraft = {
  username: string
  displayName: string
  password: string
  remember: boolean
  isSaving: boolean
}
type FeedbackDraft = {
  note: string
  choice: string
  email: string
  error: string
  dismissed: boolean
  isSaving: boolean
}
type CoachProduct = {
  id: string
  title: string
  subtitle: string
  price: string
  kind: 'credits' | 'subscription'
  credits?: number
  badge?: string
}

type DeckEditorEntry = {
  entry: DeckCard
  item: CollectionItem
}

const nameConnectorWords = new Set(['a', 'an', 'and', 'in', 'of', 'on', 'or', 'the', 'to'])
const scannerVersion = 'ocr-2026-09-24-pokemon-01'
const appVersionLabel = cardVaultAppVersion
const pokemonReportedNameCorrections: Record<string, string> = {
  'airy can': 'Clefairy',
  clofai: 'Clefairy',
  clefairv: 'Clefairy',
  'clefairv can': 'Clefairy',
  fai: 'Clefairy',
  fairv: 'Clefairy',
  'fairv can': 'Clefairy',
  flefai: 'Clefairy',
  'lofal tvs': 'Clefairy',
  'ou aurorus': 'Aurorus',
  pod: 'Aurorus',
  zui: 'Aurorus',
  dey: 'Aurorus',
  'freezing chill': 'Aurorus',
  'tundra wall': 'Aurorus',
  'of tundra wall doesn t': 'Aurorus',
  'nau rergs cet': 'Jacinthe',
  'sbubroblst pein': 'Jacinthe',
  'wlys susumu maeya': 'Jacinthe',
}
const mtgReportedNameCorrections: Record<string, string> = {
  'abomination of gudul sex': 'Abomination of Gudul',
  'auger spree lz': 'Auger Spree',
  'bees temple': 'Wayfaring Temple',
  'biionalell swmmit': 'Dragonskull Summit',
  'bleerire trailblazer': 'Creeping Trailblazer',
  'cancealed': 'Concealed Courtyard',
  'cactle garenhrio': 'Castle Garenbrig',
  'cncnlad': 'Concealed Courtyard',
  'cocaicd courtyard': 'Concealed Courtyard',
  'control if increasing vengeance': 'Increasing Vengeance',
  'contested war zone ott': 'Contested War Zone',
  'coralfc mindermnisher': "Geralf's Mindcrusher",
  'copperline gorse': 'Copperline Gorge',
  'crvntic caves': 'Cryptic Caves',
  'crvntiec cavece': 'Cryptic Caves',
  'culale': 'Bow of Nylea',
  'ctt serre': 'Shimmering Grotto',
  'dungeon geiss': 'Dungeon Geists',
  'darkcront eat': 'Darksteel Citadel',
  'darksteel ciro': 'Darksteel Citadel',
  'darksteel citadel ree': 'Darksteel Citadel',
  'dragonskall summit': 'Dragonskull Summit',
  'drazonskall sammie': 'Dragonskull Summit',
  'eer pel': 'Mountain',
  'foralfc minderiscehor': "Geralf's Mindcrusher",
  'fmereence zone': 'Emergence Zone',
  'from a graveyard copy': 'Increasing Vengeance',
  'from your graveyard jor': 'Increasing Vengeance',
  'geralfs mindoerusher': "Geralf's Mindcrusher",
  'geralts mindoerusher': "Geralf's Mindcrusher",
  'gleonjura': 'Gideon Jura',
  'hared summons': 'Shared Summons',
  'inquisitive arreet': 'Inquisitive Puppet',
  'irading prost': 'Trading Post',
  'ire of stagnation': 'Sire of Stagnation',
  'jemons horn': "Demon's Horn",
  'emonshorn': "Demon's Horn",
  'gmons orn': "Demon's Horn",
  'krenko tin street kingg': 'Krenko, Tin Street Kingpin',
  'krenkoj tin street king': 'Krenko, Tin Street Kingpin',
  "karn's bastion sag": "Karn's Bastion",
  'll increasing vengeance ee': 'Increasing Vengeance',
  'loris vengeance': "Sorin's Vengeance",
  'mir cnet cree caen': "Rogue's Passage",
  'narstorm suree': 'Warstorm Surge',
  'negite wat': 'Negate',
  'negtte': 'Negate',
  'neza tchr': 'Negate',
  'nquisitive puppet': 'Inquisitive Puppet',
  'anges the mind': 'Transgress the Mind',
  'oil of the unreal': 'Lord of the Unreal',
  'ooralfs minelerrascher': "Geralf's Mindcrusher",
  'opter assembly': 'Thopter Assembly',
  "orcerer's broom": "Sorcerer's Broom",
  'perline goree': 'Copperline Gorge',
  'pell shrivel': 'Spell Shrivel',
  'fracing post': 'Trading Post',
  'growing ranks zee': 'Growing Ranks',
  'myr lurbine': 'Myr Turbine',
  'riahteous authority': 'Righteous Authority',
  'ransgress the mind': 'Transgress the Mind',
  'coun terflux': 'Counterflux',
  'edrf di youll lie': 'Sultai Ascendancy',
  'sultai acscendancy': 'Sultai Ascendancy',
  'temole of mystery': 'Temple of Mystery',
  'the sweet taste of': 'Chalice of Life',
  'turn its still': 'Mutavault',
  'turn its still a': 'Mutavault',
  'ttt eee': 'Mirrorworks',
  'vunepiast': 'Duneblast',
  'funcdiast': 'Duneblast',
  'vunenilast caaa': 'Duneblast',
  'vtvara neilikite': 'Utvara Hellkite',
  'adusitive puppet': 'Inquisitive Puppet',
  'rrnsiive tuppet': 'Inquisitive Puppet',
  'seaff of the mind maeuc': 'Staff of the Mind Magus',
  'seaff of the mind': 'Staff of the Mind Magus',
  'staff of the mind': 'Staff of the Mind Magus',
  'cer of thee mid mocus': 'Staff of the Mind Magus',
  'cer of thee mid': 'Staff of the Mind Magus',
  'morarpod': 'Mortarpod',
  'mormrood': 'Mortarpod',
  'morarmod': 'Mortarpod',
  'warden of warden of': 'Warden of the Wall',
  'warden of warden of the wall': 'Warden of the Wall',
  'wordon of the wall': 'Warden of the Wall',
  'prn irr': 'Retributive Wand',
  'avacyns': "Avacyn's Collar",
  'ean matcher': 'Pattern Matcher',
  'oattern matcher': 'Pattern Matcher',
  'rogues gloves': "Rogue's Gloves",
  'ceetet': 'Enchanted Carriage',
  'qeets tlate': "Brawler's Plate",
  'srawlers plate': "Brawler's Plate",
  'brawlersplate': "Brawler's Plate",
  'vitness of the ages': 'Witness of the Ages',
  'treet sweeper': 'Street Sweeper',
  'root swoomor': 'Street Sweeper',
  'hambling suit': 'Shambling Suit',
  'lambing sult': 'Shambling Suit',
  "hormoa's urypi": "Tormod's Crypt",
  'lzzot kevrune': 'Izzet Keyrune',
  'rofane memento': 'Profane Memento',
  'nivix ttt': 'Nivix Guildmage',
  'iskymarkroc': 'Skymark Roc',
  'lskymark': 'Skymark Roc',
  'skymark roc v0': 'Skymark Roc',
  'roca burdmage': 'Korozda Guildmage',
  'sen sanctuary': 'Risen Sanctuary',
  'inia l iiaagul': 'Rix Maadi Guildmage',
  'inia liiaagul': 'Rix Maadi Guildmage',
  'ina ittaaul': 'Rix Maadi Guildmage',
  'azotius lnarm': 'Azorius Charm',
  'rew prahv guildmage': 'New Prahv Guildmage',
  'uzzetcharm': 'Izzet Charm',
  'izzet charm ge': 'Izzet Charm',
  'lcruei celebrant': 'Cruel Celebrant',
  'jcruel bibrent': 'Cruel Celebrant',
  'cruet celebrant': 'Cruel Celebrant',
  'despark koa': 'Despark',
  'despa rk': 'Despark',
  'lansrath captain of chaos': 'Angrath, Captain of Chaos',
  'loasrafhy capfain of chac': 'Angrath, Captain of Chaos',
  'nrlleile ctl': "Huatli's Raptor",
  'deathless knight ree': 'Deathless Knight',
  'etite vanguara': 'Elite Vanguard',
  'etite vanguard': 'Elite Vanguard',
  'dsengir vampire': 'Sengir Vampire',
  'gireball': 'Fireball',
  'hollowhenge spa': 'Hollowhenge Spirit',
  'jazaar krovod azaar krovod': 'Bazaar Krovod',
  'jake up arms': 'Take Up Arms',
  'll revenge of ravens': 'Revenge of Ravens',
  'lorrupt': 'Corrupt',
  'maraleaf pixie pp': 'Maraleaf Pixie',
  'bleeding edge jezd': 'Bleeding Edge',
  'opecters shriek': "Specter's Shriek",
  'onsume spirit': 'Consume Spirit',
  'onyx mage jen': 'Onyx Mage',
  'price of befrayal': 'Price of Betrayal',
  'qust': 'Oust',
  'return reassembling skeleton': 'Reassembling Skeleton',
  'selestial purge': 'Celestial Purge',
  'skirsdag flayer jen': 'Skirsdag Flayer',
  'tesome encore': 'Gruesome Encore',
  'unawing zombie': 'Gnawing Zombie',
  'sengir vampire ine': 'Sengir Vampire',
  'vengelul vampire neigh': 'Vengeful Vampire',
  'vengelul vampire': 'Vengeful Vampire',
  'viurderous cut': 'Murderous Cut',
  'will wash you from': 'Ride Down',
  'burning yard': 'Burning-Yard Trainer',
  'control circle of flame': 'Circle of Flame',
  'lleshpulper giant': 'Fleshpulper Giant',
  'lhandra rovice f yromance': 'Chandra, Novice Pyromancer',
  'luuiy bar': 'Young Pyromancer',
  'male melee': 'Redcap Melee',
  'manawefe sliver': 'Manaweft Sliver',
  'oaffiicted deserter': 'Afflicted Deserter',
  'oke ine riames': 'Stoke the Flames',
  'ra lr ian': 'Incendiary Sabotage',
  'rarpacx apha': 'Briarpack Alpha',
  "tibalt's rager jak": "Tibalt's Rager",
  'woodborn behermatt': 'Woodborn Behemoth',
  'augur of bolas de': 'Augur of Bolas',
  'cudgel tron': 'Cudgel Troll',
  'evolution saze': 'Evolution Sage',
  'fsatyr piper': 'Satyr Piper',
  'levetirtion sage': 'Evolution Sage',
  'lgolgari decoy': 'Golgari Decoy',
  'long finnec skywhale': 'Long-Finned Skywhale',
  'long finned skywhale vac': 'Long-Finned Skywhale',
  'mae ral': 'Bond of Flourishing',
  'mouilled slaewarm': 'Quilled Slagwurm',
  'qverrun': 'Overrun',
  'rozure mage': 'Azure Mage',
  'sel adriit': 'Set Adrift',
  'snowhorn rider bl jon': 'Snowhorn Rider',
  'golden egg str': 'Golden Egg',
  'golden ess': 'Golden Egg',
  'loisnpose scarecrow': 'Signpost Scarecrow',
  'rovine keep': 'Roving Keep',
  'sienpost scarecrow': 'Signpost Scarecrow',
  'ldrossripper': 'Dross Ripper',
  'bebss riper': 'Dross Ripper',
  'ltraining drone': 'Training Drone',
  'framing vrone': 'Training Drone',
  'kk training drone': 'Training Drone',
  'a lalning vronc': 'Training Drone',
  'bh raining vronc': 'Training Drone',
  'bgust skimmer': 'Gust-Skimmer',
  'gust skimmer i': 'Gust-Skimmer',
  'al gust skimmer': 'Gust-Skimmer',
  'heart piercer': 'Heart-Piercer Bow',
  'there 1s no force': 'Growth Curve',
  'ugluk of the white': 'Uglúk of the White Hand',
  'ugluk of li white': 'Uglúk of the White Hand',
  'fires of a doom': 'Fires of Mount Doom',
  'fires of mount poom': 'Fires of Mount Doom',
  'plains pains': 'Plains',
  'danedain blade': 'Dúnedain Blade',
  'nostrils but gandalf stood': 'You Cannot Pass!',
  'whined and cracked fire': 'You Cannot Pass!',
  'cstew the coneys': 'Stew the Coneys',
  'kseewiihe coneys': 'Stew the Coneys',
  'tranned in the tower': 'Trapped in the Tower',
}
const mtgReportedWrongPrintingIds: Record<string, string> = {
  'cancealed': 'c8769e97-aee8-4466-a9d7-0f4245ae4a97',
  'cactle garenhrio': 'e3c2c66c-f7f0-41d5-a805-a129aeaf1b75',
  'cncnlad': 'c8769e97-aee8-4466-a9d7-0f4245ae4a97',
  'cocaicd courtyard': 'c8769e97-aee8-4466-a9d7-0f4245ae4a97',
  'crvntic caves': 'fde9e9cb-68ab-4856-8ad6-30f66666dd93',
  'crvntiec cavece': 'fde9e9cb-68ab-4856-8ad6-30f66666dd93',
  'ctt serre': '7fd26b14-a920-4b82-91b8-8de0e7d03f6e',
  'contested war zone ott': 'bee9b696-5203-4235-9bdd-f2a389d69813',
  'darkcront eat': 'b0ad4395-5766-44c3-b5af-3d65d49a3333',
  'darksteel ciro': 'b0ad4395-5766-44c3-b5af-3d65d49a3333',
  'darksteel citadel ree': 'b0ad4395-5766-44c3-b5af-3d65d49a3333',
  'dragonskall summit': 'f99375bc-7465-4f20-897c-1bc61f65de61',
  'drazonskall sammie': 'f99375bc-7465-4f20-897c-1bc61f65de61',
  'dungeon geiss': 'b715da2e-c816-4c14-8522-811c97c66fed',
  'f galvanoth': 'fc1a696b-642a-419f-bd43-09af39a9401b',
  'fmereence zone': 'ab95f6e7-b806-47fe-a071-6c38b3176d94',
  'galvanoth t': 'fc1a696b-642a-419f-bd43-09af39a9401b',
  'gleonjura': '8532d72a-b2d2-49e1-85ea-35f4c91c6c8d',
  'bll galvanoth see': 'fc1a696b-642a-419f-bd43-09af39a9401b',
  'hared summons': 'f8a6969c-c78a-4d8d-b472-5d5234714dc4',
  'inquisitive arreet': '807a570a-b5eb-420d-9fa5-6e5364025510',
  "karn's bastion sag": 'b9d895af-7e8c-419f-bc5d-5596083fbfb6',
  'krenko tin street kingg': '4bf960b4-bc71-4966-9014-8dd4242c162b',
  'growing ranks zee': '12f31616-1249-4964-b81a-4435405a2449',
  'mir cnet cree caen': 'f416e36a-15cb-43c8-b27f-82f65a95ddef',
  'opter assembly': '644ab412-0603-447d-b8ef-dfd79f78e2a5',
  "orcerer's broom": '071e8f20-18b3-4bf5-a23a-adb42bf5819b',
  'pell shrivel': 'efa110cb-f091-48f0-bc62-80f5f18568e8',
  'riahteous authority': '6695e5bb-56a9-49ab-8940-72336e845875',
  'temole of mystery': '0c284acd-4407-42ad-9f4c-359041223609',
  'turn its still': '927ed667-c228-4b96-a9f6-7cbadade8134',
  'turn its still a': '927ed667-c228-4b96-a9f6-7cbadade8134',
  'nquisitive puppet': '807a570a-b5eb-420d-9fa5-6e5364025510',
  'adusitive puppet': '807a570a-b5eb-420d-9fa5-6e5364025510',
  'rrnsiive tuppet': '807a570a-b5eb-420d-9fa5-6e5364025510',
  'seaff of the mind maeuc': 'f86bf36b-b83f-4451-8cdc-2a4ccffb93c7',
  'seaff of the mind': 'f86bf36b-b83f-4451-8cdc-2a4ccffb93c7',
  'staff of the mind': 'f86bf36b-b83f-4451-8cdc-2a4ccffb93c7',
  'cer of thee mid mocus': 'f86bf36b-b83f-4451-8cdc-2a4ccffb93c7',
  'cer of thee mid': 'f86bf36b-b83f-4451-8cdc-2a4ccffb93c7',
  'jemons horn': '3f56b129-fe2d-4061-b1c9-f1f5a4db564a',
  'emonshorn': '3f56b129-fe2d-4061-b1c9-f1f5a4db564a',
  'gmons orn': '3f56b129-fe2d-4061-b1c9-f1f5a4db564a',
  'the sweet taste of': '9d9c1c46-7aa7-464c-87b0-b29b9663daef',
  'morarpod': 'fbd23da5-421f-41d0-bb60-59560da7dece',
  'mormrood': 'fbd23da5-421f-41d0-bb60-59560da7dece',
  'morarmod': 'fbd23da5-421f-41d0-bb60-59560da7dece',
  'warden of warden of': 'fd1a1f48-d46b-4b8e-a642-fc70fd9ef7df',
  'warden of warden of the wall': 'fd1a1f48-d46b-4b8e-a642-fc70fd9ef7df',
  'wordon of the wall': 'fd1a1f48-d46b-4b8e-a642-fc70fd9ef7df',
  'prn irr': '5521cb92-03b0-44ab-a141-40dc0eb5a79f',
  'avacyns': '972e9a78-204b-4012-b394-b40fd0edac4c',
  'ean matcher': 'ad608eb3-d387-4414-b194-703f38863896',
  'oattern matcher': 'ad608eb3-d387-4414-b194-703f38863896',
  'rogues gloves': '22a35bd7-8f0f-421a-948d-810513e6b0a0',
  'ceetet': '0057c2ae-ea4f-404a-ab95-f3979efd1b3b',
  'qeets tlate': 'eddc4ee6-7855-4dc7-9488-5e019609bd09',
  'srawlers plate': 'eddc4ee6-7855-4dc7-9488-5e019609bd09',
  'brawlersplate': 'eddc4ee6-7855-4dc7-9488-5e019609bd09',
  'vitness of the ages': '763ac4c4-af2d-4b71-9fb3-244c96f93860',
  'treet sweeper': '4a62827a-183f-4bef-b6ce-20a4577f6d30',
  'root swoomor': '4a62827a-183f-4bef-b6ce-20a4577f6d30',
  'hambling suit': '1100b898-31a8-4fdf-a54f-a1470ec032f3',
  'lambing sult': '1100b898-31a8-4fdf-a54f-a1470ec032f3',
  "hormoa's urypi": '314bb937-1b1e-4daa-8b9e-2d282e8f433b',
  'lzzot kevrune': '83e4e83b-cbb8-4efd-b6c4-4459a29177ac',
  'azorius kevrune': '23a05db7-dcae-4180-8ad1-60ba6fb30816',
  'rofane memento': 'f2c97632-3cf1-4b79-9d18-d8991654dcca',
  'nivix ttt': '9d3fde47-8d9e-4a84-b8a5-dcfe0c1d443c',
  'iskymarkroc': '60601296-2229-4c48-94cc-1903926750ce',
  'lskymark': '60601296-2229-4c48-94cc-1903926750ce',
  'skymark roc v0': '60601296-2229-4c48-94cc-1903926750ce',
  'roca burdmage': '761c16aa-d4a7-492d-9275-98d0e07de45a',
  'sen sanctuary': 'a0b6c136-2bbe-48c1-ac53-2a8221b96936',
  'inia l iiaagul': 'eb737465-5de8-4015-befe-2bf386da2a89',
  'inia liiaagul': 'eb737465-5de8-4015-befe-2bf386da2a89',
  'ina ittaaul': 'eb737465-5de8-4015-befe-2bf386da2a89',
  'azotius lnarm': '26adc211-d089-4102-91e5-225bbeb5f382',
  'rew prahv guildmage': '698b47d1-c72e-4dc3-b28b-7421e0163f22',
  'uzzetcharm': '1e3a5af6-5423-442b-a207-364e97a871d8',
  'izzet charm ge': '1e3a5af6-5423-442b-a207-364e97a871d8',
  'lcruei celebrant': '87ead6ac-b1c5-4852-8413-7fa43c6cfc57',
  'jcruel bibrent': '87ead6ac-b1c5-4852-8413-7fa43c6cfc57',
  'cruet celebrant': '87ead6ac-b1c5-4852-8413-7fa43c6cfc57',
  'despark koa': '32b17dfc-b916-4134-ba77-501cff435e7e',
  'despa rk': '32b17dfc-b916-4134-ba77-501cff435e7e',
  'lansrath captain of chaos': 'aa3a4cb5-945e-4caf-8a18-1ef977879fe8',
  'loasrafhy capfain of chac': 'aa3a4cb5-945e-4caf-8a18-1ef977879fe8',
  'pledge of unity': 'ca9b3518-7fcd-49e2-a4c4-e5daa8be6c21',
  'nrlleile ctl': 'ba680ad0-6134-46e0-8fcb-532b2052eca7',
  'deathless knight ree': '39c6e952-97b5-435c-a7fd-6271ddc23200',
  'bazaar krovod': 'b07bb2fe-3a9b-47d0-864b-99a662d9544b',
  'jazaar krovod azaar krovod': 'b07bb2fe-3a9b-47d0-864b-99a662d9544b',
  'bleeding edge jezd': 'ac82422c-8ac8-4fbb-b9b9-d0aa23dded61',
  'hollowhenge spa': 'a870b8ee-8ca9-42d0-96bb-b297aa989d14',
  'dsengir vampire': 'de652420-eacf-4f9d-9f13-c6bc02b0fa72',
  'jake up arms': 'e707c745-0bb1-4a85-83c1-be97131bf488',
  'price of befrayal': 'fd68e918-5089-46c9-8836-b05a3da3c7a3',
  'opecters shriek': 'edae2c29-1418-477c-9efe-e53fa6c7fe93',
  'onsume spirit': 'ef144439-fc8e-4844-8ebb-3e36e05ac9a0',
  'onyx mage jen': 'eabd38e6-1e59-42d2-bd1a-555c77cf6747',
  'selestial purge': 'cd38afd4-d7a4-493d-bdea-72be9a1d9a07',
  'return reassembling skeleton': '4d431445-d7db-4ce1-b422-41494d9be1b4',
  'skirsdag flayer jen': '274976b0-2bb5-46e6-b62e-b50d80a77e28',
  'tesome encore': '3f7f31fb-af96-4c8c-80fa-219ebd7c3d4d',
  'unawing zombie': 'f4bdd28d-7467-494b-a52f-a84932da25f3',
  'sengir vampire ine': 'de652420-eacf-4f9d-9f13-c6bc02b0fa72',
  'viurderous cut': 'b2dadff2-883f-4134-a881-be145cdcbd84',
  'burning yard': '17755d1b-3a56-4362-a534-85b35ceb1802',
  'control circle of flame': '96cbfd4a-ac95-4191-84f9-199a9e1807c4',
  'lleshpulper giant': 'f2726d3c-c182-4d8a-a723-0de2c5c4b152',
  'manawefe sliver': 'f84667e2-7d9e-478c-826c-9701ad02b594',
  'molten birth': '910098bb-c909-47c5-b2ef-0dd4e5a8346a',
  'oke ine riames': 'e59ba241-49c8-4913-9226-2eb384ef73df',
  "tibalt's rager jak": '2947ad7e-d365-45ff-b107-35819b308f8c',
  'woodborn behermatt': '7f887474-760c-4f33-8051-35b9d2e65307',
  'cudgel tron': '38cedd13-2f2a-4625-a074-ebbf34ae25e5',
  'evolution saze': '1d58d08d-cd62-416d-8d8e-7d9c56d5c4da',
  'fsatyr piper': 'e244c4d0-2c30-4a56-94bb-0fff9ffe4a6a',
  'lgolgari decoy': '511a42a8-71ce-476f-98fa-fc0dc822edcf',
  'long finnec skywhale': '772e9472-c710-474e-b8e9-54662330a592',
  'qverrun': '1d8e9cbb-8bf4-4a48-a58e-79deb3abdf7f',
  'rozure mage': 'e5a7ed36-a552-4d46-929a-48f34ae9279a',
  'sel adriit': 'b751b48c-d525-4e8f-8a6a-d1d8824664cf',
  'golden ess': '525142c3-f17c-4e02-a02d-fa385215aa12',
  'rovine keep': '38a0136e-a637-4a12-a38f-35f772b290a9',
  'sienpost scarecrow': '0dc2b435-5c5a-40fe-a5d2-9d75ca8aa69b',
  'plains pains': '6620b5f4-b1e5-4d1b-bbf2-c6ad9c8284c5',
}
const resultBatchSize = 12
const sportsPriceBatchTarget = 15
const uiStateStorageKey = 'card-vault-ui-state-v1'
const coachThreadsStorageKey = 'card-vault-coach-threads-v1'
const coachThreadMessageLimit = 20
const coachThreadFallbackMessageLimit = 10
const createNewDeckValue = '__new__'
const appTabs: AppTab[] = ['scan', 'mtg', 'pokemon', 'sports']
const gameFilters: GameFilter[] = ['mtg', 'pokemon', 'sports']
const collectionGroups: CollectionGroup[] = ['color', 'set', 'condition', 'category']
const collectionSorts: CollectionSort[] = ['name', 'value', 'quantity', 'newest']
const cardVaultSkinStorageKey = 'card-vault-desktop-skin'
const cardVaultAndroidUrl =
  'https://play.google.com/store/apps/details?id=com.cmforgedbyfire.cardvault'
const coachCreditCosts: Record<CoachMode, number> = {
  ask: 1,
  review: 2,
  combos: 3,
  improve: 3,
  build: 8,
}
const commanderSuggestionCreditCost = 1
const coachCreditChargeRows: Array<{
  label: string
  cost: number
  detail: string
}> = [
  {
    label: 'Ask Coach',
    cost: coachCreditCosts.ask,
    detail: 'Quick card, rules, or collection questions.',
  },
  {
    label: 'Deck review',
    cost: coachCreditCosts.review,
    detail: 'Checks a saved deck for strengths and weak spots.',
  },
  {
    label: 'Combos',
    cost: coachCreditCosts.combos,
    detail: 'Finds useful card interactions you own.',
  },
  {
    label: 'Improve deck',
    cost: coachCreditCosts.improve,
    detail: 'Suggests owned swaps for a stronger deck.',
  },
  {
    label: 'Build deck',
    cost: coachCreditCosts.build,
    detail: 'Builds a full deck from cards you own.',
  },
  {
    label: 'Commander suggestions',
    cost: commanderSuggestionCreditCost,
    detail: 'Finds owned commanders that match your build filters.',
  },
]
const coachCreditProducts: CoachProduct[] = [
  {
    id: 'coach_credits_25',
    title: '25 Credits',
    subtitle: 'Good for a few deck builds or quick advice.',
    price: '$2.99',
    kind: 'credits',
    credits: 25,
  },
  {
    id: 'coach_credits_75',
    title: '75 Credits',
    subtitle: 'Enough for regular tuneups and several builds.',
    price: '$6.99',
    kind: 'credits',
    credits: 75,
    badge: 'Popular',
  },
  {
    id: 'coach_credits_200',
    title: '200 Credits',
    subtitle: 'For heavy collectors managing multiple decks.',
    price: '$14.99',
    kind: 'credits',
    credits: 200,
  },
  {
    id: 'coach_unlimited',
    title: 'Unlimited Coach',
    subtitle: 'Unlimited Coach requests while the subscription is active.',
    price: '$9.99 / month',
    kind: 'subscription',
    badge: 'Best for builders',
  },
]

type AndroidCoachPurchase = {
  ok: boolean
  status?: string
  productId?: string
  purchaseToken?: string
  orderId?: string
  packageName?: string
  purchaseState?: string
  acknowledged?: boolean
  error?: string
}

function requestAndroidCoachPurchase(productId: string): Promise<AndroidCoachPurchase> {
  const androidBridge = window.CardVaultAndroid
  if (!androidBridge?.purchaseCoachProduct) {
    return Promise.resolve({
      ok: false,
      error: 'Google Play checkout is not available in this app build.',
    })
  }

  const requestId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      if (window.__cardVaultAndroidPurchaseResult === handleResult) {
        window.__cardVaultAndroidPurchaseResult = undefined
      }
      resolve({
        ok: false,
        error: 'Google Play checkout timed out. Please try again.',
      })
    }, 120001)

    const previousHandler = window.__cardVaultAndroidPurchaseResult
    const handleResult = (payload: AndroidCoachPurchase & { requestId?: string }) => {
      if (payload.requestId && payload.requestId !== requestId) {
        previousHandler?.(payload)
        return
      }
      window.clearTimeout(timeoutId)
      window.__cardVaultAndroidPurchaseResult =
        previousHandler === handleResult ? undefined : previousHandler
      resolve(payload)
    }

    window.__cardVaultAndroidPurchaseResult = handleResult
    androidBridge.purchaseCoachProduct(productId, requestId)
  })
}

function completeAndroidCoachPurchase(product: CoachProduct, purchase: AndroidCoachPurchase) {
  const androidBridge = window.CardVaultAndroid
  if (!purchase.purchaseToken || !androidBridge?.completeCoachPurchase) {
    return
  }
  androidBridge.completeCoachPurchase(
    product.id,
    purchase.purchaseToken,
    product.kind,
  )
}

const cardVaultSkins: Array<{ label: string; value: CardVaultSkin }> = [
  { label: 'Vault', value: 'vault' },
  { label: 'Arcane Table', value: 'mana' },
  { label: 'Pokemon Gym', value: 'pokemon' },
  { label: 'Binder Paper', value: 'binder' },
  { label: 'Foily Neon', value: 'arcade' },
  { label: 'Old Parchment', value: 'parchment' },
  { label: 'Midnight Mint', value: 'midnight' },
]
const cardVaultSkinValues = cardVaultSkins.map((skin) => skin.value)
const defaultCardVaultSkin: CardVaultSkin = 'vault'

const conditions: Condition[] = [
  'Near Mint',
  'Lightly Played',
  'Moderately Played',
  'Heavily Played',
  'Damaged',
]
const defaultCondition: Condition = 'Near Mint'
let cachedPersistedUiState: PersistedUiState | null = null

function getPersistedUiState(): PersistedUiState {
  if (!cachedPersistedUiState) {
    cachedPersistedUiState = readPersistedUiState()
  }

  return cachedPersistedUiState
}

function getStoredCardVaultSkin(): CardVaultSkin {
  if (typeof window === 'undefined') {
    return defaultCardVaultSkin
  }

  const storedSkin = window.localStorage.getItem(cardVaultSkinStorageKey)

  return isOneOf(storedSkin, cardVaultSkinValues) ? storedSkin : defaultCardVaultSkin
}

function readPersistedUiState(): PersistedUiState {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const rawState = window.localStorage.getItem(uiStateStorageKey)

    if (!rawState) {
      return {}
    }

    const parsed = JSON.parse(rawState) as unknown

    if (!isPlainObject(parsed)) {
      return {}
    }

    const state: PersistedUiState = {}

    if (isOneOf(parsed.activeTab, appTabs)) {
      state.activeTab = parsed.activeTab
    }

    if (isOneOf(parsed.game, gameFilters)) {
      state.game = parsed.game
    }

    if (isOneOf(parsed.collectionGroup, collectionGroups)) {
      state.collectionGroup = parsed.collectionGroup
    }

    if (isOneOf(parsed.collectionSort, collectionSorts)) {
      state.collectionSort = parsed.collectionSort
    }

    if (isStringBooleanRecord(parsed.collapsedGroups)) {
      state.collapsedGroups = parsed.collapsedGroups
    }

    return state
  } catch {
    return {}
  }
}

function getCoachThreadKey(draft: CoachDraft, profileId: string): string {
  const owner = profileId || 'local'
  const location =
    draft.scope === 'deck' && draft.deckId !== null
      ? `deck:${draft.deckId}`
      : `library:${draft.game}`
  return `${owner}:${location}`
}

function capCoachMessages(messages: CoachChatMessage[]): CoachChatMessage[] {
  return messages.slice(-coachThreadMessageLimit)
}

function readCoachThreads(): Record<string, CoachChatMessage[]> {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const rawState = window.localStorage.getItem(coachThreadsStorageKey)
    if (!rawState) {
      return {}
    }
    const parsed = JSON.parse(rawState) as unknown
    if (!isPlainObject(parsed)) {
      return {}
    }

    const threads: Record<string, CoachChatMessage[]> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (!Array.isArray(value)) {
        continue
      }
      const messages = value.filter(
        (item): item is CoachChatMessage =>
          isPlainObject(item) &&
          typeof item.id === 'string' &&
          (item.role === 'user' || item.role === 'assistant') &&
          typeof item.text === 'string' &&
          (item.result === undefined || isPlainObject(item.result)),
      )
      if (messages.length) {
        threads[key] = capCoachMessages(messages)
      }
    }
    return threads
  } catch {
    return {}
  }
}

function readCoachThreadMessages(
  draft: CoachDraft,
  profileId: string,
): CoachChatMessage[] {
  return readCoachThreads()[getCoachThreadKey(draft, profileId)] ?? []
}

function writeCoachThreadMessages(
  draft: CoachDraft,
  profileId: string,
  messages: CoachChatMessage[],
) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const threads = readCoachThreads()
    const key = getCoachThreadKey(draft, profileId)
    const cappedMessages = capCoachMessages(messages)
    if (cappedMessages.length) {
      threads[key] = cappedMessages
    } else {
      delete threads[key]
    }
    try {
      window.localStorage.setItem(coachThreadsStorageKey, JSON.stringify(threads))
    } catch {
      if (cappedMessages.length <= coachThreadFallbackMessageLimit) {
        throw new Error('Coach thread storage unavailable')
      }
      threads[key] = cappedMessages.slice(-coachThreadFallbackMessageLimit)
      window.localStorage.setItem(coachThreadsStorageKey, JSON.stringify(threads))
    }
  } catch {
    // Thread history is helpful, but Coach should remain usable without it.
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isOneOf<T extends string>(value: unknown, options: T[]): value is T {
  return typeof value === 'string' && options.includes(value as T)
}

function isSupportedGame(value: unknown): value is GameFilter {
  return isOneOf(value, gameFilters)
}

function expireStaleSportsPrice(item: CollectionItem, now: number): CollectionItem {
  if (
    item.game !== 'sports' ||
    item.sportsPriceStatus === 'pending' ||
    !item.sportsPriceExpiresAt
  ) {
    return item
  }

  const expiresAt = Date.parse(item.sportsPriceExpiresAt)
  if (!Number.isFinite(expiresAt) || expiresAt > now) {
    return item
  }

  return {
    ...item,
    priceUsd: null,
    priceLabel: 'Pricing queued',
    sportsPriceStatus: 'pending',
    sportsPriceComparables: [],
    sportsPriceExpiresAt: '',
  }
}

function feedbackKindForChoice(choice: string): 'bug' | 'suggestion' | 'other' {
  if (choice === 'Broke') {
    return 'bug'
  }
  if (choice === 'Worked well') {
    return 'suggestion'
  }
  return 'other'
}

function isStringBooleanRecord(value: unknown): value is Record<string, boolean> {
  if (!isPlainObject(value)) {
    return false
  }

  return Object.values(value).every((item) => typeof item === 'boolean')
}

function App() {
  const isDesktopShell =
    typeof window !== 'undefined' && Boolean(window.cardVaultDesktop?.platform)
  const isAndroidShell =
    typeof window !== 'undefined' && Boolean(window.CardVaultAndroid?.purchaseCoachProduct)
  const canShowAndroidAppPromo = isDesktopShell
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const lastAutoTermRef = useRef('')
  const cameraSessionRef = useRef(0)
  const activeScanIdRef = useRef(0)
  const performAccountSyncRef = useRef<(announce: boolean) => Promise<void>>(
    async () => undefined,
  )
  const accountSyncTimerRef = useRef<number | null>(null)
  const accountSyncRunningRef = useRef(false)
  const accountSyncReadyRef = useRef(false)
  const coachChatEndRef = useRef<HTMLDivElement | null>(null)
  const scanResultsRef = useRef<HTMLDivElement | null>(null)
  const previewTouchStartRef = useRef<{ x: number; y: number } | null>(null)

  const [game, setGame] = useState<GameFilter>(() => getPersistedUiState().game ?? 'mtg')
  const [activeTab, setActiveTab] = useState<AppTab>(
    () => getPersistedUiState().activeTab ?? 'scan',
  )
  const [cardVaultSkin, setCardVaultSkin] = useState<CardVaultSkin>(
    getStoredCardVaultSkin,
  )
  const [query, setQuery] = useState('')
  const [collectionGroup, setCollectionGroup] = useState<CollectionGroup>(
    () => getPersistedUiState().collectionGroup ?? 'color',
  )
  const [collectionSort, setCollectionSort] = useState<CollectionSort>(
    () => getPersistedUiState().collectionSort ?? 'name',
  )
  const [collectionQueries, setCollectionQueries] = useState<Record<GameFilter, string>>({
    mtg: '',
    pokemon: '',
    sports: '',
  })
  const [activeListKind, setActiveListKind] =
    useState<CollectionListKind>('collection')
  const [collection, setCollection] = useState<CollectionItem[]>([])
  const [decks, setDecks] = useState<Deck[]>([])
  const [deckCards, setDeckCards] = useState<DeckCard[]>([])
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(
    () => getPersistedUiState().collapsedGroups ?? {},
  )
  const [candidates, setCandidates] = useState<CardCandidate[]>([])
  const [scanResult, setScanResult] = useState<ScanResult | null>(null)
  const [selectedTerm, setSelectedTerm] = useState('')
  const [status, setStatus] = useState('Ready')
  const [isSearching, setIsSearching] = useState(false)
  const [isRefreshingResults, setIsRefreshingResults] = useState(false)
  const [isPricingSports, setIsPricingSports] = useState(false)
  const [sportsPricingStatus, setSportsPricingStatus] =
    useState<SportsPricingStatus | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [isCameraOn, setIsCameraOn] = useState(false)
  const [scannerState, setScannerState] = useState<ScannerState>('idle')
  const [capturedFrame, setCapturedFrame] = useState('')
  const [previewCard, setPreviewCard] = useState<CardCandidate | null>(null)
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false)
  const [deckBuilderGame, setDeckBuilderGame] = useState<GameFilter | null>(null)
  const [selectedDeckId, setSelectedDeckId] = useState<number | null>(null)
  const [deckDeleteDraft, setDeckDeleteDraft] = useState<Deck | null>(null)
  const [isRenamingDeck, setIsRenamingDeck] = useState(false)
  const [deckNameDraft, setDeckNameDraft] = useState('')
  const [newDeckNameDraft, setNewDeckNameDraft] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<EditCollectionDraft | null>(null)
  const [saveDeckDraft, setSaveDeckDraft] = useState<SaveDeckDraft | null>(null)
  const [scanIssueDraft, setScanIssueDraft] = useState<ScanIssueDraft | null>(null)
  const [coachDraft, setCoachDraft] = useState<CoachDraft | null>(null)
  const coachDraftRef = useRef<CoachDraft | null>(null)
  const [coachResult, setCoachResult] = useState<CoachResult | null>(null)
  const [coachError, setCoachError] = useState('')
  const [isCoachLoading, setIsCoachLoading] = useState(false)
  const [isCoachDeckBuildRunning, setIsCoachDeckBuildRunning] = useState(false)
  const [coachQuestion, setCoachQuestion] = useState('')
  const [coachMessages, setCoachMessages] = useState<CoachChatMessage[]>([])
  const [savingCoachDeckMessageId, setSavingCoachDeckMessageId] = useState<
    string | null
  >(null)
  const [savedCoachDeckNames, setSavedCoachDeckNames] = useState<
    Record<string, string>
  >({})
  const [areCoachPromptsOpen, setAreCoachPromptsOpen] = useState(false)
  const [coachComboRefinement, setCoachComboRefinement] =
    useState<CoachComboRefinement | null>(null)
  const [coachBuildRefinement, setCoachBuildRefinement] =
    useState<CoachBuildRefinement | null>(null)
  const [commanderSuggestions, setCommanderSuggestions] = useState<
    CoachCommanderSuggestion[]
  >([])
  const [coachCredits, setCoachCredits] = useState<CoachCreditStatus | null>(null)
  const [isCoachCreditListOpen, setIsCoachCreditListOpen] = useState(false)
  const [isSuggestingCommander, setIsSuggestingCommander] = useState(false)
  const [openGameMenu, setOpenGameMenu] = useState<GameFilter | null>(null)
  const [openCollectionItemMenu, setOpenCollectionItemMenu] = useState<string | null>(
    null,
  )
  const [saveNotice, setSaveNotice] = useState('')
  const [refreshingId, setRefreshingId] = useState<number | 'all' | null>(null)
  const [resultOffset, setResultOffset] = useState(0)
  const [hasMoreResults, setHasMoreResults] = useState(false)
  const [failedImages, setFailedImages] = useState<Set<string>>(() => new Set())
  const [accountSession, setAccountSession] = useState<AccountSession | null>(null)
  const [accountMode, setAccountMode] = useState<AccountMode>('login')
  const [accountDialogOpen, setAccountDialogOpen] = useState(true)
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false)
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [purchaseStatus, setPurchaseStatus] = useState('')
  const [isAndroidAppPanelOpen, setIsAndroidAppPanelOpen] = useState(false)
  const [isAccountBooting, setIsAccountBooting] = useState(true)
  const [accountStatus, setAccountStatus] = useState('')
  const [accountDraft, setAccountDraft] = useState<AccountDraft>({
    username: '',
    displayName: '',
    password: '',
    remember: true,
    isSaving: false,
  })
  const [feedbackDraft, setFeedbackDraft] = useState<FeedbackDraft>({
    note: '',
    choice: '',
    email: '',
    error: '',
    dismissed: window.localStorage.getItem('card-vault-feedback-nudge-dismissed') === '1',
    isSaving: false,
  })
  const previewIsOpen = previewCard !== null

  useEffect(() => {
    void loadCollection()
    void loadDeckData()
    void initializeAccount()

    return () => {
      stopCamera({ silent: true })
    }
  }, [])

  useEffect(() => {
    if (accountSession) {
      void syncStoredScanIssueReports(accountSession.token)
      void loadCoachCredits(accountSession.token)
    } else {
      setCoachCredits(null)
    }
  }, [accountSession])

  useEffect(() => {
    if (!isDesktopShell) {
      delete document.documentElement.dataset.cardVaultSkin
      return
    }

    document.documentElement.dataset.cardVaultSkin = cardVaultSkin
    window.localStorage.setItem(cardVaultSkinStorageKey, cardVaultSkin)
  }, [cardVaultSkin, isDesktopShell])

  useEffect(() => {
    coachChatEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [coachMessages, isCoachLoading])

  useEffect(() => {
    if (!accountSession || !accountSyncReadyRef.current) {
      return
    }

    if (accountSyncTimerRef.current !== null) {
      window.clearTimeout(accountSyncTimerRef.current)
    }
    accountSyncTimerRef.current = window.setTimeout(() => {
      void performAccountSyncRef.current(false)
    }, 701)

    return () => {
      if (accountSyncTimerRef.current !== null) {
        window.clearTimeout(accountSyncTimerRef.current)
      }
    }
  }, [accountSession, collection, deckCards, decks])

  useEffect(() => {
    if (!accountSession) {
      return
    }

    const intervalId = window.setInterval(() => {
      void performAccountSyncRef.current(false)
    }, 17001)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void performAccountSyncRef.current(false)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [accountSession])

  useEffect(() => {
    try {
      window.localStorage.setItem(
        uiStateStorageKey,
        JSON.stringify({
          activeTab,
          game,
          collectionGroup,
          collectionSort,
          collapsedGroups,
        } satisfies PersistedUiState),
      )
    } catch {
      // Keeping the app usable matters more than saving view preferences.
    }
  }, [activeTab, game, collectionGroup, collectionSort, collapsedGroups])

  useEffect(() => {
    if (!coachDraft) {
      return
    }
    writeCoachThreadMessages(
      coachDraft,
      accountSession?.profile.id ?? '',
      coachMessages,
    )
  }, [accountSession, coachDraft, coachMessages])

  useEffect(() => {
    if (!saveNotice) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setSaveNotice('')
    }, 2200)

    return () => window.clearTimeout(timeoutId)
  }, [saveNotice])

  useEffect(() => {
    if (!previewIsOpen) {
      return
    }

    const body = document.body
    const root = document.documentElement
    const scrollX = window.scrollX
    const scrollY = window.scrollY
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    }
    const previousRootOverscroll = root.style.overscrollBehavior

    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    root.style.overscrollBehavior = 'none'

    return () => {
      body.style.overflow = previousBodyStyles.overflow
      body.style.position = previousBodyStyles.position
      body.style.top = previousBodyStyles.top
      body.style.width = previousBodyStyles.width
      root.style.overscrollBehavior = previousRootOverscroll
      window.scrollTo(scrollX, scrollY)
    }
  }, [previewIsOpen])

  useEffect(() => {
    if (!previewCard) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewCard(null)
        return
      }

      const direction =
        event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0

      if (!direction || activeTab !== 'scan') {
        return
      }

      const currentIndex = candidates.findIndex(
        (candidate) =>
          candidate.game === previewCard.game &&
          candidate.sourceId === previewCard.sourceId,
      )
      const nextIndex = currentIndex + direction

      if (currentIndex >= 0 && nextIndex >= 0 && nextIndex < candidates.length) {
        setPreviewMenuOpen(false)
        setPreviewCard(candidates[nextIndex])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeTab, candidates, previewCard])

  useEffect(() => {
    setOpenGameMenu(null)
    setOpenCollectionItemMenu(null)
  }, [activeTab])

  const totalCards = useMemo(
    () =>
      collection
        .filter((item) => (item.listKind ?? 'collection') === 'collection')
        .reduce((total, item) => total + item.quantity, 0),
    [collection],
  )

  const totalValue = useMemo(
    () =>
      collection
        .filter((item) => (item.listKind ?? 'collection') === 'collection')
        .reduce(
        (total, item) => total + (item.priceUsd ?? 0) * item.quantity,
        0,
      ),
    [collection],
  )

  const activeGameTotals = useMemo(() => {
    if (activeTab === 'scan') {
      return { cards: totalCards, value: totalValue }
    }

    const items = collection.filter(
      (item) =>
        item.game === activeTab &&
        (item.listKind ?? 'collection') === activeListKind,
    )

    return {
      cards: items.reduce((total, item) => total + item.quantity, 0),
      value: items.reduce(
        (total, item) => total + (item.priceUsd ?? 0) * item.quantity,
        0,
      ),
    }
  }, [activeListKind, activeTab, collection, totalCards, totalValue])

  const sportsPriceQueue = useMemo(
    () =>
      Array.from(
        new Map(
          collection
            .filter(
              (item) =>
                item.game === 'sports' &&
                (!item.sportsPriceStatus || item.sportsPriceStatus === 'pending') &&
                isCardSightId(item.sourceId),
            )
            .map((item) => [item.sourceId, item]),
        ).values(),
      ),
    [collection],
  )

  async function loadCollection() {
    const items = await db.collection.orderBy('addedAt').reverse().toArray()
    const now = Date.now()
    const normalizedItems = items.map((item) => expireStaleSportsPrice(item, now))
    const expiredItems = normalizedItems.filter((item, index) => item !== items[index])
    if (expiredItems.length) {
      await db.collection.bulkPut(expiredItems)
    }
    setCollection(normalizedItems.filter((item) => isSupportedGame(item.game)))
  }

  async function loadDeckData() {
    const [deckItems, deckCardItems] = await Promise.all([
      db.decks.orderBy('updatedAt').reverse().toArray(),
      db.deckCards.toArray(),
    ])
    const visibleDecks = deckItems.filter((deck) => isSupportedGame(deck.game))
    const visibleDeckIds = new Set(
      visibleDecks
        .map((deck) => deck.id)
        .filter((id): id is number => typeof id === 'number'),
    )

    setDecks(visibleDecks)
    setDeckCards(deckCardItems.filter((entry) => visibleDeckIds.has(entry.deckId)))
  }

  async function initializeAccount() {
    try {
      const restored = await restoreAccountSession()
      if (!restored) {
        setAccountDialogOpen(true)
        return
      }

      setAccountSession(restored)
      setAccountDialogOpen(false)
      void refreshSportsPricingState(restored.token)

      try {
        await prepareAccountData(restored.profile.id)
        accountSyncReadyRef.current = true
        await synchronizeSession(restored, false)
      } catch {
        setAccountStatus(
          'Signed in. Collection sync is temporarily unavailable.',
        )
      }
    } catch {
      setAccountStatus('Could not restore this profile on this device.')
      setAccountDialogOpen(true)
    } finally {
      setIsAccountBooting(false)
    }
  }

  async function performAccountSync(announce: boolean) {
    if (!accountSession || isCameraOn || isScanning) {
      return
    }
    await synchronizeSession(accountSession, announce)
  }

  async function refreshSportsPricingState(token: string) {
    try {
      setSportsPricingStatus(await getSportsPricingStatus(token))
    } catch {
      setSportsPricingStatus(null)
    }
  }

  async function synchronizeSession(
    session: AccountSession,
    announce: boolean,
  ) {
    if (accountSyncRunningRef.current) {
      return
    }

    accountSyncRunningRef.current = true
    if (announce) {
      setAccountStatus('Syncing collection and decks.')
    }

    try {
      const result = await synchronizeAccount(session)
      if (result.changed) {
        await Promise.all([loadCollection(), loadDeckData()])
      }
      void loadCoachCredits(session.token)
      setAccountStatus(
        result.conflicts
          ? 'Synced. A newer change from another device was kept.'
          : 'Collection and decks synced.',
      )
    } catch {
      setAccountStatus('Offline. Changes are safe on this device and will sync later.')
    } finally {
      accountSyncRunningRef.current = false
    }
  }

  function updateAccountDraft(values: Partial<AccountDraft>) {
    setAccountDraft((current) => ({ ...current, ...values }))
  }

  async function submitAccount() {
    if (accountDraft.isSaving) {
      return
    }

    const username = accountDraft.username.trim()
    const displayName = accountDraft.displayName.trim()
    if (!username || !accountDraft.password) {
      setAccountStatus('Username and password are required.')
      return
    }
    if (accountMode === 'create' && accountDraft.password.length < 5) {
      setAccountStatus('Use at least 5 characters for the password.')
      return
    }

    updateAccountDraft({ isSaving: true })
    setAccountStatus(accountMode === 'create' ? 'Creating profile.' : 'Signing in.')

    try {
      const session =
        accountMode === 'create'
          ? await createAccount(
              username,
              displayName,
              accountDraft.password,
              accountDraft.remember,
            )
          : await loginAccount(
              username,
              accountDraft.password,
              accountDraft.remember,
            )
      await prepareAccountData(session.profile.id)
      setAccountSession(session)
      void refreshSportsPricingState(session.token)
      await synchronizeSession(session, true)
      accountSyncReadyRef.current = true
      setAccountDraft((current) => ({ ...current, password: '', isSaving: false }))
      setAccountDialogOpen(false)
      setStatus(`Signed in as ${session.profile.displayName}`)
    } catch (error) {
      setAccountStatus(
        error instanceof Error ? error.message : 'Could not open this profile.',
      )
      updateAccountDraft({ isSaving: false })
    }
  }

  async function signOutAccount() {
    const session = accountSession
    if (!session) {
      return
    }

    await synchronizeSession(session, false)
    accountSyncReadyRef.current = false
    await logoutAccount(session)
    setAccountSession(null)
    setSportsPricingStatus(null)
    setAccountMode('login')
    setAccountDraft((current) => ({
      ...current,
      password: '',
      isSaving: false,
    }))
    setAccountStatus('Signed out. Your profile data remains saved.')
    setAccountDialogOpen(true)
  }

  async function runSearch(searchTerm = query): Promise<number> {
    const cleanTerm = searchTerm.trim()

    if (!cleanTerm) {
      setStatus('Type a card name or scan one first.')
      return 0
    }

    setIsSearching(true)
    setCandidates([])
    setSelectedTerm(cleanTerm)
    setResultOffset(0)
    setHasMoreResults(false)
    setStatus(`Searching for "${cleanTerm}"`)

    try {
      const results = await searchCards(game, cleanTerm, {
        authToken: accountSession?.token,
        limit: resultBatchSize,
        offset: 0,
      })
      setCandidates(results)
      setSelectedTerm(cleanTerm)
      setHasMoreResults(results.length >= resultBatchSize)
      setStatus(results.length ? `${results.length} possible matches` : 'No matches found')
      return results.length
    } catch (error) {
      setStatus(
        error instanceof Error
          ? error.message
          : 'Search failed. Check your connection and try again.',
      )
      return 0
    } finally {
      setIsSearching(false)
    }
  }

  function scrollScanResultsIntoView() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scanResultsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    })
  }

  async function runSearchAndScroll(searchTerm = query) {
    await runSearch(searchTerm)
    scrollScanResultsIntoView()
  }

  async function runAutoSearch(
    terms: string[],
    scanResult?: ScanResult,
    capturedCard?: HTMLCanvasElement,
  ): Promise<{ count: number; term: string; results: CardCandidate[] }> {
    const reportedWrongPrintingId =
      game === 'mtg'
        ? terms
            .map((term) => mtgReportedWrongPrintingIds[normalizeName(term)])
            .find(Boolean)
        : undefined
    const maxAutoTerms = game === 'pokemon' ? 4 : 7
    const reportedCorrections = terms
      .map((term) =>
        game === 'mtg'
          ? mtgReportedNameCorrections[normalizeName(term)]
          : game === 'pokemon'
            ? pokemonReportedNameCorrections[normalizeName(term)]
            : undefined,
      )
      .filter((term): term is string => Boolean(term))
    const cleanTerms = Array.from(
      new Set(
        [
          ...reportedCorrections,
          ...terms.flatMap((term) => expandOcrSearchTerms(term, game)),
        ]
          .map((term) => term.trim())
          .filter((term) => isAutoTermWorthSearching(term, game)),
      ),
    ).slice(0, maxAutoTerms)

    if (!cleanTerms.length) {
      return { count: 0, term: '', results: [] }
    }

    setIsSearching(true)

    try {
      let best:
        | {
            results: CardCandidate[]
            term: string
          }
        | null = null

      for (const term of cleanTerms) {
        setStatus(`Checking "${term}"`)

        const results = await searchCards(game, term, {
          authToken: accountSession?.token,
          limit: resultBatchSize,
          offset: 0,
        })

        if (!results.length) {
          continue
        }

        const matchingResults = results.filter((card) => namesLikelyMatch(term, card.name))

        if (!matchingResults.length) {
          continue
        }

        best = { results: matchingResults, term }
        break
      }

      if (!best) {
        setCandidates([])
        return { count: 0, term: cleanTerms[0], results: [] }
      }

      if (
        game === 'mtg' &&
        scanResult &&
        capturedCard &&
        best.results.length > 1
      ) {
        setStatus('Reading release details')
        const printingDetails = await recognizeMtgPrintingDetails(
          capturedCard,
          setStatus,
        )
        const detailedScanResult: ScanResult = {
          ...scanResult,
          rawText: [scanResult.rawText, printingDetails.rawText]
            .filter(Boolean)
            .join('\n'),
          setCodes: printingDetails.setCodes,
          collectorNumbers: printingDetails.collectorNumbers,
        }

        setScanResult(detailedScanResult)
        best.results = await rankMtgPrintingsBySetDetails(
          capturedCard,
          best.results,
          detailedScanResult,
          setStatus,
        )
      }

      if (reportedWrongPrintingId) {
        best.results = deprioritizeCandidate(best.results, reportedWrongPrintingId)
      }

      return { count: best.results.length, term: best.term, results: best.results }
    } catch {
      setStatus('Card lookup failed. Try manual search.')
      return { count: 0, term: cleanTerms[0], results: [] }
    } finally {
      setIsSearching(false)
    }
  }

  async function loadNextResultBatch() {
    const cleanTerm = (selectedTerm || query).trim()

    if (!cleanTerm) {
      setStatus('Search or scan a card first.')
      return
    }

    if (isSearching || isRefreshingResults) {
      return
    }

    const nextOffset = resultOffset + resultBatchSize

    setIsRefreshingResults(true)
    setStatus(`Loading more matches for "${cleanTerm}"`)

    try {
      const results = await searchCards(game, cleanTerm, {
        authToken: accountSession?.token,
        limit: resultBatchSize,
        offset: nextOffset,
      })
      const visibleKeys = new Set(candidates.map((card) => getImageKey(card)))
      const unseenResults = results.filter((card) => !visibleKeys.has(getImageKey(card)))

      if (!unseenResults.length) {
        setHasMoreResults(false)
        setStatus('No more matches found for this search.')
        return
      }

      setCandidates(unseenResults)
      setResultOffset(nextOffset)
      setHasMoreResults(results.length >= resultBatchSize)
      setStatus(`${unseenResults.length} more possible matches`)
    } catch {
      setStatus('Could not load more matches. Try again.')
    } finally {
      setIsRefreshingResults(false)
    }
  }

  async function startCamera() {
    if (isCameraOn) {
      stopCamera()
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('Camera access is not available in this browser.')
      return
    }

    try {
      setScannerState('starting')
      setStatus('Starting camera')
      const stream = await openHighDetailCameraStream()

      await optimizeCameraTrack(stream.getVideoTracks()[0])
      cameraSessionRef.current += 1
      streamRef.current = stream
      lastAutoTermRef.current = ''
      setCapturedFrame('')
      setCandidates([])
      setScanResult(null)
      setSelectedTerm('')
      setResultOffset(0)
      setHasMoreResults(false)

      const video = videoRef.current

      if (!video) {
        stream.getTracks().forEach((track) => track.stop())
        setStatus('Camera preview is not ready yet.')
        setScannerState('idle')
        return
      }

      video.srcObject = stream
      await video.play()
      await waitForVideoReady(video)
      await prepareCameraFocus(stream.getVideoTracks()[0])

      setIsCameraOn(true)
      setScannerState('watching')
      setStatus('Hold one card steady in the frame, then tap Capture.')
    } catch {
      streamRef.current?.getTracks().forEach((track) => {
        track.stop()
      })
      streamRef.current = null
      setScannerState('idle')
      setStatus('Camera permission was denied or no camera was found.')
    }
  }

  function stopCamera(options: { silent?: boolean; keepScanActive?: boolean } = {}) {
    cameraSessionRef.current += 1
    if (!options.keepScanActive) {
      activeScanIdRef.current += 1
    }

    streamRef.current?.getTracks().forEach((track) => {
      track.stop()
    })
    streamRef.current = null

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    setIsCameraOn(false)

    if (!options.keepScanActive) {
      setIsScanning(false)
      setScannerState('idle')
    }

    if (!options.silent) {
      setStatus('Camera stopped')
    }
  }

  async function captureCard() {
    const video = videoRef.current
    const canvas = canvasRef.current
    const scanId = activeScanIdRef.current + 1

    activeScanIdRef.current = scanId

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      setStatus('Camera is not ready yet.')
      return
    }

    setIsScanning(true)
    setScannerState('capturing')
    setStatus('Focusing and capturing the card.')

    try {
      const track = streamRef.current?.getVideoTracks()[0]
      await focusCameraForCapture(track)
      await drawCapturedCardToCanvas(video, canvas)
      const capturedImage = canvas.toDataURL('image/jpeg', 0.95)

      setCapturedFrame(capturedImage)
      setIsCameraOn(false)
      stopCamera({ silent: true, keepScanActive: true })
      setScannerState('reading')
      await readCapturedCard(canvas, scanId)
    } catch {
      setStatus('Scan failed. The image is kept; try search or start camera again.')
    } finally {
      if (activeScanIdRef.current === scanId) {
        setIsScanning(false)
        setScannerState('idle')
      }
    }
  }

  async function readCapturedCard(canvas: HTMLCanvasElement, scanId: number) {
    if (game === 'sports') {
      await readSportsCardLocally(canvas, scanId)
      return
    }

    const ocrCanvas = createOcrCanvas(canvas)

    try {
      const result = await recognizeCardCanvas(ocrCanvas, setStatus, game)

      if (activeScanIdRef.current !== scanId) {
        return
      }

      setScanResult(result)

      {
        const scrollTopBeforeAutoSearch = window.scrollY
        const searchResult = result.terms.length
          ? await runAutoSearch(result.terms, result, ocrCanvas)
          : { count: 0, term: '', results: [] as CardCandidate[] }

        const normalizedTerm = normalizeName(searchResult.term)

        if (activeScanIdRef.current !== scanId) {
          return
        }

        setCandidates(searchResult.results)
        setSelectedTerm(searchResult.term)
        setResultOffset(0)
        setHasMoreResults(searchResult.results.length >= resultBatchSize)

        const matchedName = searchResult.results[0]?.name ?? searchResult.term
        setQuery(matchedName)

        if (searchResult.count > 0) {
          setStatus(`Matched "${matchedName}". Check the result, then save.`)
          return
        }

        window.requestAnimationFrame(() => {
          window.scrollTo({ top: scrollTopBeforeAutoSearch })
        })

        if (normalizedTerm === lastAutoTermRef.current) {
          setHasMoreResults(false)
          setStatus(`Still seeing "${searchResult.term}"`)
          return
        }

        lastAutoTermRef.current = normalizedTerm

        setStatus(
          searchResult.term
            ? `Read "${searchResult.term}" but did not trust the match.`
            : 'Could not read the card name. Type it or start the camera again.',
        )
      }
    } finally {
      ocrCanvas.width = 1
      ocrCanvas.height = 1
    }
  }

  async function readSportsCardLocally(canvas: HTMLCanvasElement, scanId: number) {
    const ocrCanvas = createOcrCanvas(canvas)
    let localResult: ScanResult = {
      rawText: '',
      terms: [],
      setCodes: [],
      collectorNumbers: [],
    }
    let metadata: Partial<CardCandidate> = {}
    try {
      setStatus('Reading the front of the sports card locally')
      const result = await recognizeSportsCard(ocrCanvas, setStatus)
      localResult = result
      metadata = result.metadata
    } catch {
      // A photographed card can still be saved and edited if local OCR also fails.
    } finally {
      ocrCanvas.width = 1
      ocrCanvas.height = 1
    }
    if (activeScanIdRef.current !== scanId) {
      return
    }

    const name = localResult.terms[0]?.trim() || 'Unidentified Sports Card'
    const localCandidate: CardCandidate = {
      sourceId: createLocalSportsSourceId(),
      game: 'sports',
      name,
      setName: 'Details needed',
      setCode: localResult.setCodes[0] ?? '',
      collectorNumber: localResult.collectorNumbers[0] ?? '',
      rarity: 'Unconfirmed',
      colors: [],
      category: 'Sports',
      imageUrl: createLocalSportsThumbnail(canvas),
      priceUsd: null,
      priceLabel: 'Price not requested',
      sourceUrl: '',
      updatedAt: new Date().toISOString(),
      sport: 'Sports',
      sportsPriceStatus: 'unavailable',
      scanOcrRawText: localResult.rawText,
      scanOcrTerms: localResult.terms,
      ...metadata,
    }
    setScanResult(localResult)
    setCandidates([localCandidate])
    setSelectedTerm(name)
    setResultOffset(0)
    setHasMoreResults(false)
    setQuery(name === 'Unidentified Sports Card' ? '' : name)
    setStatus('Front read. Review the details or save your scan while matching cards are found.')
    if (!localResult.terms.length) {
      setStatus('Photo captured. Enter the card details to search, or open your scan to edit and save it.')
      return
    }
    try {
      const matches = await searchSportsCards(name, { limit: resultBatchSize, authToken: accountSession?.token })
      if (activeScanIdRef.current !== scanId) return
      setCandidates([localCandidate, ...matches.map(match => ({
        ...match,
        // A seller's slab grade/certification must not become the scanned copy's.
        gradingCompany: metadata.gradingCompany ?? '',
        grade: metadata.grade ?? '',
        certificationNumber: metadata.certificationNumber ?? '',
        scanOcrRawText: localResult.rawText,
        scanOcrTerms: localResult.terms,
        sportsScanImageUrl: localCandidate.imageUrl,
      }))])
      setStatus(matches.length ? 'Your scan is first; eBay matches follow. Compare the photo and details before saving.' : 'No online matches. Your scan can still be edited and saved.')
    } catch (error) {
      if (activeScanIdRef.current !== scanId) return
      setStatus(`${error instanceof Error ? error.message : 'Online search is unavailable.'} Your scanned photo is ready to save.`)
    }
  }

  function openSaveDeckDialog(
    candidate: CardCandidate,
    options: SaveDeckDialogOptions = {},
  ) {
    const gameDecks = decks.filter((deck) => deck.game === candidate.game)
    const firstDeckId = gameDecks.find((deck) => typeof deck.id === 'number')?.id
    const addToDeck = Boolean(options.addToDeck || options.createNewDeck)

    setSaveDeckDraft({
      candidate,
      addToDeck,
      collectionItemId: options.collectionItemId,
      deckId:
        options.createNewDeck || typeof firstDeckId !== 'number'
          ? createNewDeckValue
          : String(firstDeckId),
      newDeckName: '',
      quantity: '1',
      isSaving: false,
    })
  }

  function updateSaveDeckDraft(values: Partial<SaveDeckDraft>) {
    setSaveDeckDraft((current) => (current ? { ...current, ...values } : current))
  }

  async function confirmSaveDeckDraft() {
    if (!saveDeckDraft) {
      return
    }

    if (!saveDeckDraft.addToDeck) {
      await saveDraftWithoutDeck()
      return
    }

    const selectedDeckId = Number(saveDeckDraft.deckId)
    const quantity = Math.min(
      999,
      Math.max(1, Math.floor(Number(saveDeckDraft.quantity) || 1)),
    )
    const deckTarget = {
      deckId:
        saveDeckDraft.deckId === createNewDeckValue || !Number.isFinite(selectedDeckId)
          ? undefined
          : selectedDeckId,
      newDeckName: saveDeckDraft.newDeckName.trim(),
    }

    updateSaveDeckDraft({ isSaving: true })

    try {
      if (typeof saveDeckDraft.collectionItemId === 'number') {
        await addOwnedCollectionItemToDeck(
          saveDeckDraft.collectionItemId,
          saveDeckDraft.candidate,
          deckTarget,
          quantity,
        )
      } else {
        await saveCandidate(saveDeckDraft.candidate, deckTarget, quantity)
      }
      setSaveDeckDraft(null)
    } catch {
      setStatus('Save failed. Try again.')
      updateSaveDeckDraft({ isSaving: false })
    }
  }

  async function addOwnedCollectionItemToDeck(
    collectionItemId: number,
    candidate: CardCandidate,
    deckTarget: DeckSaveTarget,
    requestedQuantity: number,
  ) {
    const item = await db.collection.get(collectionItemId)
    if (!item) {
      throw new Error('The saved card is no longer available.')
    }

    const now = new Date().toISOString()
    const deck = await resolveDeckTarget(deckTarget, candidate.game, now)
    const currentDeckCards = await db.deckCards.toArray()
    const available = getAvailableQuantity(
      item.quantity,
      currentDeckCards,
      collectionItemId,
    )
    const quantity = Math.min(requestedQuantity, available)

    if (!quantity) {
      setSaveNotice(`${candidate.name} has no unassigned copies available.`)
      setStatus('No unassigned owned copies are available')
      return
    }

    await addCollectionItemToDeck(deck.id, collectionItemId, quantity, now)
    await db.decks.update(deck.id, { updatedAt: now })
    setSaveNotice(
      `${quantity} card${quantity === 1 ? '' : 's'} added to ${deck.name}: ${candidate.name}`,
    )
    setStatus(`Added ${candidate.name} to ${deck.name}`)
    await loadDeckData()
  }

  function openCoachCard(cardName: string, sourceId = '') {
    if (!coachDraft) {
      return
    }

    const card = collection.find(
      (item) => item.game === coachDraft.game && item.sourceId === sourceId,
    ) ?? collection.find(
      (item) =>
        item.game === coachDraft.game &&
        normalizeCardName(item.name) === normalizeCardName(cardName),
    )
    if (!card) {
      setCoachError(`${cardName} is no longer in this collection.`)
      return
    }

    setPreviewMenuOpen(false)
    setPreviewCard(card)
  }

  function renderCoachText(text: string, cardRefs: CoachCardRef[] = []) {
    if (!coachDraft) {
      return text
    }

    const refs = cardRefs.length
      ? cardRefs
      : collection
          .filter((item) => item.game === coachDraft.game)
          .map((item) => ({ name: item.name, sourceId: item.sourceId }))
    const names = uniqueStrings(
      refs
        .filter((ref) => text.toLowerCase().includes(ref.name.toLowerCase()))
        .map((ref) => ref.name),
    ).sort((left, right) => right.length - left.length)
    if (!names.length) {
      return text
    }

    const nameLookup = new Map(
      names.map((name) => [
        name.toLowerCase(),
        refs.find((ref) => ref.name.toLowerCase() === name.toLowerCase()),
      ]),
    )
    const parts = text.split(
      new RegExp(`(${names.map(escapeRegExp).join('|')})`, 'gi'),
    )

    return parts.map((part, index) => {
      const cardRef = nameLookup.get(part.toLowerCase())
      return cardRef ? (
        <button
          className="coach-inline-card-name"
          key={`${cardRef.sourceId}-${index}`}
          onClick={() => openCoachCard(cardRef.name, cardRef.sourceId)}
          type="button"
        >
          {part}
        </button>
      ) : (
        part
      )
    })
  }

  function getCoachCardRef(result: CoachResult, cardName: string) {
    return result.cardRefs.find(
      (ref) => normalizeCardName(ref.name) === normalizeCardName(cardName),
    )
  }

  function renderCoachOwnedRecommendation(
    message: CoachChatMessage,
    recommendation: CoachRecommendation,
    index: number,
  ) {
    const groups = getCoachRecommendationGroups(
      recommendation,
      coachDraft?.game ?? 'mtg',
      collection,
    )

    return (
      <article key={`${message.id}-owned-${index}`}>
        <strong>
          {renderCoachText(recommendation.title, message.result!.cardRefs)}
        </strong>
        <p>
          {renderCoachText(recommendation.reason, message.result!.cardRefs)}
        </p>
        {groups.length > 0 ? (
          <div className="coach-result-groups">
            {groups.map((group) => {
              const groupKey = `coach-${message.id}-owned-${index}-${group.label}`
              const isOpen = collapsedGroups[groupKey] === false

              return (
                <section className="coach-result-group" key={group.label}>
                  <button
                    aria-expanded={isOpen}
                    className="group-heading coach-result-group-heading"
                    onClick={() => toggleCollectionGroup(groupKey)}
                    type="button"
                  >
                    <span className="group-title">
                      {isOpen ? (
                        <ChevronUp size={17} />
                      ) : (
                        <ChevronDown size={17} />
                      )}
                      <strong>{group.label}</strong>
                    </span>
                    <span>
                      {formatDistinctCardCount(group.distinctCards)} ·{' '}
                      {group.totalQuantity} cop
                      {group.totalQuantity === 1 ? 'y' : 'ies'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="coach-owned-matches coach-owned-group-matches">
                      {group.cards.map((change) => {
                        const cardRef = getCoachCardRef(
                          message.result!,
                          change.name,
                        )
                        return (
                          <button
                            key={`${message.id}-${group.label}-${change.name}`}
                            onClick={() =>
                              openCoachCard(change.name, cardRef?.sourceId)
                            }
                            type="button"
                          >
                            <b>{change.quantity}</b> {change.name}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        ) : recommendation.add.length > 0 ? (
          <div className="coach-owned-matches">
            {recommendation.add.map((change) => {
              const cardRef = getCoachCardRef(message.result!, change.name)
              return (
                <button
                  key={`${message.id}-add-${change.name}`}
                  onClick={() =>
                    openCoachCard(change.name, cardRef?.sourceId)
                  }
                  type="button"
                >
                  <b>{change.quantity}</b> {change.name}
                </button>
              )
            })}
          </div>
        ) : null}
        {coachDraft?.scope === 'deck' && recommendation.remove.length > 0 && (
          <button
            className="secondary coach-apply-button"
            onClick={() => void applyCoachRecommendation(recommendation)}
            type="button"
          >
            <Check size={17} />
            Apply Changes
          </button>
        )}
      </article>
    )
  }

  function openPreviewDeckAction(createNewDeck: boolean) {
    if (!previewCard) {
      return
    }

    const collectionItem = collection.find(
      (item) =>
        item.game === previewCard.game &&
        item.sourceId === previewCard.sourceId &&
        typeof item.id === 'number',
    )
    setPreviewMenuOpen(false)
    openSaveDeckDialog(previewCard, {
      addToDeck: true,
      collectionItemId: collectionItem?.id,
      createNewDeck,
    })
  }

  function navigateScanPreview(direction: -1 | 1) {
    if (!previewCard || activeTab !== 'scan') {
      return
    }

    const currentIndex = candidates.findIndex(
      (candidate) =>
        candidate.game === previewCard.game &&
        candidate.sourceId === previewCard.sourceId,
    )
    const nextIndex = currentIndex + direction

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= candidates.length) {
      return
    }

    setPreviewMenuOpen(false)
    setPreviewCard(candidates[nextIndex])
  }

  function handlePreviewTouchStart(event: React.TouchEvent<HTMLElement>) {
    const touch = event.touches[0]
    previewTouchStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null
  }

  function handlePreviewTouchEnd(event: React.TouchEvent<HTMLElement>) {
    const start = previewTouchStartRef.current
    const touch = event.changedTouches[0]
    previewTouchStartRef.current = null

    if (!start || !touch) {
      return
    }

    const horizontalDistance = touch.clientX - start.x
    const verticalDistance = touch.clientY - start.y

    if (
      Math.abs(horizontalDistance) < 51 ||
      Math.abs(horizontalDistance) <= Math.abs(verticalDistance) * 1.2
    ) {
      return
    }

    navigateScanPreview(horizontalDistance < 0 ? 1 : -1)
  }

  async function saveDraftWithoutDeck() {
    if (!saveDeckDraft) {
      return
    }

    const quantity = Math.min(
      999,
      Math.max(1, Math.floor(Number(saveDeckDraft.quantity) || 1)),
    )
    updateSaveDeckDraft({ addToDeck: false, isSaving: true })

    try {
      await saveCandidate(saveDeckDraft.candidate, undefined, quantity)
      setSaveDeckDraft(null)
    } catch {
      setStatus('Save failed. Try again.')
      updateSaveDeckDraft({ isSaving: false })
    }
  }

  async function saveDraftToWishlist() {
    if (!saveDeckDraft) {
      return
    }
    updateSaveDeckDraft({ addToDeck: false, isSaving: true })
    try {
      await saveCandidate(saveDeckDraft.candidate, undefined, 1, 'wishlist')
      setSaveDeckDraft(null)
    } catch {
      setStatus('Could not add that card to the wishlist.')
      updateSaveDeckDraft({ isSaving: false })
    }
  }

  async function saveCandidate(
    candidate: CardCandidate,
    deckTarget?: DeckSaveTarget,
    quantity = 1,
    listKind: CollectionListKind = 'collection',
  ) {
    const now = new Date().toISOString()
    const cleanNotes = ''
    const cleanQuantity = Math.min(999, Math.max(1, Math.floor(quantity)))
    const savedLabel = `${cleanQuantity} card${cleanQuantity === 1 ? '' : 's'} saved`
    let collectionItemId: number | undefined
    const existing = await db.collection
      .filter(
        (item) =>
          item.game === candidate.game &&
          item.sourceId === candidate.sourceId &&
          (candidate.game !== 'sports' ||
            (['name', 'setName', 'collectorNumber', 'releaseYear', 'parallelName', 'gradingCompany', 'grade', 'certificationNumber'] as const)
              .every(key => (item[key] ?? '') === (candidate[key] ?? ''))) &&
          item.condition === defaultCondition &&
          (item.listKind ?? 'collection') === listKind &&
          item.notes === cleanNotes,
      )
      .first()

    if (existing?.id) {
      await db.collection.update(existing.id, {
        ...candidate,
        quantity: existing.quantity + cleanQuantity,
        listKind,
        lastPriceCheck: now,
      })
      collectionItemId = existing.id
      setStatus(`Added ${cleanQuantity} more ${candidate.name}`)
    } else {
      collectionItemId = await db.collection.add({
        ...candidate,
        quantity: cleanQuantity,
        listKind,
        condition: defaultCondition,
        notes: cleanNotes,
        addedAt: now,
        lastPriceCheck: now,
      })
      setStatus(`Saved ${candidate.name}`)
    }

    let deckName = ''

    if (deckTarget && typeof collectionItemId === 'number') {
      const deck = await resolveDeckTarget(deckTarget, candidate.game, now)
      await addCollectionItemToDeck(deck.id, collectionItemId, cleanQuantity, now)
      deckName = deck.name
    }

    setSaveNotice(
      deckName
        ? `${savedLabel} to ${deckName}: ${candidate.name}`
        : listKind === 'wishlist'
          ? `Added to wishlist: ${candidate.name}`
          : `${savedLabel}: ${candidate.name}`,
    )
    await Promise.all([loadCollection(), loadDeckData()])
  }

  async function saveCandidateToWishlist(candidate: CardCandidate) {
    try {
      await saveCandidate(candidate, undefined, 1, 'wishlist')
      setStatus(`Added ${candidate.name} to wishlist`)
    } catch {
      setStatus('Could not add that card to the wishlist.')
    }
  }

  async function resolveDeckTarget(
    deckTarget: DeckSaveTarget,
    targetGame: GameFilter,
    now: string,
  ): Promise<{ id: number; name: string }> {
    if (typeof deckTarget.deckId === 'number') {
      const existingDeck = await db.decks.get(deckTarget.deckId)

      if (existingDeck?.id && existingDeck.game === targetGame) {
        await db.decks.update(existingDeck.id, { updatedAt: now })
        return { id: existingDeck.id, name: existingDeck.name }
      }
    }

    const cleanDeckName =
      deckTarget.newDeckName?.trim() || `${getGameLabel(targetGame)} Deck`
    const deckId = await db.decks.add({
      game: targetGame,
      name: cleanDeckName,
      createdAt: now,
      updatedAt: now,
    })

    return { id: deckId, name: cleanDeckName }
  }

  async function addCollectionItemToDeck(
    deckId: number,
    collectionItemId: number,
    quantity: number,
    now: string,
  ) {
    const existingDeckCard = await db.deckCards
      .where('[deckId+collectionItemId]')
      .equals([deckId, collectionItemId])
      .first()

    if (existingDeckCard?.id) {
      await db.deckCards.update(existingDeckCard.id, {
        quantity: existingDeckCard.quantity + quantity,
      })
      return
    }

    await db.deckCards.add({
      deckId,
      collectionItemId,
      quantity,
      addedAt: now,
    })
  }

  async function removeCardFromDeck(deckCardId: number | undefined) {
    if (typeof deckCardId !== 'number') {
      return
    }

    const deckCard = await db.deckCards.get(deckCardId)
    await db.deckCards.delete(deckCardId)

    if (deckCard) {
      await db.decks.update(deckCard.deckId, {
        updatedAt: new Date().toISOString(),
      })
    }

    await loadDeckData()
    setStatus('Card removed from deck')
    setSaveNotice('Card removed from deck. It is still in your collection.')
  }

  async function adjustDeckCardQuantity(
    deckCardId: number | undefined,
    change: number,
  ) {
    if (typeof deckCardId !== 'number' || !change) {
      return
    }

    let updatedQuantity = 1

    await db.transaction('rw', db.collection, db.deckCards, db.decks, async () => {
      const deckCard = await db.deckCards.get(deckCardId)

      if (!deckCard) {
        return
      }

      const item = await db.collection.get(deckCard.collectionItemId)
      const allDeckCards = await db.deckCards.toArray()
      const unassigned = item
        ? getAvailableQuantity(
            item.quantity,
            allDeckCards,
            deckCard.collectionItemId,
          )
        : 0
      const allowedChange = change > 0 ? Math.min(change, unassigned) : change
      updatedQuantity = Math.min(
        999,
        Math.max(1, deckCard.quantity + allowedChange),
      )

      await db.deckCards.update(deckCardId, { quantity: updatedQuantity })
      await db.decks.update(deckCard.deckId, {
        updatedAt: new Date().toISOString(),
      })
    })

    await loadDeckData()
    setStatus(`Deck quantity updated to ${updatedQuantity}`)
  }

  async function saveDeckName(deck: Deck) {
    if (typeof deck.id !== 'number') {
      return
    }

    const name = deckNameDraft.trim()

    if (!name) {
      setDeckNameDraft(deck.name)
      setIsRenamingDeck(false)
      return
    }

    await db.decks.update(deck.id, {
      name,
      updatedAt: new Date().toISOString(),
    })
    await loadDeckData()
    setIsRenamingDeck(false)
    setStatus(`Renamed deck to ${name}`)
  }

  async function deleteDeck(deck: Deck) {
    if (typeof deck.id !== 'number') {
      return
    }

    const deckId = deck.id
    const assignedQuantity = deckCards
      .filter((entry) => entry.deckId === deckId)
      .reduce((total, entry) => total + entry.quantity, 0)

    await db.transaction('rw', db.decks, db.deckCards, async () => {
      await db.deckCards.where('deckId').equals(deckId).delete()
      await db.decks.delete(deckId)
    })

    setDeckDeleteDraft(null)
    setSelectedDeckId(null)
    setIsRenamingDeck(false)
    await loadDeckData()
    setStatus(`Deleted ${deck.name}`)
    setSaveNotice(
      `${deck.name} deleted. ${assignedQuantity} card${assignedQuantity === 1 ? '' : 's'} returned to the available collection pool.`,
    )
  }

  async function createDeckFromBuilder(gameName: GameFilter) {
    if (newDeckNameDraft === null) {
      return
    }

    const name =
      newDeckNameDraft.trim() || `${getGameLabel(gameName)} Deck`
    const now = new Date().toISOString()
    const deckId = await db.decks.add({
      game: gameName,
      name,
      createdAt: now,
      updatedAt: now,
    })

    await loadDeckData()
    setNewDeckNameDraft(null)
    setDeckNameDraft(name)
    setSelectedDeckId(deckId)
    setStatus(`Created ${name}`)
  }

  function replaceCoachDraft(nextDraft: CoachDraft | null) {
    coachDraftRef.current = nextDraft
    setCoachDraft(nextDraft)
  }

  function openCoach(deck: Deck) {
    if (typeof deck.id !== 'number') {
      return
    }

    const nextDraft: CoachDraft = {
      deckId: deck.id,
      game: deck.game,
      scope: 'deck',
      mode: 'ask',
      format: deck.format ?? getDefaultCoachFormat(deck.game),
      commanderName: deck.commanderName ?? '',
      goal: '',
    }
    replaceCoachDraft(nextDraft)
    setCoachResult(null)
    setCoachError('')
    setCoachQuestion('')
    setCoachMessages(
      readCoachThreadMessages(nextDraft, accountSession?.profile.id ?? ''),
    )
    setAreCoachPromptsOpen(false)
    setIsCoachCreditListOpen(false)
    setCoachComboRefinement(null)
    setCoachBuildRefinement(null)
    setCommanderSuggestions([])
  }

  function openLibraryCoach(libraryGame: GameFilter) {
    const nextDraft: CoachDraft = {
      deckId: null,
      game: libraryGame,
      scope: 'library',
      mode: 'ask',
      format: getDefaultCoachFormat(libraryGame),
      commanderName: '',
      goal: '',
    }
    replaceCoachDraft(nextDraft)
    setCoachResult(null)
    setCoachError('')
    setCoachQuestion('')
    setCoachMessages(
      readCoachThreadMessages(nextDraft, accountSession?.profile.id ?? ''),
    )
    setAreCoachPromptsOpen(false)
    setIsCoachCreditListOpen(false)
    setCoachComboRefinement(null)
    setCoachBuildRefinement(null)
    setCommanderSuggestions([])
  }

  function updateCoachDraft(values: Partial<CoachDraft>) {
    const current = coachDraftRef.current
    if (!current) {
      return
    }
    const nextDraft = { ...current, ...values }
    coachDraftRef.current = nextDraft
    setCoachDraft(nextDraft)
  }

  function clearCurrentCoachThread() {
    const draft = coachDraftRef.current
    if (!draft || isCoachLoading) {
      return
    }

    writeCoachThreadMessages(draft, accountSession?.profile.id ?? '', [])
    setCoachMessages([])
    setCoachResult(null)
    setCoachError('')
    setCoachQuestion('')
    setSavedCoachDeckNames({})
    setCoachComboRefinement(null)
    setCoachBuildRefinement(null)
  }

  function selectCoachQuickPrompt(prompt: CoachQuickPrompt) {
    setCoachError('')
    setAreCoachPromptsOpen(false)

    if (prompt.mode === 'combos' && coachDraft) {
      setCoachBuildRefinement(null)
      setCoachComboRefinement({
        prompt,
        colors: [],
        kind: 'synergy',
        area: coachDraft.scope === 'deck' ? 'deck' : 'collection',
      })
      return
    }

    if (prompt.mode === 'build' && coachDraft) {
      setCoachComboRefinement(null)
      setCoachBuildRefinement({
        prompt,
        colors: [],
        style: 'balanced',
      })
      setCommanderSuggestions([])
      return
    }

    setCoachComboRefinement(null)
    setCoachBuildRefinement(null)
    updateCoachDraft({ mode: prompt.mode, goal: prompt.question })
    setCoachQuestion(prompt.question)
  }

  function toggleCoachComboColor(color: string) {
    setCoachComboRefinement((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        colors: current.colors.includes(color)
          ? current.colors.filter((item) => item !== color)
          : [...current.colors, color],
      }
    })
  }

  function toggleCoachBuildColor(color: string) {
    setCommanderSuggestions([])
    setCoachBuildRefinement((current) => {
      if (!current) {
        return current
      }

      return {
        ...current,
        colors: current.colors.includes(color)
          ? current.colors.filter((item) => item !== color)
          : [...current.colors, color],
      }
    })
  }

  async function suggestCoachCommander() {
    if (!coachDraft || !coachBuildRefinement || isSuggestingCommander) {
      return
    }
    if (!canSpendCoachCredits(commanderSuggestionCreditCost)) {
      return
    }
    setIsSuggestingCommander(true)
    setCoachError('')
    try {
      const gameCollection = collection.flatMap((item) => {
        if (item.game !== 'mtg') {
          return []
        }
        if (typeof item.id !== 'number') {
          return [item]
        }

        const quantity = getAvailableQuantity(
          item.quantity,
          deckCards,
          item.id,
        )
        return quantity > 0 ? [{ ...item, quantity }] : []
      })
      const suggestions = await requestCommanderSuggestions(
        {
          requestId: crypto.randomUUID(),
          colors: coachBuildRefinement.colors,
          style: coachBuildRefinement.style,
          collectionCards: gameCollection.map((item) =>
            buildCoachCardInput(item, item.quantity),
          ),
        },
        accountSession?.token ?? '',
      )
      void loadCoachCredits(accountSession?.token)
      setCommanderSuggestions(suggestions)
      if (suggestions.length === 0) {
        setCoachError(
          'No legal owned commanders matched those colors and deck style.',
        )
      }
    } catch (error) {
      setCoachError(
        error instanceof Error
          ? error.message
          : 'Coach could not suggest a commander.',
      )
    } finally {
      setIsSuggestingCommander(false)
    }
  }

  async function loadCoachCredits(token = accountSession?.token) {
    if (!token) {
      setCoachCredits(null)
      return
    }
    try {
      setCoachCredits(await requestCoachCreditStatus(token))
    } catch {
      // Credit status is informational; Coach requests still enforce it server-side.
    }
  }

  function openPurchaseDialog() {
    if (!accountSession) {
      setAccountDialogOpen(true)
      setStatus('Sign in first so purchases can attach to your profile.')
      return
    }
    setPurchaseStatus('')
    setPurchaseDialogOpen(true)
  }

  async function beginCoachPurchase(product: CoachProduct) {
    if (!accountSession) {
      setAccountDialogOpen(true)
      setStatus('Sign in first so purchases can attach to your profile.')
      return
    }

    if (isDesktopShell && window.cardVaultDesktop?.purchaseCoachProduct) {
      setPurchaseStatus(`Opening Microsoft Store checkout for ${product.title}...`)
      const purchase = await window.cardVaultDesktop.purchaseCoachProduct(product.id)
      if (!purchase.ok) {
        setPurchaseStatus(purchase.error || 'Microsoft Store purchase was not completed.')
        return
      }

      try {
        const updatedCredits = await recordMicrosoftStoreCoachPurchase(
          accountSession.token,
          purchase,
        )
        setCoachCredits(updatedCredits)
        setPurchaseStatus(
          product.kind === 'subscription'
            ? 'Unlimited Coach is active on this account.'
            : `${product.title} added to this account.`,
        )
      } catch (error) {
        setPurchaseStatus(
          error instanceof Error
            ? error.message
            : 'Purchase finished, but credits could not be applied.',
        )
      }
      return
    }

    if (isAndroidShell && window.CardVaultAndroid?.purchaseCoachProduct) {
      setPurchaseStatus(`Opening Google Play checkout for ${product.title}...`)
      const purchase = await requestAndroidCoachPurchase(product.id)
      if (!purchase.ok) {
        setPurchaseStatus(purchase.error || 'Google Play purchase was not completed.')
        return
      }

      try {
        const updatedCredits = await recordGooglePlayCoachPurchase(
          accountSession.token,
          purchase,
        )
        setCoachCredits(updatedCredits)
        completeAndroidCoachPurchase(product, purchase)
        setPurchaseStatus(
          product.kind === 'subscription'
            ? 'Unlimited Coach is active on this account.'
            : `${product.title} added to this account.`,
        )
      } catch (error) {
        setPurchaseStatus(
          error instanceof Error
            ? error.message
            : 'Purchase finished, but credits could not be applied.',
        )
      }
      return
    }

    const platform = isDesktopShell
      ? 'Microsoft Store'
      : /Android/i.test(navigator.userAgent)
        ? 'Google Play'
        : 'the web checkout'
    setPurchaseStatus(
      `${product.title} is ready for ${platform} wiring. Next step is connecting receipt verification before taking payment.`,
    )
  }

  function canSpendCoachCredits(cost: number): boolean {
    if (coachCredits?.unlimited) {
      return true
    }
    if (coachCredits && coachCredits.balance < cost) {
      setCoachError(
        "You're out of Coach credits. Add credits or choose Unlimited Coach to keep using Coach.",
      )
      return false
    }
    return true
  }

  function askCoachForRefinedCombos(skipFilters = false) {
    if (!coachDraft || !coachComboRefinement) {
      return
    }

    const question = skipFilters
      ? coachComboRefinement.prompt.question
      : buildCoachComboQuestion(coachDraft.game, coachComboRefinement)

    setCoachQuestion('')
    updateCoachDraft({ mode: 'combos', goal: question })
    void runCoach({ mode: 'combos', question })
  }

  function askCoachForRefinedBuild(skipFilters = false) {
    const draft = coachDraftRef.current
    if (!draft || !coachBuildRefinement) {
      return
    }

    if (
      draft.game === 'mtg' &&
      draft.format === 'commander' &&
      !draft.commanderName.trim()
    ) {
      setCoachError('Choose your commander before building a Commander deck.')
      return
    }

    const question = skipFilters
      ? coachBuildRefinement.prompt.question
      : buildCoachDeckQuestion(draft.game, coachBuildRefinement)

    setCoachBuildRefinement(null)
    setCoachQuestion('')
    updateCoachDraft({ mode: 'build', goal: question })
    void runCoach({ mode: 'build', question })
  }

  async function runCoach(
    options: { mode?: CoachMode; question?: string } = {},
  ) {
    const draft = coachDraftRef.current
    if (!draft || isCoachLoading) {
      return
    }
    const question = (options.question ?? coachQuestion).trim()
    const mode = options.mode ?? draft.mode
    if (!question) {
      setCoachError('Ask Coach a question first.')
      return
    }
    const coachCost = coachCreditCosts[mode] ?? coachCreditCosts.ask
    if (!canSpendCoachCredits(coachCost)) {
      return
    }
    if (
      mode === 'build' &&
      draft.game === 'mtg' &&
      draft.format === 'commander' &&
      !draft.commanderName.trim()
    ) {
      setCoachError('Choose your commander before building a Commander deck.')
      return
    }

    const deck =
      draft.scope === 'deck'
        ? decks.find((item) => item.id === draft.deckId)
        : null

    if (draft.scope === 'deck' && !deck) {
      setCoachError('That deck is no longer available.')
      return
    }

    const entries = deckCards
      .filter(
        (entry) =>
          draft.scope === 'deck' &&
          entry.deckId === draft.deckId,
      )
      .map((entry) => ({
        entry,
        item: collection.find((item) => item.id === entry.collectionItemId),
      }))
      .filter(
        (
          value,
        ): value is {
          entry: DeckCard
          item: CollectionItem
        } => Boolean(value.item),
      )
    const gameCollection = collection.filter((item) => item.game === draft.game)
    const selectedCommanderKey =
      mode === 'build' && draft.game === 'mtg' && draft.format === 'commander'
        ? normalizeCardName(draft.commanderName)
        : ''
    if (selectedCommanderKey) {
      const commanderItems = gameCollection.filter(
        (item) => normalizeCardName(item.name) === selectedCommanderKey,
      )

      if (!commanderItems.length) {
        setCoachError('That commander is not saved in your MTG collection.')
        return
      }

      const availableCommanderQuantity = commanderItems.reduce((total, item) => {
        if (typeof item.id !== 'number') {
          return total + item.quantity
        }
        return (
          total +
          getAvailableQuantity(
            item.quantity,
            deckCards,
            item.id,
          )
        )
      }, 0)

      if (availableCommanderQuantity < 1) {
        const assignedDeckNames = uniqueStrings(
          commanderItems.flatMap((item) => {
            if (typeof item.id !== 'number') {
              return []
            }
            return deckCards
              .filter(
                (entry) =>
                  entry.collectionItemId === item.id &&
                  entry.quantity > 0,
              )
              .map(
                (entry) =>
                  decks.find((deck) => deck.id === entry.deckId)?.name ?? 'another deck',
              )
          }),
        )
        const assignedText = assignedDeckNames.length
          ? ` It is currently assigned to ${assignedDeckNames.join(', ')}.`
          : ''
        setCoachError(
          `That commander is already assigned to another deck.${assignedText} Remove it from that deck or delete the deck before Coach can build with it.`,
        )
        return
      }
    }
    const coachCollection = gameCollection.flatMap((item) => {
      if (mode !== 'build' || typeof item.id !== 'number') {
        return [item]
      }

      const quantity = getAvailableQuantity(
        item.quantity,
        deckCards,
        item.id,
      )
      return quantity > 0 ? [{ ...item, quantity }] : []
    })

    setIsCoachDeckBuildRunning(isFullDeckBuildRequest(question, mode))
    setIsCoachLoading(true)
    setCoachError('')
    setCoachResult(null)
    setIsCoachCreditListOpen(false)
    const userMessage: CoachChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: question,
    }
    const conversation = capCoachMessages(coachMessages).map((message) => ({
      role: message.role,
      content: getCoachMessageContext(message),
    }))
    setCoachMessages((current) => capCoachMessages([...current, userMessage]))
    setCoachQuestion('')

    try {
      if (deck && typeof deck.id === 'number') {
        await db.decks.update(deck.id, {
          format: draft.format,
          commanderName:
            draft.format === 'commander'
              ? draft.commanderName.trim()
              : '',
          updatedAt: new Date().toISOString(),
        })
      }

      const result = await requestCoach(
        {
          requestId: crypto.randomUUID(),
          game: draft.game,
          scope: draft.scope,
          mode,
          deckName: deck?.name ?? `${getGameLabel(draft.game)} Library`,
          format: draft.format,
          commanderName:
            draft.format === 'commander'
              ? draft.commanderName.trim()
              : '',
          goal: question,
          conversation,
          deckCards: entries.map(({ entry, item }) =>
            buildCoachCardInput(item, entry.quantity),
          ),
          collectionCards: coachCollection.map((item) =>
            buildCoachCardInput(item, item.quantity),
          ),
        },
        accountSession?.token ?? '',
      )

      if (result.credits) {
        setCoachCredits(result.credits)
      }
      setCoachResult(result)
      setCoachMessages((current) =>
        capCoachMessages([
          ...current,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            text: result.summary,
            result,
          },
        ]),
      )
      updateCoachDraft({ mode: 'ask', goal: '' })
      setCoachComboRefinement(null)
      setCoachBuildRefinement(null)
      if (draft.scope === 'deck') {
        await loadDeckData()
      }
    } catch (error) {
      void loadCoachCredits()
      setCoachError(
        error instanceof DOMException && error.name === 'AbortError'
          ? 'Coach took too long to answer. Deterministic checks remain available after retry.'
          : error instanceof Error
            ? error.message
            : `Coach could not analyze this ${draft.scope}.`,
      )
    } finally {
      setIsCoachDeckBuildRunning(false)
      setIsCoachLoading(false)
    }
  }

  async function applyCoachRecommendation(recommendation: CoachRecommendation) {
    if (!coachDraft) {
      return
    }

    const deckId = coachDraft.deckId
    if (coachDraft.scope !== 'deck' || typeof deckId !== 'number') {
      return
    }

    for (const change of recommendation.remove) {
      let remaining = change.quantity
      const currentEntries = await db.deckCards
        .where('deckId')
        .equals(deckId)
        .toArray()

      for (const entry of currentEntries) {
        if (remaining <= 0) {
          break
        }
        const item = await db.collection.get(entry.collectionItemId)
        if (!item || normalizeCardName(item.name) !== normalizeCardName(change.name)) {
          continue
        }

        const removed = Math.min(entry.quantity, remaining)
        if (removed === entry.quantity && typeof entry.id === 'number') {
          await db.deckCards.delete(entry.id)
        } else if (typeof entry.id === 'number') {
          await db.deckCards.update(entry.id, {
            quantity: entry.quantity - removed,
          })
        }
        remaining -= removed
      }
    }

    for (const change of recommendation.add) {
      let remaining = change.quantity
      const ownedItems = await db.collection
        .filter(
          (item) =>
            item.game === coachDraft.game &&
            normalizeCardName(item.name) === normalizeCardName(change.name),
        )
        .toArray()

      for (const item of ownedItems) {
        if (remaining <= 0 || typeof item.id !== 'number') {
          break
        }
        const allDeckCards = await db.deckCards.toArray()
        const available = getAvailableQuantity(
          item.quantity,
          allDeckCards,
          item.id,
        )
        const added = Math.min(available, remaining)

        if (!added) {
          continue
        }
        await addCollectionItemToDeck(
          deckId,
          item.id,
          added,
          new Date().toISOString(),
        )
        remaining -= added
      }
    }

    await db.decks.update(deckId, { updatedAt: new Date().toISOString() })
    await loadDeckData()
    setSaveNotice(`Applied Coach changes: ${recommendation.title}`)
    setStatus('Coach changes applied to deck')
  }

  async function saveCoachGeneratedDeck(message: CoachChatMessage) {
    if (
      !coachDraft ||
      !message.result ||
      message.result.mode !== 'build' ||
      savingCoachDeckMessageId
    ) {
      return
    }

    const requestedByName = new Map<string, { name: string; quantity: number }>()
    for (const recommendation of message.result.ownedRecommendations) {
      for (const change of recommendation.add) {
        const key = normalizeCardName(change.name)
        const existing = requestedByName.get(key)
        requestedByName.set(key, {
          name: existing?.name ?? change.name,
          quantity: (existing?.quantity ?? 0) + change.quantity,
        })
      }
    }

    const requestedCards = Array.from(requestedByName.values())
    if (!requestedCards.length) {
      setCoachError('Coach did not provide any owned cards to save.')
      return
    }

    setSavingCoachDeckMessageId(message.id)
    setCoachError('')
    const now = new Date().toISOString()
    const primaryCardName = requestedCards[0]?.name.trim()
    const baseName = primaryCardName ? `${primaryCardName} Deck` : 'New Deck'
    const existingNames = new Set(decks.map((deck) => normalizeCardName(deck.name)))
    let deckName = baseName
    let suffix = 2
    while (existingNames.has(normalizeCardName(deckName))) {
      deckName = `${baseName} ${suffix}`
      suffix += 1
    }

    try {
      let deckId = 0
      let savedCards = 0
      await db.transaction('rw', db.collection, db.decks, db.deckCards, async () => {
        const currentDeckCards = await db.deckCards.toArray()
        const unavailableRequests: string[] = []

        for (const requested of requestedCards) {
          const ownedItems = await db.collection
            .filter(
              (item) =>
                item.game === coachDraft.game &&
                normalizeCardName(item.name) === normalizeCardName(requested.name),
            )
            .toArray()
          const available = ownedItems.reduce((total, item) => {
            if (typeof item.id !== 'number') {
              return total
            }
            return (
              total +
              getAvailableQuantity(
                item.quantity,
                currentDeckCards,
                item.id,
              )
            )
          }, 0)

          if (available < requested.quantity) {
            unavailableRequests.push(
              `${requested.name} needs ${requested.quantity}, available ${available}`,
            )
          }
        }

        if (unavailableRequests.length) {
          throw new Error(
            `Generated deck is stale because some cards are no longer available: ${unavailableRequests
              .slice(0, 4)
              .join('; ')}.`,
          )
        }

        deckId = await db.decks.add({
          game: coachDraft.game,
          name: deckName,
          format: coachDraft.format,
          commanderName:
            coachDraft.format === 'commander'
              ? coachDraft.commanderName.trim()
              : '',
          createdAt: now,
          updatedAt: now,
        })

        for (const requested of requestedCards) {
          let remaining = requested.quantity
          const ownedItems = await db.collection
            .filter(
              (item) =>
                item.game === coachDraft.game &&
                normalizeCardName(item.name) === normalizeCardName(requested.name),
            )
            .toArray()

          for (const item of ownedItems) {
            if (remaining <= 0 || typeof item.id !== 'number') {
              break
            }
            const available = getAvailableQuantity(
              item.quantity,
              await db.deckCards.toArray(),
              item.id,
            )
            const quantity = Math.min(available, remaining)
            if (quantity < 1) {
              continue
            }
            await db.deckCards.add({
              deckId,
              collectionItemId: item.id,
              quantity,
              addedAt: now,
            })
            remaining -= quantity
            savedCards += quantity
          }
        }

        if (!savedCards) {
          await db.decks.delete(deckId)
          deckId = 0
        }
      })

      if (!deckId || !savedCards) {
        throw new Error('None of the generated cards are still available to save.')
      }

      await loadDeckData()
      setSavedCoachDeckNames((current) => ({ ...current, [message.id]: deckName }))
      setSaveNotice(`${deckName} saved with ${savedCards} cards.`)
      setStatus(`Saved ${deckName}`)
    } catch (error) {
      setCoachError(
        error instanceof Error ? error.message : 'Could not save the generated deck.',
      )
    } finally {
      setSavingCoachDeckMessageId(null)
    }
  }

  async function removeItem(id: number | undefined) {
    if (typeof id !== 'number') {
      return
    }

    await db.collection.delete(id)
    await loadCollection()
    setStatus('Card removed')
  }

  async function moveItemToList(
    item: CollectionItem,
    listKind: CollectionListKind,
  ) {
    if (typeof item.id !== 'number') {
      return
    }
    await db.collection.update(item.id, {
      listKind,
      updatedAt: new Date().toISOString(),
    })
    setOpenCollectionItemMenu(null)
    await loadCollection()
    setStatus(
      listKind === 'wishlist'
        ? `${item.name} moved to wishlist`
        : `${item.name} moved to collection`,
    )
  }

  async function adjustCollectionItemQuantity(
    itemId: number | undefined,
    change: number,
  ) {
    if (typeof itemId !== 'number' || !change) {
      return
    }

    let updatedQuantity: number | null = null

    await db.transaction('rw', db.collection, async () => {
      const item = await db.collection.get(itemId)

      if (!item) {
        return
      }

      updatedQuantity = Math.min(999, Math.max(1, item.quantity + change))

      if (updatedQuantity === item.quantity) {
        return
      }

      await db.collection.update(itemId, {
        quantity: updatedQuantity,
        updatedAt: new Date().toISOString(),
      })
    })

    if (updatedQuantity === null) {
      return
    }

    await loadCollection()
    setStatus(`Collection quantity updated to ${updatedQuantity}`)
  }

  function openEditItem(item: CollectionItem) {
    if (typeof item.id !== 'number') {
      return
    }

    setEditDraft({
      addedAt: item.addedAt,
      category: item.category,
      collectorNumber: item.collectorNumber,
      colors: item.colors.join(', '),
      condition: item.condition,
      game: item.game,
      id: item.id,
      imageUrl: item.imageUrl,
      lastPriceCheck: item.lastPriceCheck,
      name: item.name,
      notes: item.notes,
      priceLabel: item.priceLabel,
      priceUsd: item.priceUsd === null ? '' : String(item.priceUsd),
      quantity: String(item.quantity),
      rarity: item.rarity,
      setCode: item.setCode,
      setName: item.setName,
      sourceId: item.sourceId,
      sourceUrl: item.sourceUrl,
      updatedAt: item.updatedAt,
      listKind: item.listKind ?? 'collection',
      sport: item.sport ?? '',
      manufacturer: item.manufacturer ?? '',
      releaseName: item.releaseName ?? '',
      releaseYear: item.releaseYear ?? '',
      parallelId: item.parallelId ?? '',
      parallelName: item.parallelName ?? '',
      numberedTo: item.numberedTo === undefined ? '' : String(item.numberedTo),
      gradingCompanyId: item.gradingCompanyId ?? '',
      gradingCompany: item.gradingCompany ?? '',
      gradeId: item.gradeId ?? '',
      grade: item.grade ?? '',
      certificationNumber: item.certificationNumber ?? '',
      gradeQualifier: item.gradeQualifier ?? '',
      autographGrade: item.autographGrade ?? '',
    })
  }

  function updateEditDraft<Key extends keyof EditCollectionDraft>(
    key: Key,
    value: EditCollectionDraft[Key],
  ) {
    setEditDraft((current) => (current ? { ...current, [key]: value } : current))
  }

  async function saveEditDraft() {
    if (!editDraft) {
      return
    }

    const quantityValue = Math.max(1, Math.floor(Number(editDraft.quantity) || 1))
    const priceText = editDraft.priceUsd.trim()
    const priceValue = priceText ? Number.parseFloat(priceText) : null
    const priceUsd = priceValue !== null && Number.isFinite(priceValue) ? priceValue : null
    const numberedToValue = Number.parseInt(editDraft.numberedTo, 10)

    await db.collection.update(editDraft.id, {
      addedAt: editDraft.addedAt.trim(),
      category: editDraft.category.trim() || 'Other',
      collectorNumber: editDraft.collectorNumber.trim(),
      colors: editDraft.colors
        .split(',')
        .map((color) => color.trim())
        .filter(Boolean),
      condition: editDraft.condition,
      game: editDraft.game,
      imageUrl: editDraft.imageUrl.trim(),
      lastPriceCheck: editDraft.lastPriceCheck.trim(),
      name: editDraft.name.trim() || 'Untitled Card',
      notes: editDraft.notes.trim(),
      priceLabel: editDraft.priceLabel.trim() || (priceUsd === null ? 'No price' : `$${priceUsd.toFixed(2)}`),
      priceUsd,
      quantity: quantityValue,
      rarity: editDraft.rarity.trim() || 'Unknown',
      setCode: editDraft.setCode.trim(),
      setName: editDraft.setName.trim() || 'Unknown Set',
      sourceId: editDraft.sourceId.trim(),
      sourceUrl: editDraft.sourceUrl.trim(),
      updatedAt: editDraft.updatedAt.trim(),
      listKind: editDraft.listKind,
      sport: editDraft.sport.trim(),
      manufacturer: editDraft.manufacturer.trim(),
      releaseName: editDraft.releaseName.trim(),
      releaseYear: editDraft.releaseYear.trim(),
      parallelId: editDraft.parallelId.trim(),
      parallelName: editDraft.parallelName.trim(),
      numberedTo: Number.isFinite(numberedToValue) ? numberedToValue : undefined,
      gradingCompanyId: editDraft.gradingCompanyId.trim(),
      gradingCompany: editDraft.gradingCompany.trim(),
      gradeId: editDraft.gradeId.trim(),
      grade: editDraft.grade.trim(),
      certificationNumber: editDraft.certificationNumber.trim(),
      gradeQualifier: editDraft.gradeQualifier.trim(),
      autographGrade: editDraft.autographGrade.trim(),
    })

    setFailedImages(new Set())
    setEditDraft(null)
    await loadCollection()
    setStatus('Card updated')
  }

  async function refreshOne(item: CollectionItem) {
    if (typeof item.id !== 'number') {
      return
    }

    if (item.game === 'sports') {
      setStatus('Sold-price lookup is not available for this card yet. You can enter a value in Edit Card.')
      return
    }

    setRefreshingId(item.id)

    try {
      await db.collection.update(item.id, await refreshCandidatePrice(item))
      await loadCollection()
      setStatus(`Updated ${item.name}`)
    } catch {
      setStatus(`Could not update ${item.name}`)
    } finally {
      setRefreshingId(null)
    }
  }

  async function refreshGame(sectionGame: GameFilter) {
    if (sectionGame === 'sports') {
      await runSportsPriceBatch()
      return
    }
    const gameItems = collection.filter((item) => item.game === sectionGame)

    setRefreshingId('all')

    try {
      for (const item of gameItems) {
        if (typeof item.id === 'number') {
          await db.collection.update(item.id, await refreshCandidatePrice(item))
        }
      }

      await loadCollection()
      setStatus(`${getGameLabel(sectionGame)} prices updated`)
    } catch {
      setStatus('Some prices could not be updated.')
    } finally {
      setRefreshingId(null)
    }
  }

  async function runSportsPriceBatch() {
    if (!sportsPriceQueue.length) {
      setStatus('No sports cards are waiting for pricing.')
      return
    }
    if (!accountSession) {
      setStatus('Sign in before sending the sports price batch.')
      setAccountDialogOpen(true)
      return
    }

    const isFullBatch = sportsPriceQueue.length >= sportsPriceBatchTarget
    const batch = sportsPriceQueue.slice(0, sportsPriceBatchTarget)
    const mode: SportsPricingMode = isFullBatch ? 'batch' : 'manual'
    setIsPricingSports(true)
    setRefreshingId('all')
    setStatus(
      isFullBatch
        ? `Requesting completed-sale pricing for ${sportsPriceBatchTarget} sports cards`
        : `Requesting pricing for the ${batch.length} cards currently waiting`,
    )
    try {
      const updates = await fetchSportsPriceUpdates(batch, accountSession.token, mode)
      await applySportsPriceUpdates(updates)
      await refreshSportsPricingState(accountSession.token)
      const pricedCount = Array.from(updates.values()).filter(
        (update) => update.sportsPriceStatus === 'priced',
      ).length
      setStatus(
        `Sports price batch complete: ${pricedCount} of ${batch.length} cards had matching sold comps.`,
      )
    } catch (error) {
      setStatus(
        error instanceof Error ? error.message : 'Sports price batch failed.',
      )
    } finally {
      setIsPricingSports(false)
      setRefreshingId(null)
    }
  }

  async function applySportsPriceUpdates(
    updates: Awaited<ReturnType<typeof fetchSportsPriceUpdates>>,
  ) {
    const sourceIds = new Set(updates.keys())
    const storedItems = await db.collection.toArray()
    await db.transaction('rw', db.collection, async () => {
      for (const item of storedItems) {
        if (
          typeof item.id === 'number' &&
          sourceIds.has(item.sourceId) &&
          updates.has(item.sourceId)
        ) {
          await db.collection.update(item.id, updates.get(item.sourceId) as object)
        }
      }
    })
    await loadCollection()
  }

  function openScanIssueReport() {
    if (!capturedFrame) {
      setStatus('Scan a card first so the report can include the captured image.')
      return
    }

    setScanIssueDraft({
      correctName: query.trim(),
      notes: '',
      wrongCandidateSourceId: candidates[0]?.sourceId ?? '',
      isSaving: false,
    })
  }

  function updateScanIssueDraft(values: Partial<ScanIssueDraft>) {
    setScanIssueDraft((current) => (current ? { ...current, ...values } : current))
  }

  async function saveScanIssueReport() {
    if (!scanIssueDraft || !capturedFrame) {
      return
    }

    const correctName = scanIssueDraft.correctName.trim()

    if (!correctName) {
      setStatus('Type the correct card name before saving the report.')
      return
    }

    const wrongCandidate =
      candidates.find(
        (candidate) => candidate.sourceId === scanIssueDraft.wrongCandidateSourceId,
      ) ?? null
    const notes = scanIssueDraft.notes.trim()
    const reportedWrongCandidate =
      wrongCandidate &&
      normalizeCardName(wrongCandidate.name) === normalizeCardName(correctName) &&
      !notes
        ? null
        : wrongCandidate

    const report: ScanIssueReport = {
      scannerVersion,
      game,
      imageDataUrl: capturedFrame,
      ocrRawText: scanResult?.rawText ?? '',
      ocrTerms: scanResult?.terms ?? [],
      selectedTerm,
      searchQuery: query.trim(),
      correctName,
      notes,
      wrongCandidateName: reportedWrongCandidate?.name ?? '',
      wrongCandidateSourceId: reportedWrongCandidate?.sourceId ?? '',
      candidateNames: candidates.slice(0, 12).map((candidate) => candidate.name),
      createdAt: new Date().toISOString(),
    }

    updateScanIssueDraft({ isSaving: true })

    let localReportId: number

    try {
      localReportId = await db.scanIssueReports.add(report)
    } catch {
      updateScanIssueDraft({ isSaving: false })
      setStatus('Could not save the scan issue. Try again.')
      return
    }

    setScanIssueDraft(null)
    setStatus(`Scan issue saved locally for ${correctName}. Sending in background.`)
    setSaveNotice(`Scan issue saved: ${correctName}`)

    void submitScanIssueReport(
      { ...report, id: localReportId },
      accountSession?.token ?? '',
    )
      .then(async (syncResult) => {
        await db.scanIssueReports.update(localReportId, {
          syncedAt: new Date().toISOString(),
          serverReportId: syncResult.reportId ?? '',
          syncError: '',
        })
        setStatus(`Scan issue sent for ${correctName}`)
        setSaveNotice(`Scan issue sent: ${correctName}`)
      })
      .catch(async (error: unknown) => {
        await db.scanIssueReports.update(localReportId, {
          syncError:
            error instanceof Error
              ? error.message
              : 'Could not send to Fire Line server.',
        })
        setStatus(`Scan issue saved locally for ${correctName}. It will sync later.`)
      })
  }

  async function syncStoredScanIssueReports(accountToken: string) {
    const unsyncedReports = await db.scanIssueReports
      .filter((report) => !report.syncedAt)
      .toArray()

    let syncedCount = 0

    for (const report of unsyncedReports) {
      if (typeof report.id !== 'number') {
        continue
      }

      try {
        const syncResult = await submitScanIssueReport(report, accountToken)
        await db.scanIssueReports.update(report.id, {
          syncedAt: new Date().toISOString(),
          serverReportId: syncResult.reportId ?? '',
          syncError: '',
        })
        syncedCount += 1
      } catch (error) {
        await db.scanIssueReports.update(report.id, {
          syncError: error instanceof Error ? error.message : 'Sync failed.',
        })
      }
    }

    if (syncedCount) {
      setStatus(
        `${syncedCount} saved scan issue report${syncedCount === 1 ? '' : 's'} sent.`,
      )
    }
  }

  function dismissFeedbackNudge() {
    window.localStorage.setItem('card-vault-feedback-nudge-dismissed', '1')
    setFeedbackDraft((current) => ({ ...current, dismissed: true }))
  }

  async function sendFeedbackNote(choice = feedbackDraft.choice) {
    const note = feedbackDraft.note.trim()
    if (!note) {
      setFeedbackDraft((current) => ({
        ...current,
        error: 'Enter a feedback note before sending.',
      }))
      setStatus('Enter a feedback note before sending.')
      return
    }

    const nextChoice = choice || 'Other'
    const kind = feedbackKindForChoice(nextChoice)
    setFeedbackDraft((current) => ({
      ...current,
      choice: nextChoice,
      error: '',
      isSaving: true,
    }))

    try {
      if (isDesktopShell && window.cardVaultDesktop?.sendFeedback) {
        const desktopVersion = window.cardVaultDesktop.version || appVersionLabel
        const payload = await window.cardVaultDesktop.sendFeedback({
          body: [
            note,
            '',
            `Category: ${nextChoice}`,
            `Version: ${desktopVersion}`,
            `Tab: ${activeTab}`,
            `Game: ${game}`,
          ].join('\n'),
          email: feedbackDraft.email,
          kind,
        })
        if (!payload.ok) {
          throw new Error(payload.error || 'Could not save feedback.')
        }
      } else {
        if (!accountSession) {
          setAccountDialogOpen(true)
          setStatus('Sign in first so feedback can be saved to your profile.')
          setFeedbackDraft((current) => ({ ...current, isSaving: false }))
          return
        }

        const response = await fetch(getApiUrl('/api/feedback'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accountSession.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            version: appVersionLabel,
            kind,
            body: [
              'Early access feedback:',
              `Category: ${nextChoice}`,
              `Feedback: ${note}`,
              `Tab: ${activeTab}`,
              `Game: ${game}`,
              `Search: ${query.trim() || selectedTerm || 'none'}`,
              `Profile: ${accountSession.profile.displayName}`,
              `Phone/browser: ${navigator.userAgent}`,
            ].filter(Boolean).join('\n'),
            context: {
              activeTab,
              game,
              hasScanResult: Boolean(scanResult),
              collectionCount: collection.length,
              deckCount: decks.length,
              ...(feedbackDraft.email.trim()
                ? { reply_email: feedbackDraft.email.trim() }
                : {}),
            },
          }),
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok || !payload.ok) {
          throw new Error(payload.error || 'Could not save feedback.')
        }
      }
      dismissFeedbackNudge()
      setFeedbackDialogOpen(false)
      setFeedbackDraft({
        note: '',
        choice: '',
        email: '',
        error: '',
        dismissed: true,
        isSaving: false,
      })
      setStatus('Feedback saved. Thank you.')
    } catch (error) {
      setFeedbackDraft((current) => ({ ...current, isSaving: false }))
      setStatus(error instanceof Error ? error.message : 'Feedback could not be saved.')
    }
  }

  function exportGameCollection(sectionGame: GameFilter) {
    const gameItems = collection.filter(
      (item) =>
        item.game === sectionGame &&
        (item.listKind ?? 'collection') === activeListKind,
    )
    const blob = new Blob([JSON.stringify(gameItems, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = `card-vault-${sectionGame}-${activeListKind}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  function markImageFailed(card: CardCandidate) {
    const key = getImageKey(card)
    setFailedImages((current) => new Set(current).add(key))
  }

  function renderCardImage(card: CardCandidate) {
    const key = getImageKey(card)

    if (!card.imageUrl || failedImages.has(key)) {
      return (
        <div className="card-thumb missing" aria-hidden="true" />
      )
    }

    return (
      <img
        alt=""
        className="card-thumb"
        onError={() => markImageFailed(card)}
        src={card.imageUrl}
      />
    )
  }

  function renderGameTab(sectionGame: GameFilter) {
    const items = collection.filter(
      (item) =>
        item.game === sectionGame &&
        (item.listKind ?? 'collection') === activeListKind,
    )
    const collectionQuery = collectionQueries[sectionGame]
    const visibleItems = items.filter((item) =>
      matchesCollectionSearch(item, collectionQuery),
    )
    const groups = groupCollection(visibleItems, collectionGroup, collectionSort)
    const quantity = items.reduce((total, item) => total + item.quantity, 0)
    const value = items.reduce(
      (total, item) => total + (item.priceUsd ?? 0) * item.quantity,
      0,
    )
    const gameLabel = getGameLabel(sectionGame)
    const getGroupKey = (label: string) => `${sectionGame}-${collectionGroup}-${label}`
    const isGroupOpen = (label: string) => collapsedGroups[getGroupKey(label)] === false
    const deckById = new Map(
      decks
        .filter((deck) => deck.game === sectionGame && typeof deck.id === 'number')
        .map((deck) => [deck.id as number, deck]),
    )
    const deckUsageByItemId = new Map<
      number,
      Array<{ deckId: number; deckName: string; quantity: number }>
    >()

    for (const entry of deckCards) {
      const deck = deckById.get(entry.deckId)

      if (!deck || entry.quantity < 1) {
        continue
      }

      const usages = deckUsageByItemId.get(entry.collectionItemId) ?? []
      usages.push({
        deckId: entry.deckId,
        deckName: deck.name,
        quantity: entry.quantity,
      })
      deckUsageByItemId.set(entry.collectionItemId, usages)
    }

    const renderCollectionItems = (groupItems: CollectionItem[]) => (
      <div className="collection-list">
        {groupItems.map((item) => {
          const deckUsage =
            typeof item.id === 'number' ? deckUsageByItemId.get(item.id) ?? [] : []
          const deckQuantity = deckUsage.reduce(
            (total, usage) => total + usage.quantity,
            0,
          )
          const deckUsageLabel =
            deckUsage.length === 1
              ? `Deck: ${deckUsage[0].deckName}`
              : `Decks: ${deckUsage[0]?.deckName} +${deckUsage.length - 1}`
          const itemMenuKey = `${item.game}-${item.id ?? item.sourceId}`

          return (
            <article className="collection-item" key={item.id}>
              <button
                aria-label={`Preview ${item.name}`}
                className="collection-preview-trigger"
                onClick={() => setPreviewCard(item)}
                title={`Preview ${item.name}`}
                type="button"
              >
                {renderCardImage(item)}
                <span className="item-main">
                  <h3>{item.name}</h3>
                  <span className="item-meta">
                    {item.game === 'sports' ? (
                      <>
                        {[item.releaseYear, item.manufacturer, item.setName]
                          .filter(Boolean)
                          .join(' · ')}{' '}
                        #{item.collectorNumber}
                        {item.parallelName ? ` · ${item.parallelName}` : ''}
                        {item.gradingCompany
                          ? ` · ${item.gradingCompany} ${item.grade}`
                          : ` · ${item.condition}`}
                      </>
                    ) : (
                      <>
                        {item.setCode} #{item.collectorNumber} · {item.condition}
                      </>
                    )}
                  </span>
                  {item.notes && <span className="item-notes">{item.notes}</span>}
                  <span className="muted">
                    {item.game === 'sports' && item.sportsPriceStatus === 'pending'
                      ? 'Waiting for the 100-card price batch'
                      : `Checked ${formatDate(item.lastPriceCheck)}`}
                  </span>
                </span>
              </button>
              <div className="item-price">
                <strong>{item.priceLabel}</strong>
                <span>{formatMoney((item.priceUsd ?? 0) * item.quantity)}</span>
                {deckQuantity > 0 && (
                  <details className="deck-usage">
                    <summary
                      aria-label={`${deckQuantity} ${item.name} ${
                        deckQuantity === 1 ? 'copy' : 'copies'
                      } assigned to decks`}
                    >
                      <FolderKanban aria-hidden="true" size={15} />
                      <span className="deck-usage-label">{deckUsageLabel}</span>
                    </summary>
                    <div className="deck-usage-popover">
                      {deckUsage.map((usage) => (
                        <div key={usage.deckId}>
                          <span>{usage.deckName}</span>
                          <strong>×{usage.quantity}</strong>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                <div
                  aria-label={`Collection quantity for ${item.name}`}
                  className="collection-quantity-stepper"
                >
                  <button
                    aria-label={`Decrease ${item.name} quantity`}
                    disabled={item.quantity <= 1}
                    onClick={() => void adjustCollectionItemQuantity(item.id, -1)}
                    title="Decrease quantity"
                    type="button"
                  >
                    <Minus size={17} />
                  </button>
                  <output aria-live="polite">{item.quantity}</output>
                  <button
                    aria-label={`Increase ${item.name} quantity`}
                    disabled={item.quantity >= 999}
                    onClick={() => void adjustCollectionItemQuantity(item.id, 1)}
                    title="Increase quantity"
                    type="button"
                  >
                    <Plus size={17} />
                  </button>
                </div>
                <div className="collection-item-menu">
                  <button
                    aria-expanded={openCollectionItemMenu === itemMenuKey}
                    aria-haspopup="menu"
                    aria-label={`${item.name} actions`}
                    onClick={() =>
                      setOpenCollectionItemMenu((current) =>
                        current === itemMenuKey ? null : itemMenuKey,
                      )
                    }
                    title="Card actions"
                    type="button"
                  >
                    <MoreVertical size={18} />
                  </button>
                  {openCollectionItemMenu === itemMenuKey && (
                    <div className="collection-item-menu-popover" role="menu">
                      <button
                        onClick={() => {
                          setOpenCollectionItemMenu(null)
                          openEditItem(item)
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <Pencil size={17} />
                        Edit
                      </button>
                      {item.sourceUrl && (
                        <a
                          href={item.sourceUrl}
                          onClick={() => setOpenCollectionItemMenu(null)}
                          rel="noreferrer"
                          role="menuitem"
                          target="_blank"
                        >
                          <ExternalLink size={17} />
                          {item.game === 'sports' && item.sourceUrl.includes('ebay.com')
                            ? 'Compare on eBay'
                            : 'Open Webpage'}
                        </a>
                      )}
                      <button
                        disabled={refreshingId === item.id}
                        onClick={() => {
                          setOpenCollectionItemMenu(null)
                          void refreshOne(item)
                        }}
                        role="menuitem"
                        type="button"
                      >
                        {refreshingId === item.id ? (
                          <Loader2 className="spin" size={17} />
                        ) : (
                          <RefreshCw size={17} />
                        )}
                        Refresh Price
                      </button>
                      <button
                        onClick={() =>
                          void moveItemToList(
                            item,
                            activeListKind === 'collection' ? 'wishlist' : 'collection',
                          )
                        }
                        role="menuitem"
                        type="button"
                      >
                        <Heart size={17} />
                        {activeListKind === 'collection'
                          ? 'Move to Wishlist'
                          : 'Move to Collection'}
                      </button>
                      <button
                        onClick={() => {
                          setOpenCollectionItemMenu(null)
                          void removeItem(item.id)
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <Trash2 size={17} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </article>
          )
        })}
      </div>
    )

    return (
      <section className="workspace game-workspace">
        <section className="collection-panel">
          <div className="panel-heading collection-heading">
            <div>
              <h2>{gameLabel}</h2>
              <div className="collection-summary">
                <span className="muted">
                  {items.length} saved entries
                </span>
                <span className="collection-card-count">
                  {quantity} cards - {formatMoney(value)}
                </span>
              </div>
            </div>
            <div className="collection-heading-side">
              {sectionGame !== 'sports' && activeListKind === 'collection' && (
                <button
                  className="collection-deck-button"
                  onClick={() => {
                    setDeckBuilderGame(sectionGame)
                    setSelectedDeckId(null)
                    setIsRenamingDeck(false)
                    setNewDeckNameDraft(null)
                    setOpenGameMenu(null)
                  }}
                  type="button"
                >
                  <FolderKanban size={17} />
                  Deck Builder
                </button>
              )}
              <div className="collection-heading-actions">
                <label className="collection-search">
                  <Search aria-hidden="true" size={17} />
                  <input
                    aria-label={`Search ${gameLabel} collection`}
                    onChange={(event) =>
                      setCollectionQueries((current) => ({
                        ...current,
                        [sectionGame]: event.target.value,
                      }))
                    }
                    onPointerDown={() => {
                      if (collectionQuery) {
                        setCollectionQueries((current) => ({
                          ...current,
                          [sectionGame]: '',
                        }))
                      }
                    }}
                    placeholder="Search cards"
                    type="search"
                    value={collectionQuery}
                  />
                </label>
                {sectionGame !== 'sports' && activeListKind === 'collection' && (
                  <button
                    className="collection-coach-button"
                    disabled={!items.length}
                    onClick={() => openLibraryCoach(sectionGame)}
                    type="button"
                  >
                    <Sparkles size={17} />
                    Coach
                  </button>
                )}
                <div className="game-menu">
                  <button
                    aria-expanded={openGameMenu === sectionGame}
                    aria-haspopup="menu"
                    aria-label={`${gameLabel} actions`}
                    onClick={() =>
                      setOpenGameMenu((current) =>
                        current === sectionGame ? null : sectionGame,
                      )
                    }
                    title={`${gameLabel} actions`}
                    type="button"
                  >
                    <MoreVertical size={18} />
                  </button>
                  {openGameMenu === sectionGame && (
                    <div className="game-menu-popover" role="menu">
                      <button
                        disabled={!items.length || refreshingId === 'all'}
                        onClick={() => {
                          setOpenGameMenu(null)
                          void refreshGame(sectionGame)
                        }}
                        role="menuitem"
                        type="button"
                      >
                        {refreshingId === 'all' ? (
                          <Loader2 className="spin" size={17} />
                        ) : (
                          <RefreshCw size={17} />
                        )}
                        Refresh Prices
                      </button>
                      <button
                        disabled={!items.length}
                        onClick={() => {
                          setOpenGameMenu(null)
                          exportGameCollection(sectionGame)
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <Download size={17} />
                        Save / Export
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="collection-list-tabs" aria-label="Saved card list">
            <button
              className={activeListKind === 'collection' ? 'active' : ''}
              onClick={() => setActiveListKind('collection')}
              type="button"
            >
              Collection
            </button>
            <button
              className={activeListKind === 'wishlist' ? 'active' : ''}
              onClick={() => setActiveListKind('wishlist')}
              type="button"
            >
              <Heart size={16} />
              Wishlist
            </button>
          </div>

          {sectionGame === 'sports' && activeListKind === 'collection' && sportsPricingStatus?.configured && sportsPriceQueue.length > 0 && (
            <section className="sports-price-queue" aria-label="Sports pricing queue">
              <div>
                <strong>
                  Pricing queue: {Math.min(sportsPriceQueue.length, sportsPriceBatchTarget)}/
                  {sportsPriceBatchTarget}
                </strong>
                <span>
                  {!sportsPricingStatus?.configured
                    ? 'Pricing is unavailable. Scanning and saving still work normally.'
                    : `Pricing runs only when you press the button. Scanning and saving make no provider call.`}
                </span>
              </div>
              <button
                className="primary"
                disabled={!sportsPriceQueue.length || isPricingSports}
                onClick={() => void runSportsPriceBatch()}
                type="button"
              >
                {isPricingSports ? (
                  <Loader2 className="spin" size={17} />
                ) : (
                  <RefreshCw size={17} />
                )}
                {!sportsPriceQueue.length
                  ? 'No Cards Waiting'
                  : sportsPriceQueue.length >= sportsPriceBatchTarget
                    ? `Price ${sportsPriceBatchTarget} Cards`
                    : `Price ${sportsPriceQueue.length} Now`}
              </button>
            </section>
          )}

          {sectionGame === 'sports' && (!sportsPricingStatus?.configured || !sportsPriceQueue.length) && (
            <p className="muted">Sold-price lookup is not connected for these cards. Scanning, saving and manual values are available.</p>
          )}

          <div className="collection-controls">
            <label>
              Group
              <select
                onChange={(event) =>
                  setCollectionGroup(event.target.value as CollectionGroup)
                }
                value={collectionGroup}
              >
                <option value="color">
                  {sectionGame === 'sports' ? 'Sport' : 'Color / Type'}
                </option>
                <option value="category">Card Type</option>
                <option value="set">Set</option>
                <option value="condition">Condition</option>
              </select>
            </label>
            <label>
              Sort
              <select
                onChange={(event) =>
                  setCollectionSort(event.target.value as CollectionSort)
                }
                value={collectionSort}
              >
                <option value="name">Name</option>
                <option value="value">Value</option>
                <option value="quantity">Quantity</option>
                <option value="newest">Newest</option>
              </select>
            </label>
          </div>

          {items.length === 0 ? (
            <div className="empty-state">
              <Database size={32} />
              <h3>No {gameLabel} cards saved yet</h3>
              <p>Scan or search a card, check the match, then tap save.</p>
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="empty-state">
              <Search size={32} />
              <h3>No matching cards</h3>
              <p>Try another card name, set, color, type, or note.</p>
              <button
                onClick={() =>
                  setCollectionQueries((current) => ({
                    ...current,
                    [sectionGame]: '',
                  }))
                }
                type="button"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="grouped-collection">
              {groups.map((group) => (
                <section className="collection-group" key={group.label}>
                  <button
                    aria-expanded={isGroupOpen(group.label)}
                    className="group-heading"
                    onClick={() => toggleCollectionGroup(getGroupKey(group.label))}
                    type="button"
                  >
                    <span className="group-title">
                      {isGroupOpen(group.label) ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                      <strong>{group.label}</strong>
                    </span>
                    <span>
                      {formatDistinctCardCount(group.distinctCards)} ·{' '}
                      {formatMoney(group.value)}
                    </span>
                  </button>
                  {isGroupOpen(group.label) && (
                    sectionGame === 'mtg' &&
                    collectionGroup === 'color' &&
                    [
                      'White',
                      'Blue',
                      'Black',
                      'Red',
                      'Green',
                      'Multicolor',
                      'Colorless',
                      'Land',
                    ].includes(group.label) ? (
                      <div className="collection-subgroups">
                        {groupMtgColorSectionItems(group.label, group.items).map((subgroup) => {
                          const subgroupStateKey = `${group.label}/${subgroup.label}`
                          return (
                            <section className="collection-subgroup" key={subgroup.label}>
                              <button
                                aria-expanded={isGroupOpen(subgroupStateKey)}
                                className="group-heading subgroup-heading"
                                onClick={() =>
                                  toggleCollectionGroup(getGroupKey(subgroupStateKey))
                                }
                                type="button"
                              >
                                <span className="group-title">
                                  {isGroupOpen(subgroupStateKey) ? (
                                    <ChevronUp size={17} />
                                  ) : (
                                    <ChevronDown size={17} />
                                  )}
                                  <strong>{subgroup.label}</strong>
                                </span>
                                <span>
                                  {formatDistinctCardCount(subgroup.distinctCards)} ·{' '}
                                  {formatMoney(subgroup.value)}
                                </span>
                              </button>
                              {isGroupOpen(subgroupStateKey) &&
                                renderCollectionItems(subgroup.items)}
                            </section>
                          )
                        })}
                      </div>
                    ) : (
                      renderCollectionItems(group.items)
                    )
                  )}
                </section>
              ))}
            </div>
          )}
        </section>
      </section>
    )
  }

  function toggleCollectionGroup(groupKey: string) {
    setCollapsedGroups((current) => ({
      ...current,
      [groupKey]: current[groupKey] === false,
    }))
  }

  function renderSaveDeckDialog() {
    if (!saveDeckDraft) {
      return null
    }

    const candidate = saveDeckDraft.candidate
    const gameDecks = decks.filter((deck) => deck.game === candidate.game)
    const isCreatingDeck =
      saveDeckDraft.addToDeck &&
      (saveDeckDraft.deckId === createNewDeckValue || gameDecks.length === 0)
    const saveQuantity = Math.min(
      999,
      Math.max(1, Math.floor(Number(saveDeckDraft.quantity) || 1)),
    )

    return (
      <div
        aria-modal="true"
        className="card-preview-backdrop save-deck-backdrop"
        onClick={() => {
          if (!saveDeckDraft.isSaving) {
            setSaveDeckDraft(null)
          }
        }}
        role="dialog"
      >
        <section
          className="save-deck-popout"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            aria-label="Close save options"
            className="preview-close"
            disabled={saveDeckDraft.isSaving}
            onClick={() => setSaveDeckDraft(null)}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>

          <div>
            <h2>
              {typeof saveDeckDraft.collectionItemId === 'number'
                ? 'Add to Deck'
                : 'Save Card'}
            </h2>
            <p className="muted">
              {candidate.name} · {getGameLabel(candidate.game)}
            </p>
          </div>

          <div className="save-quantity-row">
            <span>Quantity</span>
            <div className="save-quantity-stepper">
              <button
                aria-label="Decrease save quantity"
                disabled={saveDeckDraft.isSaving || saveQuantity <= 1}
                onClick={() =>
                  updateSaveDeckDraft({ quantity: String(saveQuantity - 1) })
                }
                title="Decrease quantity"
                type="button"
              >
                <Minus size={17} />
              </button>
              <output aria-live="polite">{saveQuantity}</output>
              <button
                aria-label="Increase save quantity"
                disabled={saveDeckDraft.isSaving || saveQuantity >= 999}
                onClick={() =>
                  updateSaveDeckDraft({ quantity: String(saveQuantity + 1) })
                }
                title="Increase quantity"
                type="button"
              >
                <Plus size={17} />
              </button>
            </div>
          </div>

          {typeof saveDeckDraft.collectionItemId !== 'number' &&
            candidate.game === 'sports' && (
              <div className="save-choice">
                <button
                  className="active"
                  disabled={saveDeckDraft.isSaving}
                  onClick={() => void saveDraftWithoutDeck()}
                  type="button"
                >
                  <Save size={18} />
                  Add to Collection
                </button>
                <button
                  disabled={saveDeckDraft.isSaving}
                  onClick={() => void saveDraftToWishlist()}
                  type="button"
                >
                  <Heart size={18} />
                  Add to Wishlist
                </button>
              </div>
            )}

          {typeof saveDeckDraft.collectionItemId !== 'number' &&
            candidate.game !== 'sports' && (
            <div className="save-choice">
              <button
                className={!saveDeckDraft.addToDeck ? 'active' : ''}
                disabled={saveDeckDraft.isSaving}
                onClick={() => void saveDraftWithoutDeck()}
                type="button"
              >
                {saveDeckDraft.isSaving && !saveDeckDraft.addToDeck ? (
                  <Loader2 className="spin" size={18} />
                ) : (
                  <Save size={18} />
                )}
                No, just save
              </button>
              <button
                className={saveDeckDraft.addToDeck ? 'active' : ''}
                disabled={saveDeckDraft.isSaving}
                onClick={() =>
                  updateSaveDeckDraft({
                    addToDeck: true,
                    deckId: gameDecks[0]?.id
                      ? String(gameDecks[0].id)
                      : createNewDeckValue,
                  })
                }
                type="button"
              >
                Yes, add to deck
              </button>
            </div>
            )}

          {saveDeckDraft.addToDeck && (
            <div className="deck-save-fields">
              <label>
                Deck
                <select
                  disabled={saveDeckDraft.isSaving}
                  onChange={(event) =>
                    updateSaveDeckDraft({ deckId: event.target.value })
                  }
                  value={
                    gameDecks.length === 0 ? createNewDeckValue : saveDeckDraft.deckId
                  }
                >
                  {gameDecks.map((deck) => (
                    <option key={deck.id} value={String(deck.id)}>
                      {deck.name}
                    </option>
                  ))}
                  <option value={createNewDeckValue}>Create new deck</option>
                </select>
              </label>

              {isCreatingDeck && (
                <label>
                  New deck name
                  <input
                    disabled={saveDeckDraft.isSaving}
                    onChange={(event) =>
                      updateSaveDeckDraft({ newDeckName: event.target.value })
                    }
                    placeholder={`${getGameLabel(candidate.game)} Deck`}
                    value={saveDeckDraft.newDeckName}
                  />
                </label>
              )}

            </div>
          )}

          {saveDeckDraft.addToDeck && (
            <div className="edit-actions">
              <button
                className="secondary"
                disabled={saveDeckDraft.isSaving}
                onClick={() => setSaveDeckDraft(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="primary"
                disabled={saveDeckDraft.isSaving}
                onClick={() => void confirmSaveDeckDraft()}
                type="button"
              >
                {saveDeckDraft.isSaving ? (
                  <Loader2 className="spin" size={18} />
                ) : (
                  <Save size={18} />
                )}
                {isCreatingDeck ? 'Create Deck and Add' : 'Add to Deck'}
              </button>
            </div>
          )}
        </section>
      </div>
    )
  }

  function renderDeckBuilder(gameName: GameFilter) {
    const gameDecks = decks.filter((deck) => deck.game === gameName)
    const selectedDeck = gameDecks.find((deck) => deck.id === selectedDeckId)

    if (selectedDeck?.id) {
      const entries = deckCards
        .filter((entry) => entry.deckId === selectedDeck.id)
        .map((entry) => ({
          entry,
          item: collection.find((item) => item.id === entry.collectionItemId),
        }))
        .filter(
          (
            value,
          ): value is {
            entry: DeckCard
            item: CollectionItem
          } => Boolean(value.item),
        )
        .sort((left, right) => left.item.name.localeCompare(right.item.name))
      const totalCards = entries.reduce(
        (total, value) => total + value.entry.quantity,
        0,
      )
      const estimatedValue = entries.reduce(
        (total, value) =>
          total + (value.item.priceUsd ?? 0) * value.entry.quantity,
        0,
      )
      const deckGroups = groupDeckEditorEntries(
        entries,
        gameName,
        selectedDeck.commanderName,
      )
      const getDeckGroupKey = (label: string) =>
        `deck-${selectedDeck.id}-${label}`
      const isDeckGroupOpen = (label: string) =>
        collapsedGroups[getDeckGroupKey(label)] === false

    return (
      <div
        aria-modal="true"
        className="card-preview-backdrop"
          onClick={() => {
            setDeckBuilderGame(null)
            setSelectedDeckId(null)
            setIsRenamingDeck(false)
          }}
        role="dialog"
      >
        <section
            className="deck-popout deck-detail-popout"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            aria-label="Close deck builder"
            className="preview-close"
              onClick={() => {
                setDeckBuilderGame(null)
                setSelectedDeckId(null)
                setIsRenamingDeck(false)
              }}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>

            <div className="deck-detail-heading">
              <button
                aria-label="Back to decks"
                className="deck-back-button"
                onClick={() => {
                  setSelectedDeckId(null)
                  setIsRenamingDeck(false)
                }}
                title="Back to decks"
                type="button"
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                {isRenamingDeck ? (
                  <div className="deck-name-editor">
                    <input
                      aria-label="Deck name"
                      autoFocus
                      onChange={(event) => setDeckNameDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          void saveDeckName(selectedDeck)
                        }
                      }}
                      value={deckNameDraft}
                    />
                    <button
                      aria-label="Save deck name"
                      onClick={() => void saveDeckName(selectedDeck)}
                      title="Save deck name"
                      type="button"
                    >
                      <Save size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="deck-title-row">
                    <h2>{selectedDeck.name}</h2>
                    <button
                      aria-label="Rename deck"
                      onClick={() => {
                        setDeckNameDraft(selectedDeck.name)
                        setIsRenamingDeck(true)
                      }}
                      title="Rename deck"
                      type="button"
                    >
                      <Pencil size={17} />
                    </button>
                    <button
                      aria-label={`Delete ${selectedDeck.name}`}
                      className="deck-delete-button"
                      onClick={() => setDeckDeleteDraft(selectedDeck)}
                      title="Delete deck"
                      type="button"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                )}
                <p className="muted">{getGameLabel(gameName)} deck</p>
              </div>
            </div>

            <div className="deck-stats">
              <span>
                <strong>{totalCards}</strong>
                Cards
              </span>
              <span>
                <strong>{entries.length}</strong>
                Unique
              </span>
              <span>
                <strong>{formatMoney(estimatedValue)}</strong>
                Est. value
              </span>
            </div>

            <button
              className="coach-launch-button"
              onClick={() => openCoach(selectedDeck)}
              type="button"
            >
              <Sparkles size={18} />
              Coach
            </button>

            {entries.length === 0 ? (
              <div className="mini-empty-state">
                <FolderKanban size={24} />
                <span>This deck is empty.</span>
              </div>
            ) : (
              <div className="deck-category-list">
                {deckGroups.map((group) => (
                  <section className="deck-category" key={group.label}>
                    <button
                      aria-expanded={isDeckGroupOpen(group.label)}
                      className="group-heading deck-category-heading"
                      onClick={() =>
                        toggleCollectionGroup(getDeckGroupKey(group.label))
                      }
                      type="button"
                    >
                      <span className="group-title">
                        {isDeckGroupOpen(group.label) ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                        <strong>{group.label}</strong>
                      </span>
                      <span>
                        {group.entries.length} different · {group.quantity}{' '}
                        {group.quantity === 1 ? 'card' : 'cards'}
                      </span>
                    </button>
                    {isDeckGroupOpen(group.label) && (
                      <div className="collection-list deck-card-list">
                        {group.entries.map(({ entry, item }) => (
                          <article
                            className="collection-item deck-card-item"
                            key={entry.id}
                          >
                    <button
                      aria-label={`Open ${item.name}`}
                      className="deck-card-image-button"
                      onClick={() => setPreviewCard(item)}
                      title={`Open ${item.name}`}
                      type="button"
                    >
                      {renderCardImage(item)}
                    </button>
                    <div className="item-main">
                      <h3>{item.name}</h3>
                      <p>
                        {item.setCode} #{item.collectorNumber}
                      </p>
                      <span className="muted">{item.category}</span>
                    </div>
                    <div className="item-price">
                      <strong>{item.priceLabel}</strong>
                      <span>
                        {formatMoney((item.priceUsd ?? 0) * entry.quantity)}
                      </span>
                      <div className="deck-card-actions">
                        <div
                          aria-label={`Deck quantity for ${item.name}`}
                          className="deck-quantity-stepper"
                        >
                          <button
                            aria-label={`Decrease ${item.name} quantity`}
                            disabled={entry.quantity <= 1}
                            onClick={() =>
                              void adjustDeckCardQuantity(entry.id, -1)
                            }
                            title="Decrease quantity"
                            type="button"
                          >
                            <Minus size={16} />
                          </button>
                          <output aria-live="polite">{entry.quantity}</output>
                          <button
                            aria-label={`Increase ${item.name} quantity`}
                            disabled={entry.quantity >= 999}
                            onClick={() =>
                              void adjustDeckCardQuantity(entry.id, 1)
                            }
                            title="Increase quantity"
                            type="button"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <div className="icon-actions">
                          <a
                            aria-label={`Open source for ${item.name}`}
                            href={item.sourceUrl}
                            rel="noreferrer"
                            target="_blank"
                            title="Open source"
                          >
                            <ExternalLink size={17} />
                          </a>
                          <button
                            onClick={() => void removeCardFromDeck(entry.id)}
                            title="Remove from deck"
                            type="button"
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </div>
                    </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}
          </section>
        </div>
      )
    }

    return (
      <div
        aria-modal="true"
        className="card-preview-backdrop"
        onClick={() => {
          setDeckBuilderGame(null)
          setSelectedDeckId(null)
          setNewDeckNameDraft(null)
        }}
        role="dialog"
      >
        <section
          className="deck-popout"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            aria-label="Close deck builder"
            className="preview-close"
            onClick={() => {
              setDeckBuilderGame(null)
              setSelectedDeckId(null)
              setNewDeckNameDraft(null)
            }}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>
          <div className="deck-list-heading">
            <div>
              <h2>{getGameLabel(gameName)} Decks</h2>
              <p className="muted">Select a deck to view and edit its cards.</p>
            </div>
            <button
              aria-label="Add new deck"
              className="deck-add-button"
              onClick={() => setNewDeckNameDraft('')}
              title="Add new deck"
              type="button"
            >
              <Plus size={20} />
            </button>
          </div>

          {newDeckNameDraft !== null && (
            <form
              className="deck-create-row"
              onSubmit={(event) => {
                event.preventDefault()
                void createDeckFromBuilder(gameName)
              }}
            >
              <input
                aria-label="New deck name"
                autoFocus
                onChange={(event) => setNewDeckNameDraft(event.target.value)}
                placeholder={`${getGameLabel(gameName)} Deck`}
                value={newDeckNameDraft}
              />
              <button
                aria-label="Cancel new deck"
                className="secondary"
                onClick={() => setNewDeckNameDraft(null)}
                title="Cancel"
                type="button"
              >
                <X size={18} />
              </button>
              <button
                aria-label="Create deck"
                className="primary"
                title="Create deck"
                type="submit"
              >
                <Save size={18} />
              </button>
            </form>
          )}

          {gameDecks.length === 0 ? (
            <div className="mini-empty-state">
              <FolderKanban size={24} />
              <span>No {getGameLabel(gameName)} decks yet.</span>
            </div>
          ) : (
            <div className="deck-list">
              {gameDecks.map((deck) => {
                const cardsInDeck = deckCards
                  .filter((entry) => entry.deckId === deck.id)
                  .reduce((total, entry) => total + entry.quantity, 0)

                return (
                  <button
                    className="deck-row"
                    key={deck.id}
                    onClick={() => {
                      if (typeof deck.id === 'number') {
                        setSelectedDeckId(deck.id)
                        setDeckNameDraft(deck.name)
                      }
                    }}
                    type="button"
                  >
                    <div>
                      <h3>{deck.name}</h3>
                      <p className="muted">Updated {formatDate(deck.updatedAt)}</p>
                    </div>
                    <span className="deck-row-count">
                      <strong>{cardsInDeck}</strong>
                      cards
                      <ChevronRight size={18} />
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </section>
      </div>
    )
  }

  function renderDeckDeleteDialog() {
    if (!deckDeleteDraft || typeof deckDeleteDraft.id !== 'number') {
      return null
    }

    const assignedQuantity = deckCards
      .filter((entry) => entry.deckId === deckDeleteDraft.id)
      .reduce((total, entry) => total + entry.quantity, 0)

    return (
      <div
        aria-modal="true"
        className="card-preview-backdrop deck-delete-backdrop"
        onClick={() => setDeckDeleteDraft(null)}
        role="dialog"
      >
        <section
          className="save-deck-popout deck-delete-popout"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            aria-label="Cancel deck deletion"
            className="preview-close"
            onClick={() => setDeckDeleteDraft(null)}
            title="Cancel"
            type="button"
          >
            <X size={20} />
          </button>
          <div className="deck-delete-heading">
            <Trash2 size={22} />
            <div>
              <h2>Delete {deckDeleteDraft.name}?</h2>
              <p className="muted">This cannot be undone.</p>
            </div>
          </div>
          <p>
            The deck and its {assignedQuantity} assigned{' '}
            {assignedQuantity === 1 ? 'card' : 'cards'} will be removed. Your
            collection cards will remain saved and become available to other
            decks.
          </p>
          <div className="deck-delete-actions">
            <button
              className="secondary"
              onClick={() => setDeckDeleteDraft(null)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="danger-action"
              onClick={() => void deleteDeck(deckDeleteDraft)}
              type="button"
            >
              <Trash2 size={18} />
              Delete Deck
            </button>
          </div>
        </section>
      </div>
    )
  }

  function renderCoachDialog() {
    if (!coachDraft) {
      return null
    }

    const deck = decks.find((item) => item.id === coachDraft.deckId)
    const formatOptions = getCoachFormats(coachDraft.game)
    const deterministic = coachResult?.deterministic
    const strengths = uniqueStrings([
      ...(deterministic?.strengths ?? []),
      ...(coachResult?.strengths ?? []),
    ])
    const concerns = uniqueStrings([
      ...(deterministic?.concerns ?? []),
      ...(coachResult?.concerns ?? []),
    ])
    const quickPrompts = getCoachQuickPrompts(
      coachDraft.game,
      coachDraft.scope,
    )
    const comboColorOptions = getCoachComboColorOptions(coachDraft.game)
    const buildColorOptions = getCoachBuildColorOptions(coachDraft.game)
    const coachCost = coachCreditCosts[coachDraft.mode] ?? coachCreditCosts.ask
    const isCoachOutOfCredits =
      Boolean(coachCredits) &&
      !coachCredits?.unlimited &&
      (coachCredits?.balance ?? 0) < coachCost
    const coachCreditWarning =
      "You're out of Coach credits. Add credits or choose Unlimited Coach to keep using Coach."
    const latestCreditUsage = coachResult?.creditUsage
    const latestCreditChargeLabel = latestCreditUsage
      ? latestCreditUsage.unlimited || latestCreditUsage.deducted === 0
        ? 'No credits charged'
        : `${latestCreditUsage.deducted} credit${latestCreditUsage.deducted === 1 ? '' : 's'} charged`
      : ''

    return (
      <div
        aria-modal="true"
        className="card-preview-backdrop coach-backdrop"
        onClick={() => {
          if (!isCoachLoading) {
            replaceCoachDraft(null)
            setCoachComboRefinement(null)
            setCoachBuildRefinement(null)
          }
        }}
        role="dialog"
      >
        <section
          className="coach-popout"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            aria-label="Close Coach"
            className="preview-close"
            disabled={isCoachLoading}
            onClick={() => {
              replaceCoachDraft(null)
              setCoachComboRefinement(null)
              setCoachBuildRefinement(null)
            }}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>
          <button
            aria-label="Clear Coach thread"
            className="coach-thread-clear"
            disabled={isCoachLoading || coachMessages.length === 0}
            onClick={clearCurrentCoachThread}
            title="Clear thread"
            type="button"
          >
            <Trash2 size={18} />
          </button>

          <header className="coach-heading">
            <div className="coach-mark">
              <Sparkles size={22} />
            </div>
            <div>
              <h2>Coach</h2>
              <p className="muted">
                {deck?.name ?? `${getGameLabel(coachDraft.game)} Library`}
              </p>
            </div>
          </header>

          <div className="coach-credit-strip">
            <span>
              {!coachCredits
                ? 'Checking Coach credits'
                : coachCredits.unlimited
                ? 'Unlimited Coach'
                  : `${coachCredits.balance} Coach credits`}
            </span>
            {latestCreditUsage ? (
              <span>{latestCreditChargeLabel}</span>
            ) : (
              <button
                aria-expanded={isCoachCreditListOpen}
                className="coach-credit-list-button"
                onClick={() => setIsCoachCreditListOpen((current) => !current)}
                type="button"
              >
                Credit List
              </button>
            )}
            <button
              className="coach-credit-action"
              onClick={openPurchaseDialog}
              type="button"
            >
              <CreditCard size={15} />
              Add Credits
            </button>
          </div>
          {isCoachCreditListOpen && (
            <section className="coach-credit-list-panel" aria-label="Coach credit list">
              <header>
                <div>
                  <strong>Coach credit list</strong>
                  <span>Charges appear after Coach confirms the request.</span>
                </div>
                <button
                  aria-label="Close credit list"
                  onClick={() => setIsCoachCreditListOpen(false)}
                  type="button"
                >
                  <X size={16} />
                </button>
              </header>
              <div className="coach-credit-list-rows">
                {coachCreditChargeRows.map((row) => (
                  <div key={row.label}>
                    <span>
                      <strong>{row.label}</strong>
                      <small>{row.detail}</small>
                    </span>
                    <b>
                      {row.cost} credit{row.cost === 1 ? '' : 's'}
                    </b>
                  </div>
                ))}
                <div>
                  <span>
                    <strong>Unlimited Coach</strong>
                    <small>No credit spend while the subscription is active.</small>
                  </span>
                  <b>No spend</b>
                </div>
              </div>
            </section>
          )}
          {isCoachOutOfCredits && (
            <div className="coach-alert warning" role="alert">
              <TriangleAlert size={18} />
              <span>{coachCreditWarning}</span>
            </div>
          )}

          {coachDraft.scope === 'deck' && (
            <div className="coach-format-bar">
              <label>
                Deck format
                <select
                  disabled={isCoachLoading}
                  onChange={(event) =>
                    updateCoachDraft({
                      format: event.target.value,
                      commanderName:
                        event.target.value === 'commander'
                          ? coachDraft.commanderName
                          : '',
                    })
                  }
                  value={coachDraft.format}
                >
                  {formatOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {coachDraft.game === 'mtg' &&
                coachDraft.format === 'commander' && (
                  <label>
                    Commander
                    <input
                      disabled={isCoachLoading}
                      onChange={(event) =>
                        updateCoachDraft({ commanderName: event.target.value })
                      }
                      placeholder="Card name"
                      value={coachDraft.commanderName}
                    />
                  </label>
                )}
            </div>
          )}

          <div className="coach-chat" aria-live="polite">
            <div className="coach-message assistant">
              <div className="coach-message-mark">
                <Sparkles size={16} />
              </div>
              <div className="coach-message-body">
                <strong>What do you need help with?</strong>
              </div>
            </div>

            {coachMessages.map((message) => (
              <div
                className={`coach-message ${message.role}`}
                key={message.id}
              >
                {message.role === 'assistant' && (
                  <div className="coach-message-mark">
                    <Sparkles size={16} />
                  </div>
                )}
                <div className="coach-message-body">
                  <p>
                    {message.role === 'assistant'
                      ? renderCoachText(message.text, message.result?.cardRefs)
                      : message.text}
                  </p>

                  {message.result?.mode === 'build' &&
                    message.result.ownedRecommendations.length > 0 && (
                      <div className="coach-save-deck-action">
                        <button
                          className="coach-save-deck-button"
                          disabled={
                            Boolean(savingCoachDeckMessageId) ||
                            Boolean(savedCoachDeckNames[message.id])
                          }
                          onClick={() => void saveCoachGeneratedDeck(message)}
                          title="Save this generated deck"
                          type="button"
                        >
                          {savingCoachDeckMessageId === message.id ? (
                            <Loader2 className="spin" size={17} />
                          ) : (
                            <Plus size={17} />
                          )}
                          {savedCoachDeckNames[message.id]
                            ? `Saved as ${savedCoachDeckNames[message.id]}`
                            : 'Save as New Deck'}
                        </button>
                      </div>
                    )}

                  {message.result?.synergies.length ? (
                    <div className="coach-answer-cards">
                      {message.result.synergies.map((synergy, index) => (
                        <article key={`${message.id}-synergy-${index}`}>
                          {synergy.tier && (
                            <span className="coach-synergy-tier">{synergy.tier}</span>
                          )}
                          <div className="coach-card-name-row">
                            {synergy.cards.map((cardName, cardIndex) => {
                              const cardRef = getCoachCardRef(message.result!, cardName)
                              return (
                                <span key={`${message.id}-${cardName}`}>
                                  {cardIndex > 0 && <i>+</i>}
                                  <button
                                    onClick={() =>
                                      openCoachCard(cardName, cardRef?.sourceId)
                                    }
                                    type="button"
                                  >
                                    {cardName}
                                  </button>
                                </span>
                              )
                            })}
                          </div>
                          <p>
                            {renderCoachText(
                              synergy.explanation,
                              message.result!.cardRefs,
                            )}
                          </p>
                        </article>
                      ))}
                    </div>
                  ) : null}

                  {message.result?.ownedRecommendations.length ? (
                    <div className="coach-answer-cards">
                      {message.result.ownedRecommendations.map(
                        (recommendation, index) =>
                          renderCoachOwnedRecommendation(
                            message,
                            recommendation,
                            index,
                          ),
                      )}
                    </div>
                  ) : null}

                  {message.result &&
                    message.result.synergies.length === 0 &&
                    message.result.ownedRecommendations.length === 0 &&
                    (message.result.strengths.length > 0 ||
                      message.result.concerns.length > 0) && (
                      <ul className="coach-answer-points">
                        {[
                          ...message.result.strengths,
                          ...message.result.concerns,
                        ].map((item) => (
                          <li key={`${message.id}-${item}`}>
                            {renderCoachText(item, message.result!.cardRefs)}
                          </li>
                        ))}
                      </ul>
                    )}

                  {message.result && (
                    <details className="coach-answer-details">
                      <summary>Answer details</summary>
                      <p>
                        {message.result.mode === 'build' ? (
                          <>
                            {countCoachAdditions(message.result)} nonland cards
                            selected. Coach searched{' '}
                            {message.result.analysisScope.collectionCardsMatched ??
                              message.result.analysisScope.collectionCardsHydrated}{' '}
                            matching owned cards and closely checked{' '}
                            {message.result.analysisScope.collectionCardsHydrated}.
                          </>
                        ) : (
                          <>
                            Coach searched{' '}
                            {message.result.analysisScope.collectionCardsMatched ??
                              message.result.analysisScope.collectionCardsHydrated}{' '}
                            matching owned cards and closely checked{' '}
                            {message.result.analysisScope.collectionCardsHydrated ||
                              message.result.analysisScope.collectionCardsModeled}.
                          </>
                        )}
                      </p>
                      {message.result.deterministic.legalityIssues.length > 0 && (
                        <ul>
                          {message.result.deterministic.legalityIssues.map(
                            (issue, index) => (
                              <li key={`${message.id}-issue-${index}`}>
                                <b>{issue.card}:</b> {issue.issue}
                              </li>
                            ),
                          )}
                        </ul>
                      )}
                    </details>
                  )}
                </div>
              </div>
            ))}

            {isCoachLoading && (
              <div className="coach-message assistant thinking">
                <div className="coach-message-mark">
                  <Loader2 className="spin" size={16} />
                </div>
                <div className="coach-message-body">
                  <p>Checking your cards and their exact rules...</p>
                </div>
              </div>
            )}
            {isCoachDeckBuildRunning && (
              <div className="coach-build-wait coach-build-wait-chat" role="status">
                <TriangleAlert aria-hidden="true" size={18} />
                <span>
                  Building a full deck takes longer. Coach is checking your collection.
                </span>
              </div>
            )}
            <div ref={coachChatEndRef} />
          </div>

          {coachComboRefinement ? (
            <section className="coach-combo-refinement">
              <header>
                <div>
                  <strong>Useful combo filters</strong>
                  <span>Only cards you own will be checked.</span>
                </div>
                <button
                  aria-label="Close combo filters"
                  onClick={() => setCoachComboRefinement(null)}
                  title="Close filters"
                  type="button"
                >
                  <X size={17} />
                </button>
              </header>

              <fieldset>
                <legend>
                  {coachDraft.game === 'pokemon' ? 'Pokemon types' : 'Colors'}
                </legend>
                <div className="coach-filter-chips">
                  <button
                    className={
                      coachComboRefinement.colors.length === 0 ? 'active' : ''
                    }
                    onClick={() =>
                      setCoachComboRefinement((current) =>
                        current ? { ...current, colors: [] } : current,
                      )
                    }
                    type="button"
                  >
                    Any
                  </button>
                  {comboColorOptions.map((option) => (
                    <button
                      className={
                        coachComboRefinement.colors.includes(option.value)
                          ? 'active'
                          : ''
                      }
                      key={option.value}
                      onClick={() => toggleCoachComboColor(option.value)}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend>Combo type</legend>
                <div className="coach-filter-chips">
                  {getCoachComboKinds().map((option) => (
                    <button
                      className={
                        coachComboRefinement.kind === option.value
                          ? 'active'
                          : ''
                      }
                      key={option.value}
                      onClick={() =>
                        setCoachComboRefinement((current) =>
                          current ? { ...current, kind: option.value } : current,
                        )
                      }
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </fieldset>

              {coachDraft.scope === 'deck' && (
                <fieldset>
                  <legend>Search area</legend>
                  <div className="coach-filter-chips">
                    {[
                      { label: 'This deck', value: 'deck' as const },
                      { label: 'Whole collection', value: 'collection' as const },
                    ].map((option) => (
                      <button
                        className={
                          coachComboRefinement.area === option.value
                            ? 'active'
                            : ''
                        }
                        key={option.value}
                        onClick={() =>
                          setCoachComboRefinement((current) =>
                            current ? { ...current, area: option.value } : current,
                          )
                        }
                        type="button"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              <div className="coach-filter-actions">
                <button
                  disabled={isCoachLoading || isCoachOutOfCredits}
                  onClick={() => askCoachForRefinedCombos(true)}
                  type="button"
                >
                  Skip filters
                </button>
                <button
                  className="primary"
                  disabled={isCoachLoading || isCoachOutOfCredits}
                  onClick={() => askCoachForRefinedCombos()}
                  type="button"
                >
                  <Sparkles size={17} />
                  Ask Coach
                </button>
              </div>
            </section>
          ) : coachBuildRefinement ? (
            <section className="coach-combo-refinement">
              <header>
                <div>
                  <strong>Deck build options</strong>
                  <span>Coach will build only from cards you own.</span>
                </div>
                <button
                  aria-label="Close deck build options"
                  onClick={() => setCoachBuildRefinement(null)}
                  title="Close options"
                  type="button"
                >
                  <X size={17} />
                </button>
              </header>

              <fieldset>
                <legend>
                  {coachDraft.game === 'pokemon' ? 'Pokemon types' : 'Colors'}
                </legend>
                <div className="coach-filter-chips">
                  <button
                    className={
                      coachBuildRefinement.colors.length === 0 ? 'active' : ''
                    }
                    onClick={() => {
                      setCommanderSuggestions([])
                      setCoachBuildRefinement((current) =>
                        current ? { ...current, colors: [] } : current,
                      )
                    }}
                    type="button"
                  >
                    Any
                  </button>
                  {buildColorOptions.map((option) => (
                    <button
                      className={
                        coachBuildRefinement.colors.includes(option.value)
                          ? 'active'
                          : ''
                      }
                      key={option.value}
                      onClick={() => toggleCoachBuildColor(option.value)}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend>Deck style</legend>
                <div className="coach-filter-chips">
                  {getCoachBuildStyles(coachDraft.game).map((option) => (
                    <button
                      className={
                        coachBuildRefinement.style === option.value ? 'active' : ''
                      }
                      key={option.value}
                      onClick={() => {
                        setCommanderSuggestions([])
                        setCoachBuildRefinement((current) =>
                          current ? { ...current, style: option.value } : current,
                        )
                      }}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend>Deck format</legend>
                <div className="coach-filter-chips">
                  {formatOptions.map((option) => (
                    <button
                      className={coachDraft.format === option.value ? 'active' : ''}
                      key={option.value}
                      onClick={() => {
                        setCommanderSuggestions([])
                        updateCoachDraft({
                          format: option.value,
                          commanderName:
                            option.value === 'commander'
                              ? coachDraft.commanderName
                              : '',
                        })
                      }}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {coachDraft.game === 'mtg' &&
                  coachDraft.format === 'commander' && (
                    <div className="coach-build-commander">
                      <div className="coach-commander-label-row">
                        <span>Commander card</span>
                        <button
                          disabled={
                            isCoachLoading ||
                            isSuggestingCommander ||
                            (
                              Boolean(coachCredits) &&
                              !coachCredits?.unlimited &&
                              (coachCredits?.balance ?? 0) <
                                commanderSuggestionCreditCost
                            )
                          }
                          onClick={() => void suggestCoachCommander()}
                          type="button"
                        >
                          {isSuggestingCommander ? (
                            <Loader2 className="spin" size={15} />
                          ) : (
                            <Sparkles size={15} />
                          )}
                          Suggest a Commander
                        </button>
                      </div>
                      <input
                        disabled={isCoachLoading}
                        onChange={(event) => {
                          setCommanderSuggestions([])
                          updateCoachDraft({ commanderName: event.target.value })
                        }}
                        placeholder="Card name"
                        value={coachDraft.commanderName}
                      />
                      {commanderSuggestions.length > 0 && (
                        <div className="coach-commander-suggestions">
                          {commanderSuggestions.map((suggestion) => {
                            const card = collection.find(
                              (item) => item.sourceId === suggestion.sourceId,
                            )
                            return (
                              <article key={suggestion.sourceId}>
                                <button
                                  className="coach-commander-preview"
                                  onClick={() =>
                                    openCoachCard(suggestion.name, suggestion.sourceId)
                                  }
                                  title={`View ${suggestion.name}`}
                                  type="button"
                                >
                                  {card?.imageUrl && (
                                    <img alt="" src={card.imageUrl} />
                                  )}
                                  <span>
                                    <strong>{suggestion.name}</strong>
                                    <small>{suggestion.reason}</small>
                                  </span>
                                </button>
                                <button
                                  className="coach-use-commander"
                                  onClick={() =>
                                    updateCoachDraft({ commanderName: suggestion.name })
                                  }
                                  type="button"
                                >
                                  <Check size={15} />
                                  Use Commander
                                </button>
                              </article>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
              </fieldset>

              <div className="coach-filter-actions">
                <button
                  disabled={isCoachLoading || isCoachOutOfCredits}
                  onClick={() => askCoachForRefinedBuild(true)}
                  type="button"
                >
                  Skip options
                </button>
                <button
                  className="primary"
                  disabled={isCoachLoading || isCoachOutOfCredits}
                  onClick={() => askCoachForRefinedBuild()}
                  type="button"
                >
                  <Sparkles size={17} />
                  Build Deck
                </button>
              </div>
            </section>
          ) : (
            <section className="coach-quick-section">
              <button
                aria-expanded={areCoachPromptsOpen}
                className="coach-quick-toggle"
                disabled={isCoachLoading}
                onClick={() => setAreCoachPromptsOpen((current) => !current)}
                type="button"
              >
                <span>Common questions</span>
                <ChevronDown
                  className={areCoachPromptsOpen ? 'is-open' : ''}
                  size={19}
                />
              </button>
              {areCoachPromptsOpen && (
                <div className="coach-quick-prompts">
                  {quickPrompts.map((prompt) => (
                    <button
                      disabled={isCoachLoading}
                      key={prompt.label}
                      onClick={() => selectCoachQuickPrompt(prompt)}
                      type="button"
                    >
                      {prompt.label}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          <form
            className="coach-composer"
            onSubmit={(event) => {
              event.preventDefault()
              void runCoach()
            }}
          >
            <textarea
              aria-label="Ask Coach"
              disabled={isCoachLoading || isCoachOutOfCredits}
              onChange={(event) => {
                setCoachQuestion(event.target.value)
                updateCoachDraft({ goal: event.target.value })
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void runCoach()
                }
              }}
              placeholder="Ask Coach about cards you own..."
              rows={2}
              value={coachQuestion}
            />
            <button
              aria-label="Send question"
              disabled={isCoachLoading || isCoachOutOfCredits || !coachQuestion.trim()}
              title="Send"
              type="submit"
            >
              {isCoachLoading ? (
                <Loader2 className="spin" size={19} />
              ) : (
                <Send size={19} />
              )}
            </button>
          </form>

          {coachError && (
            <div className="coach-alert error" role="alert">
              <TriangleAlert size={18} />
              <span>{coachError}</span>
            </div>
          )}

          {coachResult && !isCoachLoading && (
            <details className="coach-full-analysis">
              <summary>Full deck analysis</summary>
              <div className="coach-results">
                <div className="coach-result-heading">
                  <div>
                    <span
                      className={`coach-status ${coachResult.modelStatus}`}
                    >
                      {coachResult.modelStatus === 'ready'
                        ? 'Coach ready'
                        : coachResult.modelStatus === 'partial'
                          ? 'Coach checked'
                        : coachResult.modelStatus === 'busy'
                          ? 'Checks only'
                          : 'Model offline'}
                    </span>
                    <h3>{renderCoachText(coachResult.summary, coachResult.cardRefs)}</h3>
                  </div>
                </div>

                {deterministic && (
                  <div className="coach-metrics">
                    {Object.entries(deterministic.metrics).map(([key, value]) => (
                      <span key={key}>
                        <strong>{value}</strong>
                        {formatMetricLabel(key)}
                      </span>
                    ))}
                  </div>
                )}

                {deterministic?.legalityIssues.length ? (
                  <section className="coach-section coach-legality">
                    <h3>
                      <TriangleAlert size={18} />
                      Deck Checks
                    </h3>
                    <div className="coach-list">
                      {deterministic.legalityIssues.map((issue, index) => (
                        <article key={`${issue.card}-${index}`}>
                          <strong>
                            {renderCoachText(issue.card, coachResult.cardRefs)}
                          </strong>
                          <p>{issue.issue}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : deterministic && coachDraft.scope === 'deck' ? (
                  <div className="coach-alert success">
                    <Check size={18} />
                    <span>Deck checks passed.</span>
                  </div>
                ) : null}

                {(strengths.length > 0 || concerns.length > 0) && (
                  <div className="coach-summary-grid">
                    {strengths.length > 0 && (
                      <section className="coach-section">
                        <h3>Working Well</h3>
                        <ul>
                          {strengths.map((item) => (
                            <li key={item}>
                              {renderCoachText(item, coachResult.cardRefs)}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    {concerns.length > 0 && (
                      <section className="coach-section">
                        <h3>Needs Attention</h3>
                        <ul>
                          {concerns.map((item) => (
                            <li key={item}>
                              {renderCoachText(item, coachResult.cardRefs)}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                  </div>
                )}

                {coachResult.warnings.length > 0 && (
                  <details className="coach-notes">
                    <summary>Analysis notes</summary>
                    <ul>
                      {coachResult.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </details>
          )}
        </section>
      </div>
    )
  }

  function renderScanIssueDialog() {
    if (!scanIssueDraft) {
      return null
    }

    return (
      <div
        aria-modal="true"
        className="card-preview-backdrop"
        onClick={() => {
          if (!scanIssueDraft.isSaving) {
            setScanIssueDraft(null)
          }
        }}
        role="dialog"
      >
        <section
          className="scan-issue-popout"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            aria-label="Close scan issue report"
            className="preview-close"
            disabled={scanIssueDraft.isSaving}
            onClick={() => setScanIssueDraft(null)}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>

          <div>
            <h2>Report Scan Issue</h2>
            <p className="muted">
              This saves the captured image and scan details locally.
            </p>
          </div>

          {capturedFrame && (
            <img
              alt=""
              className="scan-issue-image"
              src={capturedFrame}
            />
          )}

          <div className="deck-save-fields">
            <label>
              Correct card name
              <input
                autoComplete="off"
                disabled={scanIssueDraft.isSaving}
                onChange={(event) =>
                  updateScanIssueDraft({ correctName: event.target.value })
                }
                placeholder="Korvold, Fae-Cursed King"
                value={scanIssueDraft.correctName}
              />
            </label>

            <label>
              Wrong result shown
              <select
                disabled={scanIssueDraft.isSaving || candidates.length === 0}
                onChange={(event) =>
                  updateScanIssueDraft({ wrongCandidateSourceId: event.target.value })
                }
                value={scanIssueDraft.wrongCandidateSourceId}
              >
                <option value="">No wrong result selected</option>
                {candidates.map((candidate) => (
                  <option
                    key={`${candidate.game}-${candidate.sourceId}`}
                    value={candidate.sourceId}
                  >
                    {candidate.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Notes
              <input
                disabled={scanIssueDraft.isSaving}
                onChange={(event) =>
                  updateScanIssueDraft({ notes: event.target.value })
                }
                placeholder="What went wrong?"
                value={scanIssueDraft.notes}
              />
            </label>
          </div>

          <div className="scan-report-context">
            <span>{getGameLabel(game)}</span>
            <span>{selectedTerm || query.trim() || 'No search term'}</span>
          </div>

          <div className="edit-actions">
            <button
              className="secondary"
              disabled={scanIssueDraft.isSaving}
              onClick={() => setScanIssueDraft(null)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="primary"
              disabled={scanIssueDraft.isSaving}
              onClick={() => void saveScanIssueReport()}
              type="button"
            >
              {scanIssueDraft.isSaving ? (
                <Loader2 className="spin" size={18} />
              ) : (
                <Save size={18} />
              )}
              Save Report
            </button>
          </div>
        </section>
      </div>
    )
  }

  function renderAccountDialog() {
    if (!accountDialogOpen) {
      return null
    }

    return (
      <div className="account-backdrop" role="presentation">
        <section
          aria-modal="true"
          className="account-dialog"
          role="dialog"
        >
          <div className="account-heading">
            <UserRound size={23} />
            <div>
              <p className="eyebrow">Card Vault profile</p>
              <h2>
                {accountSession
                  ? accountSession.profile.displayName
                  : accountMode === 'create'
                    ? 'Create Profile'
                    : 'Sign In'}
              </h2>
            </div>
          </div>

          {isAccountBooting ? (
            <div className="account-loading">
              <Loader2 className="spin" size={21} />
              Checking this device
            </div>
          ) : accountSession ? (
            <>
              <div className="account-profile-row">
                <div>
                  <strong>{accountSession.profile.displayName}</strong>
                  <span>@{accountSession.profile.username}</span>
                </div>
                <Cloud size={21} />
              </div>
              {accountStatus && <p className="account-status">{accountStatus}</p>}
              <div className="account-actions">
                {canShowAndroidAppPromo && (
                  <button
                    className="secondary android-app-button"
                    onClick={() => setIsAndroidAppPanelOpen(true)}
                    type="button"
                  >
                    <ScanLine size={18} />
                    Android QR
                  </button>
                )}
                <button
                  className="secondary"
                  onClick={() => void signOutAccount()}
                  type="button"
                >
                  <LogOut size={18} />
                  Sign Out
                </button>
                <button
                  className="secondary"
                  onClick={openPurchaseDialog}
                  type="button"
                >
                  <CreditCard size={18} />
                  Coach Credits
                </button>
                <button
                  className="secondary"
                  onClick={() => void performAccountSync(true)}
                  type="button"
                >
                  <Cloud size={18} />
                  Sync Now
                </button>
                <button
                  className="primary"
                  onClick={() => setAccountDialogOpen(false)}
                  type="button"
                >
                  <Check size={18} />
                  Done
                </button>
              </div>
            </>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault()
                void submitAccount()
              }}
            >
              <div className="account-mode" aria-label="Account action">
                <button
                  className={accountMode === 'login' ? 'active' : ''}
                  onClick={() => {
                    setAccountMode('login')
                    setAccountStatus('')
                  }}
                  type="button"
                >
                  Sign In
                </button>
                <button
                  className={accountMode === 'create' ? 'active' : ''}
                  onClick={() => {
                    setAccountMode('create')
                    setAccountStatus('')
                  }}
                  type="button"
                >
                  Create
                </button>
              </div>

              <label>
                Username
                <input
                  autoCapitalize="none"
                  autoComplete="username"
                  onChange={(event) =>
                    updateAccountDraft({ username: event.target.value })
                  }
                  value={accountDraft.username}
                />
              </label>

              {accountMode === 'create' && (
                <label>
                  Preferred name
                  <input
                    autoComplete="name"
                    onChange={(event) =>
                      updateAccountDraft({ displayName: event.target.value })
                    }
                    placeholder={accountDraft.username || 'Optional'}
                    value={accountDraft.displayName}
                  />
                </label>
              )}

              <label>
                Password
                <input
                  autoComplete={
                    accountMode === 'create' ? 'new-password' : 'current-password'
                  }
                  onChange={(event) =>
                    updateAccountDraft({ password: event.target.value })
                  }
                  type="password"
                  value={accountDraft.password}
                />
              </label>
              {accountMode === 'create' && (
                <p className="account-note">
                  At least 5 characters. No symbol or capitalization rules.
                </p>
              )}

              <label className="account-remember">
                <input
                  checked={accountDraft.remember}
                  onChange={(event) =>
                    updateAccountDraft({ remember: event.target.checked })
                  }
                  type="checkbox"
                />
                Remember this device
              </label>

              {accountStatus && <p className="account-status">{accountStatus}</p>}

              <div className="account-actions">
                {canShowAndroidAppPromo && (
                  <button
                    className="secondary android-app-button"
                    onClick={() => setIsAndroidAppPanelOpen(true)}
                    type="button"
                  >
                    <ScanLine size={18} />
                    Android QR
                  </button>
                )}
                <button
                  className="primary"
                  disabled={accountDraft.isSaving}
                  type="submit"
                >
                  {accountDraft.isSaving ? (
                    <Loader2 className="spin" size={18} />
                  ) : (
                    <UserRound size={18} />
                  )}
                  {accountMode === 'create' ? 'Create Profile' : 'Sign In'}
                </button>
              </div>
            </form>
          )}
          {canShowAndroidAppPromo && isAndroidAppPanelOpen && (
            <section className="android-app-panel" aria-label="Card Vault Android app">
              <div className="android-app-panel-heading">
                <div>
                  <p className="eyebrow">Phone scanner</p>
                  <h3>Get Card Vault for Android</h3>
                </div>
                <button
                  aria-label="Close Android app QR"
                  onClick={() => setIsAndroidAppPanelOpen(false)}
                  type="button"
                >
                  <X size={17} />
                </button>
              </div>
              <p>
                Scan cards on your phone, review and organize on Windows.
              </p>
              <img
                alt="QR code for Card Vault on Google Play"
                className="android-app-qr"
                src="./card-vault-android-qr.png"
              />
              <a
                className="android-app-store-link"
                href={cardVaultAndroidUrl}
                rel="noreferrer"
                target="_blank"
              >
                <ExternalLink size={17} />
                Open Play Store
              </a>
            </section>
          )}
          <nav className="account-policy-links" aria-label="Account help and policies">
            <a href="https://cmforgedbyfire.com/privacy.html">
              Privacy
            </a>
            <a href="https://cmforgedbyfire.com/support.html">
              Support
            </a>
            <a href="https://cmforgedbyfire.com/delete-account.html">
              Delete account
            </a>
          </nav>
        </section>
      </div>
    )
  }

  function renderFeedbackDialog() {
    if (!feedbackDialogOpen) {
      return null
    }

    return (
      <div
        className="account-backdrop"
        onClick={() => setFeedbackDialogOpen(false)}
        role="presentation"
      >
        <section
          aria-modal="true"
          className="account-dialog feedback-dialog"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
        >
          <button
            aria-label="Close feedback"
            className="preview-close"
            onClick={() => setFeedbackDialogOpen(false)}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>
          <div className="account-heading">
            <MessageSquare size={23} />
            <div>
              <p className="eyebrow">Card Vault feedback</p>
              <h2>Send Feedback</h2>
            </div>
          </div>

          <div className="feedback-kind-row" aria-label="Feedback category">
            {['Worked well', 'Confusing', 'Broke'].map((choice) => (
              <button
                className={feedbackDraft.choice === choice ? 'selected' : ''}
                disabled={feedbackDraft.isSaving}
                key={choice}
                onClick={() =>
                  setFeedbackDraft((current) => ({
                    ...current,
                    choice,
                    error: '',
                  }))
                }
                type="button"
              >
                {choice}
              </button>
            ))}
          </div>

          <label>
            Feedback note
            <textarea
              aria-invalid={Boolean(feedbackDraft.error)}
              onChange={(event) =>
                setFeedbackDraft((current) => ({
                  ...current,
                  error: '',
                  note: event.target.value,
                }))
              }
              placeholder="Tell me what happened or what you want changed"
              rows={5}
              value={feedbackDraft.note}
            />
          </label>

          <label>
            Reply email
            <input
              autoComplete="email"
              onChange={(event) =>
                setFeedbackDraft((current) => ({
                  ...current,
                  email: event.target.value,
                  error: '',
                }))
              }
              placeholder="Optional"
              type="email"
              value={feedbackDraft.email}
            />
          </label>

          {feedbackDraft.error ? (
            <p className="feedback-nudge-error" role="alert">
              {feedbackDraft.error}
            </p>
          ) : null}

          <div className="account-actions">
            <button
              className="secondary"
              disabled={feedbackDraft.isSaving}
              onClick={() => setFeedbackDialogOpen(false)}
              type="button"
            >
              <X size={18} />
              Close
            </button>
            <button
              className="primary"
              disabled={feedbackDraft.isSaving}
              onClick={() => void sendFeedbackNote()}
              type="button"
            >
              {feedbackDraft.isSaving ? (
                <Loader2 className="spin" size={18} />
              ) : (
                <Send size={18} />
              )}
              Send
            </button>
          </div>
        </section>
      </div>
    )
  }

  function renderPurchaseDialog() {
    if (!purchaseDialogOpen) {
      return null
    }

    return (
      <div
        className="account-backdrop"
        onClick={() => setPurchaseDialogOpen(false)}
        role="presentation"
      >
        <section
          aria-modal="true"
          className="purchase-dialog"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
        >
          <button
            aria-label="Close Coach purchase options"
            className="preview-close"
            onClick={() => setPurchaseDialogOpen(false)}
            title="Close"
            type="button"
          >
            <X size={20} />
          </button>
          <div className="purchase-heading">
            <div className="coach-mark">
              <Sparkles size={22} />
            </div>
            <div>
              <p className="eyebrow">Coach access</p>
              <h2>Add Credits or Go Unlimited</h2>
            </div>
          </div>

          <div className="purchase-balance">
            <span>
              {coachCredits?.unlimited
                ? 'Unlimited Coach active'
                : `${coachCredits?.balance ?? 0} credits available`}
            </span>
            <small>
              Windows uses Microsoft Store checkout. Android uses Google Play.
            </small>
          </div>

          <div className="purchase-products">
            {coachCreditProducts.map((product) => (
              <article
                className={
                  product.kind === 'subscription'
                    ? 'purchase-product subscription'
                    : 'purchase-product'
                }
                key={product.id}
              >
                <div className="purchase-product-heading">
                  <div>
                    <h3>{product.title}</h3>
                    <p>{product.subtitle}</p>
                  </div>
                  {product.badge && <span>{product.badge}</span>}
                </div>
                <div className="purchase-product-footer">
                  <strong>{product.price}</strong>
                  <button
                    className={product.kind === 'subscription' ? 'primary' : 'secondary'}
                    onClick={() => void beginCoachPurchase(product)}
                    type="button"
                  >
                    {product.kind === 'subscription' ? (
                      <Crown size={17} />
                    ) : (
                      <CreditCard size={17} />
                    )}
                    {product.kind === 'subscription' ? 'Choose Plan' : 'Buy Credits'}
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="purchase-costs">
            <span>Ask 1</span>
            <span>Review 2</span>
            <span>Combos 3</span>
            <span>Improve 3</span>
            <span>Build 8</span>
          </div>

          {purchaseStatus && (
            <p className="account-status purchase-status">{purchaseStatus}</p>
          )}
        </section>
      </div>
    )
  }

  performAccountSyncRef.current = performAccountSync
  const scanPreviewIndex =
    previewCard && activeTab === 'scan'
      ? candidates.findIndex(
          (candidate) =>
            candidate.game === previewCard.game &&
            candidate.sourceId === previewCard.sourceId,
        )
      : -1
  const hasScanPreviewNavigation = scanPreviewIndex >= 0 && candidates.length > 1
  const visibleAppVersion =
    isDesktopShell && window.cardVaultDesktop?.version
      ? window.cardVaultDesktop.version
      : appVersionLabel
  return (
    <main className={isDesktopShell ? 'app-shell desktop-shell' : 'app-shell'}>
      <header className="topbar">
        <div className="brand-heading">
          <img alt="" className="brand-icon" src="./card-vault-icon.png" />
          <div>
            <p className="eyebrow">MTG + Pokemon + Sports collection tracker</p>
            <div className="brand-title-row">
              <h1>Card Vault</h1>
              <span className="brand-badge">Early Access</span>
              <span className="brand-badge version-badge">v{visibleAppVersion}</span>
            </div>
          </div>
        </div>
        <div className="topbar-actions">
          {isDesktopShell && (
            <label className="skin-picker">
              <span>Skin</span>
              <select
                aria-label="Desktop skin"
                onChange={(event) => setCardVaultSkin(event.target.value as CardVaultSkin)}
                value={cardVaultSkin}
              >
                {cardVaultSkins.map((skin) => (
                  <option key={skin.value} value={skin.value}>
                    {skin.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isDesktopShell && (
            <button
              className="account-button feedback-button"
              onClick={() => setFeedbackDialogOpen(true)}
              title="Send feedback"
              type="button"
            >
              <MessageSquare size={18} />
              <span>Feedback</span>
            </button>
          )}
          <button
            className="account-button"
            onClick={() => setAccountDialogOpen(true)}
            title={accountSession ? 'Profile and sync' : 'Sign in'}
            type="button"
          >
            {accountSession ? <Cloud size={18} /> : <UserRound size={18} />}
            <span>
              {accountSession?.profile.displayName ?? 'Profile'}
            </span>
          </button>
        </div>
      </header>

      <div className="app-body">
      <nav className="tabbar" aria-label="Main views">
        <button
          className={activeTab === 'scan' ? 'active' : ''}
          onClick={() => setActiveTab('scan')}
          type="button"
        >
          <Camera size={18} />
          Scan
        </button>
        <button
          className={activeTab === 'mtg' ? 'active' : ''}
          onClick={() => setActiveTab('mtg')}
          type="button"
        >
          <Layers3 size={18} />
          MTG
        </button>
        <button
          className={activeTab === 'pokemon' ? 'active' : ''}
          onClick={() => setActiveTab('pokemon')}
          type="button"
        >
          <Database size={18} />
          Pokemon
        </button>
        <button
          className={activeTab === 'sports' ? 'active' : ''}
          onClick={() => setActiveTab('sports')}
          type="button"
        >
          <Trophy size={18} />
          Sports
        </button>
      </nav>

      <div className="app-content">

      {activeTab === 'scan' && (
        <section className="workspace scan-workspace">
        <div className="tool-panel">
          <div className="panel-heading">
            <h2>Scan or Search</h2>
          </div>

          <div className="scanner-station">
          <div className="segmented" aria-label="Card game">
            {gameFilters.map((choice) => (
              <button
                className={game === choice ? 'active' : ''}
                key={choice}
                onClick={() => setGame(choice)}
                type="button"
              >
                {getGameLabel(choice)}
              </button>
            ))}
          </div>
          <label className="scan-game-picker">
            <span>Scan as</span>
            <select
              aria-label="Card game"
              onChange={(event) => setGame(event.target.value as GameFilter)}
              value={game}
            >
              {gameFilters.map((choice) => (
                <option key={choice} value={choice}>
                  {getGameLabel(choice)}
                </option>
              ))}
            </select>
          </label>

          <div className="camera-frame">
            <video
              className={[
                'camera-video',
                isCameraOn ? 'active' : '',
              ].filter(Boolean).join(' ')}
              ref={videoRef}
              autoPlay
              muted
              playsInline
            />
            {!isCameraOn && capturedFrame && (
              <img alt="" className="captured-frame" src={capturedFrame} />
            )}
            {!isCameraOn && (
              <div className={capturedFrame ? 'camera-empty hidden-empty' : 'camera-empty'}>
                <img alt="" src="./card-vault-icon.png" />
                <span>Camera ready</span>
              </div>
            )}
            <div className="scan-guide" aria-hidden="true" />
          </div>
          <div className="scan-border-hint">
            <ScanLine aria-hidden="true" size={17} />
            <span>
              {game === 'sports'
                ? 'Photograph the front only. Keep the full card or graded slab and label visible.'
                : 'Keep the full black card border visible on all four sides.'}
            </span>
          </div>
          <canvas ref={canvasRef} className="hidden-canvas" />

          <div className="button-row">
            <button
              className="secondary"
              disabled={isScanning}
              onClick={startCamera}
              type="button"
            >
              {isCameraOn ? <X size={18} /> : <Camera size={18} />}
              {isCameraOn ? 'Stop' : 'Camera'}
            </button>
            <button
              className="primary"
              disabled={!isCameraOn || isScanning || scannerState === 'capturing'}
              onClick={() => void captureCard()}
              type="button"
            >
              {isScanning ? <Loader2 className="spin" size={18} /> : <Search size={18} />}
              Capture
            </button>
          </div>
          <div className="scanner-status" aria-live="polite">
            {status}
          </div>
          <button
            className="report-scan-button"
            disabled={!capturedFrame || Boolean(scanIssueDraft)}
            onClick={openScanIssueReport}
            type="button"
          >
            <Send size={18} />
            Report Scan Issue
          </button>
          {!feedbackDraft.dismissed && (
            <section className="feedback-nudge" aria-label="Quick feedback">
              <div>
                <p className="feedback-nudge-title">Quick feedback?</p>
                <p className="feedback-nudge-copy">Pick a category, add a note, then send.</p>
              </div>
              <textarea
                aria-invalid={Boolean(feedbackDraft.error)}
                onChange={(event) =>
                  setFeedbackDraft((current) => ({
                    ...current,
                    error: '',
                    note: event.target.value,
                  }))
                }
                placeholder="Enter feedback note"
                rows={2}
                value={feedbackDraft.note}
              />
              {feedbackDraft.error ? (
                <p className="feedback-nudge-error" role="alert">
                  {feedbackDraft.error}
                </p>
              ) : null}
              <div className="feedback-nudge-actions">
                {['Worked well', 'Confusing', 'Broke'].map((choice) => (
                  <button
                    className={feedbackDraft.choice === choice ? 'selected' : ''}
                    disabled={feedbackDraft.isSaving}
                    key={choice}
                    onClick={() => {
                      setFeedbackDraft((current) => ({
                        ...current,
                        choice,
                        error: '',
                      }))
                    }}
                    type="button"
                  >
                    {choice === 'Worked well' ? 'Worked' : choice}
                  </button>
                ))}
                <button
                  disabled={feedbackDraft.isSaving}
                  onClick={() => void sendFeedbackNote()}
                  type="button"
                >
                  {feedbackDraft.isSaving ? 'Sending' : 'Send'}
                </button>
                <button
                  disabled={feedbackDraft.isSaving}
                  onClick={dismissFeedbackNudge}
                  type="button"
                >
                  No thanks
                </button>
              </div>
            </section>
          )}
          </div>

          <form
            className="search-form"
            onSubmit={(event) => {
              event.preventDefault()
              void runSearchAndScroll()
            }}
          >
            <label>
              Card name
              <input
                autoComplete="off"
                onChange={(event) => setQuery(event.target.value)}
                onClick={(event) => selectInputContents(event.currentTarget)}
                onFocus={(event) => selectInputContents(event.currentTarget)}
                placeholder={
                  game === 'sports'
                    ? 'Player, year, set, card number...'
                    : 'Lightning Bolt, Charizard...'
                }
                value={query}
              />
            </label>
            <button disabled={isSearching} type="submit">
              {isSearching ? <Loader2 className="spin" size={18} /> : <Search size={18} />}
              Search
            </button>
          </form>

          {game === 'sports' && (
            <button className="secondary" type="button" onClick={() => setPreviewCard({
              sourceId: createLocalSportsSourceId(), game: 'sports', name: query.trim() || 'Unidentified Sports Card',
              setName: '', setCode: '', collectorNumber: '', rarity: 'Unconfirmed', colors: [], category: 'Sports',
              imageUrl: '', priceUsd: null, priceLabel: 'Value unavailable', sourceUrl: '',
              updatedAt: new Date().toISOString(), sport: 'Sports', sportsPriceStatus: 'unavailable',
            })}>Enter a sports card manually</button>
          )}

          {scanResult && (
            <div className="scan-output">
              <div className="scan-output-header">
                <span>OCR terms</span>
                <span>{selectedTerm || 'No search yet'}</span>
              </div>
              <div className="term-list">
                {scanResult.terms.map((term) => (
                  <button
                    key={term}
                    onClick={() => {
                      setQuery(term)
                      void runSearchAndScroll(term)
                    }}
                    type="button"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div
            ref={scanResultsRef}
            className={`candidate-list scan-results${
              candidates.length === 0 ? ' empty' : ''
            }`}
          >
            {candidates.length === 0 && (
              <div className="scan-results-empty" aria-hidden="true">
                <img alt="" src="./card-vault-icon.png" />
              </div>
            )}
            {candidates.map((candidate) => (
              <article
                className="candidate"
                key={`${candidate.game}-${candidate.sourceId}`}
                onClick={() => setPreviewCard(candidate)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setPreviewCard(candidate)
                  }
                }}
                role="button"
                tabIndex={0}
                title={`Open ${candidate.name}`}
              >
                {renderCardImage(candidate)}
                <div className="candidate-main">
                  <h3>{candidate.name}</h3>
                  <p className="card-meta">
                    {getGameLabel(candidate.game)} · {candidate.setName} #
                    {candidate.collectorNumber}
                  </p>
                  <p>
                    {candidate.rarity} - {candidate.priceLabel}
                  </p>
                </div>
                <button
                  aria-label={`Save ${candidate.name}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    openSaveDeckDialog(candidate)
                  }}
                  title="Save card"
                  type="button"
                >
                  <Save size={18} />
                </button>
                <button
                  aria-label={`Add ${candidate.name} to wishlist`}
                  onClick={(event) => {
                    event.stopPropagation()
                    void saveCandidateToWishlist(candidate)
                  }}
                  title="Add to wishlist"
                  type="button"
                >
                  <Heart size={18} />
                </button>
              </article>
            ))}
          </div>

          {(candidates.length > 0 || selectedTerm) && (
            <div className="more-results-pull">
              <button
                className="secondary"
                disabled={isRefreshingResults || isSearching || !hasMoreResults}
                onClick={() => void loadNextResultBatch()}
                type="button"
              >
                {isRefreshingResults ? (
                  <Loader2 className="spin" size={18} />
                ) : (
                  <RefreshCw size={18} />
                )}
                {hasMoreResults ? 'More Matches' : 'No More Matches'}
              </button>
              <span>Show the next batch of card options for this search.</span>
            </div>
          )}
        </div>
        </section>
      )}

      {activeTab === 'mtg' && renderGameTab('mtg')}

      {activeTab === 'pokemon' && renderGameTab('pokemon')}

      {activeTab === 'sports' && renderGameTab('sports')}

      {activeTab !== 'scan' && (
        <footer className="bottom-summary">
          <div className="stats-strip" aria-label="Collection summary">
            <span>
              <strong>{activeGameTotals.cards}</strong> cards
            </span>
            <span>
              <strong>{formatMoney(activeGameTotals.value)}</strong> value
            </span>
          </div>
        </footer>
      )}
      </div>
      </div>

      {saveNotice && (
        <div className="save-toast" role="status" aria-live="polite">
          <Save size={18} />
          {saveNotice}
        </div>
      )}

      {editDraft && (
        <div
          aria-modal="true"
          className="card-preview-backdrop"
          onClick={() => setEditDraft(null)}
          role="dialog"
        >
          <form
            className="edit-popout"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault()
              void saveEditDraft()
            }}
          >
            <button
              aria-label="Close editor"
              className="preview-close"
              onClick={() => setEditDraft(null)}
              title="Close"
              type="button"
            >
              <X size={20} />
            </button>
            <div>
              <h2>Edit Card</h2>
              <p className="muted">{editDraft.name}</p>
            </div>

            <div className="edit-grid">
              <label className="wide-field">
                Card name
                <input
                  onChange={(event) => updateEditDraft('name', event.target.value)}
                  value={editDraft.name}
                />
              </label>
              <label>
                Game
                <select
                  onChange={(event) =>
                    updateEditDraft('game', event.target.value as GameFilter)
                  }
                  value={editDraft.game}
                >
                  <option value="mtg">MTG</option>
                  <option value="pokemon">Pokemon</option>
                  <option value="sports">Sports</option>
                </select>
              </label>
              <label>
                Saved list
                <select
                  onChange={(event) =>
                    updateEditDraft(
                      'listKind',
                      event.target.value as CollectionListKind,
                    )
                  }
                  value={editDraft.listKind}
                >
                  <option value="collection">Collection</option>
                  <option value="wishlist">Wishlist</option>
                </select>
              </label>
              <label>
                Quantity
                <input
                  min="1"
                  onChange={(event) => updateEditDraft('quantity', event.target.value)}
                  type="number"
                  value={editDraft.quantity}
                />
              </label>
              <label>
                Condition
                <select
                  onChange={(event) =>
                    updateEditDraft('condition', event.target.value as Condition)
                  }
                  value={editDraft.condition}
                >
                  {conditions.map((entry) => (
                    <option key={entry}>{entry}</option>
                  ))}
                </select>
              </label>
              <label>
                Set name
                <input
                  onChange={(event) => updateEditDraft('setName', event.target.value)}
                  value={editDraft.setName}
                />
              </label>
              <label>
                Set code
                <input
                  onChange={(event) => updateEditDraft('setCode', event.target.value)}
                  value={editDraft.setCode}
                />
              </label>
              <label>
                Card number
                <input
                  onChange={(event) =>
                    updateEditDraft('collectorNumber', event.target.value)
                  }
                  value={editDraft.collectorNumber}
                />
              </label>
              <label>
                Rarity
                <input
                  onChange={(event) => updateEditDraft('rarity', event.target.value)}
                  value={editDraft.rarity}
                />
              </label>
              <label>
                Type / Category
                <input
                  onChange={(event) => updateEditDraft('category', event.target.value)}
                  value={editDraft.category}
                />
              </label>
              <label>
                Colors / Types
                <input
                  onChange={(event) => updateEditDraft('colors', event.target.value)}
                  placeholder="B, R or Lightning"
                  value={editDraft.colors}
                />
              </label>
              <label>
                Price USD
                <input
                  onChange={(event) => updateEditDraft('priceUsd', event.target.value)}
                  step="0.01"
                  type="number"
                  value={editDraft.priceUsd}
                />
              </label>
              <label>
                Price label
                <input
                  onChange={(event) => updateEditDraft('priceLabel', event.target.value)}
                  value={editDraft.priceLabel}
                />
              </label>
              <label className="wide-field">
                Notes
                <input
                  onChange={(event) => updateEditDraft('notes', event.target.value)}
                  placeholder="binder, deck, graded..."
                  value={editDraft.notes}
                />
              </label>
              {editDraft.game === 'sports' && (
                <>
                  <label>
                    Sport
                    <input
                      onChange={(event) => updateEditDraft('sport', event.target.value)}
                      value={editDraft.sport}
                    />
                  </label>
                  <label>
                    Manufacturer
                    <input
                      onChange={(event) =>
                        updateEditDraft('manufacturer', event.target.value)
                      }
                      value={editDraft.manufacturer}
                    />
                  </label>
                  <label>
                    Release / Product
                    <input
                      onChange={(event) =>
                        updateEditDraft('releaseName', event.target.value)
                      }
                      value={editDraft.releaseName}
                    />
                  </label>
                  <label>
                    Year
                    <input
                      onChange={(event) =>
                        updateEditDraft('releaseYear', event.target.value)
                      }
                      value={editDraft.releaseYear}
                    />
                  </label>
                  <label>
                    Parallel
                    <input
                      onChange={(event) =>
                        updateEditDraft('parallelName', event.target.value)
                      }
                      value={editDraft.parallelName}
                    />
                  </label>
                  <label>
                    Numbered to
                    <input
                      min="1"
                      onChange={(event) =>
                        updateEditDraft('numberedTo', event.target.value)
                      }
                      type="number"
                      value={editDraft.numberedTo}
                    />
                  </label>
                  <label>
                    Grading company
                    <input
                      onChange={(event) =>
                        updateEditDraft('gradingCompany', event.target.value)
                      }
                      placeholder="PSA, BGS, SGC..."
                      value={editDraft.gradingCompany}
                    />
                  </label>
                  <label>
                    Grade
                    <input
                      onChange={(event) => updateEditDraft('grade', event.target.value)}
                      placeholder="10, 9.5..."
                      value={editDraft.grade}
                    />
                  </label>
                  <label>
                    Certification number
                    <input
                      onChange={(event) =>
                        updateEditDraft('certificationNumber', event.target.value)
                      }
                      value={editDraft.certificationNumber}
                    />
                  </label>
                  <label>
                    Grade qualifier
                    <input
                      onChange={(event) =>
                        updateEditDraft('gradeQualifier', event.target.value)
                      }
                      placeholder="OC, MC, PD..."
                      value={editDraft.gradeQualifier}
                    />
                  </label>
                  <label>
                    Autograph grade
                    <input
                      onChange={(event) =>
                        updateEditDraft('autographGrade', event.target.value)
                      }
                      value={editDraft.autographGrade}
                    />
                  </label>
                  <label className="wide-field">
                    CardSight parallel ID
                    <input
                      onChange={(event) =>
                        updateEditDraft('parallelId', event.target.value)
                      }
                      value={editDraft.parallelId}
                    />
                  </label>
                </>
              )}
              <label className="wide-field">
                Image URL
                <input
                  onChange={(event) => updateEditDraft('imageUrl', event.target.value)}
                  value={editDraft.imageUrl}
                />
              </label>
              <label className="wide-field">
                Source URL
                <input
                  onChange={(event) => updateEditDraft('sourceUrl', event.target.value)}
                  value={editDraft.sourceUrl}
                />
              </label>
              <label>
                Source ID
                <input
                  onChange={(event) => updateEditDraft('sourceId', event.target.value)}
                  value={editDraft.sourceId}
                />
              </label>
              <label>
                Updated at
                <input
                  onChange={(event) => updateEditDraft('updatedAt', event.target.value)}
                  value={editDraft.updatedAt}
                />
              </label>
              <label>
                Added at
                <input
                  onChange={(event) => updateEditDraft('addedAt', event.target.value)}
                  value={editDraft.addedAt}
                />
              </label>
              <label>
                Last price check
                <input
                  onChange={(event) =>
                    updateEditDraft('lastPriceCheck', event.target.value)
                  }
                  value={editDraft.lastPriceCheck}
                />
              </label>
            </div>

            <div className="edit-actions">
              <button
                className="secondary"
                onClick={() => setEditDraft(null)}
                type="button"
              >
                Cancel
              </button>
              <button className="primary" type="submit">
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {deckBuilderGame && renderDeckBuilder(deckBuilderGame)}
      {renderDeckDeleteDialog()}

      {renderCoachDialog()}

      {previewCard && (
        <div
          aria-modal="true"
          className={`card-preview-backdrop ${
            coachDraft ? 'coach-card-preview' : ''
          }`}
          onClick={() => {
            setPreviewMenuOpen(false)
            setPreviewCard(null)
          }}
          role="dialog"
        >
          <section
            className="card-preview"
            onClick={(event) => event.stopPropagation()}
            onTouchEnd={handlePreviewTouchEnd}
            onTouchStart={handlePreviewTouchStart}
          >
            <button
              aria-label="Close card preview"
              className="preview-close"
              onClick={() => {
                setPreviewMenuOpen(false)
                setPreviewCard(null)
              }}
              title="Close"
              type="button"
            >
              <X size={20} />
            </button>
            <div
              className={`preview-menu ${previewCard.game === 'sports' ? 'sports-hidden' : ''}`}
            >
              <button
                aria-expanded={previewMenuOpen}
                aria-haspopup="menu"
                aria-label="Card actions"
                className="preview-menu-trigger"
                onClick={() => setPreviewMenuOpen((current) => !current)}
                title="Card actions"
                type="button"
              >
                <MoreVertical size={20} />
              </button>
              {previewMenuOpen && previewCard.game !== 'sports' && (
                <div className="preview-menu-popover" role="menu">
                  <button
                    onClick={() => openPreviewDeckAction(false)}
                    role="menuitem"
                    type="button"
                  >
                    <Layers3 size={18} />
                    Add to deck
                  </button>
                  <button
                    onClick={() => openPreviewDeckAction(true)}
                    role="menuitem"
                    type="button"
                  >
                    <Plus size={18} />
                    New deck
                  </button>
                </div>
              )}
            </div>
            <div className="preview-image-shell">
              {hasScanPreviewNavigation && (
                <button
                  aria-label="Previous card"
                  className="preview-navigation previous"
                  disabled={scanPreviewIndex <= 0}
                  onClick={() => navigateScanPreview(-1)}
                  title="Previous card"
                  type="button"
                >
                  <ChevronLeft size={24} />
                </button>
              )}
              {previewCard.imageUrl && !failedImages.has(getImageKey(previewCard)) ? (
                <img
                  alt=""
                  className="preview-image"
                  onError={() => markImageFailed(previewCard)}
                  src={previewCard.imageUrl}
                />
              ) : (
                <div className="preview-image missing">
                </div>
              )}
              {hasScanPreviewNavigation && (
                <button
                  aria-label="Next card"
                  className="preview-navigation next"
                  disabled={scanPreviewIndex >= candidates.length - 1}
                  onClick={() => navigateScanPreview(1)}
                  title="Next card"
                  type="button"
                >
                  <ChevronRight size={24} />
                </button>
              )}
            </div>
            <div className="preview-details">
              <h2>{previewCard.name}</h2>
              <p className="card-meta">
                {getGameLabel(previewCard.game)} · {previewCard.setName} #
                {previewCard.collectorNumber}
              </p>
              <p>
                {previewCard.rarity} - {previewCard.priceLabel}
              </p>
              {previewCard.game === 'sports' && (
                <SportsDetailsEditor card={previewCard} onChange={setPreviewCard} />
              )}
              {previewCard.game === 'sports' && (
                <div className="sports-card-details">
                  <span>{previewCard.sport || 'Sports'}</span>
                  {previewCard.releaseYear && <span>{previewCard.releaseYear}</span>}
                  {previewCard.manufacturer && <span>{previewCard.manufacturer}</span>}
                  {previewCard.parallelName && <span>{previewCard.parallelName}</span>}
                  {previewCard.numberedTo && <span>/{previewCard.numberedTo}</span>}
                  {previewCard.gradingCompany && (
                    <span>
                      {previewCard.gradingCompany} {previewCard.grade}
                    </span>
                  )}
                  {previewCard.certificationNumber && (
                    <span>Cert {previewCard.certificationNumber}</span>
                  )}
                </div>
              )}
              {previewCard.game === 'sports' &&
                previewCard.sportsPriceComparables?.length ? (
                  <details className="sports-price-details">
                    <summary>
                      {previewCard.sportsPriceComparables.length} completed-sale source
                      {previewCard.sportsPriceComparables.length === 1 ? '' : 's'}
                    </summary>
                    <div>
                      {previewCard.sportsPriceComparables.map((comparable) => (
                        <a
                          href={comparable.url || undefined}
                          key={`${comparable.source}-${comparable.date}-${comparable.price}`}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <span>{comparable.source}</span>
                          <strong>{formatMoney(comparable.price)}</strong>
                          <small>{comparable.date ? formatDate(comparable.date) : ''}</small>
                        </a>
                      ))}
                    </div>
                  </details>
                ) : null}
              <div className="preview-actions">
                <button
                  className="primary"
                  onClick={() => openSaveDeckDialog(previewCard)}
                  type="button"
                >
                  <Save size={18} />
                  Add to Library
                </button>
                <button
                  className="secondary"
                  onClick={() => void saveCandidateToWishlist(previewCard)}
                  type="button"
                >
                  <Heart size={18} />
                  Wishlist
                </button>
                {previewCard.sourceUrl && (
                  <a href={previewCard.sourceUrl} rel="noreferrer" target="_blank">
                    <ExternalLink size={18} />
                    {previewCard.game === 'sports' &&
                    previewCard.sourceUrl.includes('ebay.com')
                      ? 'View eBay listing'
                      : 'Source'}
                  </a>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      {renderSaveDeckDialog()}

      {renderScanIssueDialog()}

      {renderAccountDialog()}

      {renderFeedbackDialog()}

      {renderPurchaseDialog()}

    </main>
  )
}

function getDefaultCoachFormat(game: GameFilter): string {
  return game === 'pokemon' ? 'standard' : 'casual'
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getCoachMessageContext(message: CoachChatMessage): string {
  if (!message.result) {
    return message.text
  }

  const cardNames = uniqueStrings([
    ...message.result.synergies.flatMap((synergy) => synergy.cards),
    ...message.result.ownedRecommendations.flatMap((recommendation) =>
      recommendation.add.map((change) => change.name),
    ),
  ])

  return [message.text, cardNames.length ? `Cards discussed: ${cardNames.join(', ')}` : '']
    .filter(Boolean)
    .join('\n')
}

function countCoachAdditions(result: CoachResult): number {
  return result.ownedRecommendations.reduce(
    (total, recommendation) =>
      total + recommendation.add.reduce((sum, change) => sum + change.quantity, 0),
    0,
  )
}

function isFullDeckBuildRequest(question: string, mode: CoachMode): boolean {
  if (mode === 'build') {
    return true
  }
  const normalized = normalizeName(question)
  return (
    /\b(?:build|create|make|construct|put together)\b.{0,31}\bdeck\b/.test(
      normalized,
    ) ||
    /\bdeck\b.{0,31}\b(?:build|create|make|construct|put together)\b/.test(
      normalized,
    )
  )
}

function getCoachComboColorOptions(
  game: GameFilter,
): Array<{ label: string; value: string }> {
  if (game === 'pokemon') {
    return [
      { label: 'Grass', value: 'grass' },
      { label: 'Fire', value: 'fire' },
      { label: 'Water', value: 'water' },
      { label: 'Lightning', value: 'lightning' },
      { label: 'Psychic', value: 'psychic' },
      { label: 'Fighting', value: 'fighting' },
      { label: 'Darkness', value: 'darkness' },
      { label: 'Metal', value: 'metal' },
      { label: 'Dragon', value: 'dragon' },
      { label: 'Colorless', value: 'colorless' },
    ]
  }

  return [
    { label: 'White', value: 'white' },
    { label: 'Blue', value: 'blue' },
    { label: 'Black', value: 'black' },
    { label: 'Red', value: 'red' },
    { label: 'Green', value: 'green' },
    { label: 'Multicolor', value: 'multicolor' },
    { label: 'Colorless', value: 'colorless' },
  ]
}

function getCoachComboKinds(): Array<{
  label: string
  value: CoachComboKind
}> {
  return [
    { label: 'General synergy', value: 'synergy' },
    { label: 'Two-card combo', value: 'two-card' },
    { label: 'Value engine', value: 'engine' },
    { label: 'Win condition', value: 'win' },
  ]
}

function getCoachBuildColorOptions(
  game: GameFilter,
): Array<{ label: string; value: string }> {
  const options = getCoachComboColorOptions(game)
  if (game !== 'mtg') {
    return options
  }

  const colorlessIndex = options.findIndex((option) => option.value === 'colorless')
  const artifactOption = { label: 'Artifact', value: 'artifact' }
  if (colorlessIndex < 0) {
    return [...options, artifactOption]
  }

  return [
    ...options.slice(0, colorlessIndex),
    artifactOption,
    ...options.slice(colorlessIndex),
  ]
}

function buildCoachComboQuestion(
  game: GameFilter,
  refinement: CoachComboRefinement,
): string {
  const kindLabels: Record<CoachComboKind, string> = {
    synergy: 'useful synergies',
    'two-card': 'two-card combinations',
    engine: 'repeatable value engines',
    win: 'win-condition combinations',
  }
  const colorList = refinement.colors.join(' and ')
  const colorPhrase = colorList
    ? game === 'pokemon'
      ? ` built around ${colorList} Pokemon`
      : ` in ${colorList}`
    : ''
  const areaPhrase =
    refinement.area === 'deck'
      ? ' for this deck'
      : ' across my whole collection'

  return (
    `Find the strongest ${kindLabels[refinement.kind]}${colorPhrase}${areaPhrase} ` +
    'using only cards I own. Explain how each one works in plain language.'
  )
}

function getCoachBuildStyles(
  game: GameFilter,
): Array<{ label: string; value: CoachBuildStyle }> {
  if (game === 'pokemon') {
    return [
      { label: 'Balanced', value: 'balanced' },
      { label: 'Fast attacks', value: 'aggressive' },
      { label: 'Ability engine', value: 'combo' },
      { label: 'Evolution focused', value: 'creatures' },
      { label: 'Control', value: 'control' },
    ]
  }

  return [
    { label: 'Balanced', value: 'balanced' },
    { label: 'Aggressive', value: 'aggressive' },
    { label: 'Control', value: 'control' },
    { label: 'Creature focused', value: 'creatures' },
    { label: 'Artifact focused', value: 'artifacts' },
    { label: 'Combo focused', value: 'combo' },
  ]
}

function buildCoachDeckQuestion(
  game: GameFilter,
  refinement: CoachBuildRefinement,
): string {
  const styleLabels: Record<CoachBuildStyle, string> = {
    balanced: 'balanced',
    aggressive: game === 'pokemon' ? 'fast-attacking' : 'aggressive',
    control: 'control',
    creatures: game === 'pokemon' ? 'evolution-focused' : 'creature-focused',
    artifacts: 'artifact-focused',
    combo: game === 'pokemon' ? 'Ability-engine' : 'combo-focused',
  }
  const wantsArtifacts = game === 'mtg' && refinement.colors.includes('artifact')
  const colorList = refinement.colors
    .filter((color) => color !== 'artifact')
    .join(' and ')
  const colorPhrase = colorList
    ? game === 'pokemon'
      ? ` using ${colorList} Pokemon types`
      : ` using ${colorList} cards`
    : ''
  const artifactPhrase =
    wantsArtifacts && refinement.style !== 'artifacts'
      ? ' with an artifact-focused core'
      : ''

  return (
    `Build me a full ${styleLabels[refinement.style]} ${getGameLabel(game)} deck` +
    `${colorPhrase}${artifactPhrase} using only cards I own. Include the complete nonland core and quantities.`
  )
}

function getCoachQuickPrompts(
  game: GameFilter,
  scope: CoachScope,
): CoachQuickPrompt[] {
  if (game === 'pokemon') {
    return scope === 'deck'
      ? [
          {
            label: 'Improve this deck',
            mode: 'improve',
            question:
              'Which Pokemon cards I own would improve this deck, and what should I replace?',
          },
          {
            label: 'Check Energy',
            mode: 'ask',
            question:
              'Does this deck have the right Energy for its attack costs?',
          },
          {
            label: 'Find an ability',
            mode: 'ask',
            question:
              'Find Pokemon I own with attacks that cause Poison.',
          },
          {
            label: 'Show owned combos',
            mode: 'combos',
            question: 'What useful card combinations do I own for this deck?',
          },
        ]
      : [
          {
            label: 'Find Pokemon',
            mode: 'ask',
            question:
              'Find Pokemon I own with an Ability.',
          },
          {
            label: 'Show owned combos',
            mode: 'combos',
            question: 'What useful Pokemon card combinations do I own?',
          },
          {
            label: 'Build a deck',
            mode: 'build',
            question:
              'What deck would you build using only the cards I own?',
          },
          {
            label: 'Check my Energy',
            mode: 'ask',
            question:
              'What Energy cards do I own, and which Pokemon can use them?',
          },
        ]
  }

  return scope === 'deck'
    ? [
        {
          label: 'Improve this deck',
          mode: 'improve',
          question:
            'Which MTG cards I own would improve this deck, and what should I replace?',
        },
        {
          label: 'Check my mana',
          mode: 'ask',
          question:
            'Does this deck have enough lands and the right colors of mana?',
        },
        {
          label: 'Find a creature',
          mode: 'ask',
          question:
            'Find creatures I own with flying that could help this deck.',
        },
        {
          label: 'Show owned combos',
          mode: 'combos',
          question: 'What useful card combinations do I own for this deck?',
        },
      ]
    : [
        {
          label: 'Find answers to threats',
          mode: 'ask',
          question:
            'Which removal spells do I own, and what can each one answer?',
        },
        {
          label: 'Show owned combos',
          mode: 'combos',
          question: 'What useful MTG card combinations do I own?',
        },
        {
          label: 'Build a deck',
          mode: 'build',
          question:
            'What deck would you build using only the cards I own?',
        },
        {
          label: 'Check my mana',
          mode: 'ask',
          question:
            'What lands and mana-producing cards do I own by color?',
        },
      ]
}

function getCoachFormats(
  game: GameFilter,
): Array<{ value: string; label: string }> {
  if (game === 'pokemon') {
    return [
      { value: 'standard', label: 'Standard' },
      { value: 'expanded', label: 'Expanded' },
      { value: 'casual', label: 'Casual' },
    ]
  }

  return [
    { value: 'casual', label: 'Casual' },
    { value: 'standard', label: 'Standard' },
    { value: 'pioneer', label: 'Pioneer' },
    { value: 'modern', label: 'Modern' },
    { value: 'commander', label: 'Commander' },
    { value: 'pauper', label: 'Pauper' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'vintage', label: 'Vintage' },
  ]
}

function formatMetricLabel(value: string): string {
  const spaced = value.replace(/([a-z])([A-Z])/g, '$1 $2')
  return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1).toLowerCase()}`
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>()

  return values.filter((value) => {
    const key = value.trim().toLowerCase()

    if (!key || seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

function normalizeCardName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function getImageKey(card: CardCandidate): string {
  return `${card.game}-${card.sourceId}`
}

function deprioritizeCandidate(
  candidates: CardCandidate[],
  rejectedSourceId: string,
): CardCandidate[] {
  const rejectedIndex = candidates.findIndex(
    (candidate) => candidate.sourceId === rejectedSourceId,
  )

  if (rejectedIndex < 0 || rejectedIndex === candidates.length - 1) {
    return candidates
  }

  return [
    ...candidates.filter((_, index) => index !== rejectedIndex),
    candidates[rejectedIndex],
  ]
}

async function submitScanIssueReport(
  report: ScanIssueReport,
  accountToken: string,
): Promise<ScanIssueSyncResponse> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), 7391)

  try {
    const response = await fetch(getApiUrl('/api/scan-issue-reports'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accountToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(report),
      signal: controller.signal,
    })

    const payload = (await response.json()) as ScanIssueSyncResponse

    if (!response.ok || !payload.ok) {
      throw new Error(payload.error || 'Could not send scan issue report.')
    }

    return payload
  } finally {
    window.clearTimeout(timeoutId)
  }
}

function getGameLabel(game: GameFilter): string {
  if (game === 'pokemon') {
    return 'Pokemon'
  }
  if (game === 'sports') {
    return 'Sports'
  }
  return 'MTG'
}

function namesLikelyMatch(term: string, cardName: string): boolean {
  const normalizedTerm = normalizeName(term)
  const normalizedName = normalizeName(cardName)
  const termWords = normalizedTerm.split(' ').filter(Boolean)
  const nameWords = normalizedName.split(' ').filter(Boolean)

  if (!normalizedTerm || !normalizedName) {
    return false
  }

  if (termWords.length === 1 && nameWords.length > 1) {
    return false
  }

  if (
    normalizedName === normalizedTerm ||
    normalizedName.includes(normalizedTerm) ||
    (normalizedTerm.length >= 6 && normalizedTerm.includes(normalizedName))
  ) {
    return true
  }

  const termWordSet = new Set(termWords)
  const sharedWords = nameWords.filter((word) => termWordSet.has(word)).length
  const wordCoverage = sharedWords / Math.max(termWords.length, nameWords.length)
  const similarity = similarityScore(normalizedTerm, normalizedName)
  const termSignificantWords = termWords.filter(
    (word) => !nameConnectorWords.has(word),
  )
  const nameSignificantWords = nameWords.filter(
    (word) => !nameConnectorWords.has(word),
  )
  const fuzzySharedWords = termSignificantWords.filter((termWord) =>
    nameSignificantWords.some(
      (nameWord) => similarityScore(termWord, nameWord) >= 0.78,
    ),
  ).length
  const significantWordCoverage =
    fuzzySharedWords /
    Math.max(termSignificantWords.length, nameSignificantWords.length, 1)

  return (
    (normalizedTerm.length >= 5 && similarity >= 0.72) ||
    (termWords.length >= 3 && wordCoverage >= 0.66 && similarity >= 0.58) ||
    (termSignificantWords.length >= 2 && significantWordCoverage >= 0.75)
  )
}

function isAutoTermWorthSearching(term: string, currentGame: GameFilter = 'mtg'): boolean {
  const normalized = normalizeName(term)
  const words = normalized.split(' ').filter(Boolean)
  const letters = normalized.replace(/[^a-z]/g, '')
  const maxWords = currentGame === 'pokemon' ? 4 : 9
  const maxLetters = currentGame === 'pokemon' ? 28 : 56

  if (letters.length < 4 || letters.length > maxLetters || words.length > maxWords) {
    return false
  }

  const shortWords = words.filter(
    (word, index) => word.length <= 1 && !isNameConnectorWord(word, index, words.length),
  )

  if (
    shortWords.length ||
    words.some(
      (word, index) =>
        word.length === 2 &&
        words.length > 1 &&
        !isNameConnectorWord(word, index, words.length),
    )
  ) {
    return false
  }

  if (/([a-z])\1{3,}/.test(letters)) {
    return false
  }

  return true
}

function expandOcrSearchTerms(term: string, currentGame: GameFilter = 'mtg'): string[] {
  const trimmed = term.trim()
  const variants = new Set<string>()

  if (!trimmed) {
    return []
  }

  if (currentGame === 'mtg') {
    const reportedCorrection = mtgReportedNameCorrections[normalizeName(trimmed)]

    if (reportedCorrection) {
      variants.add(reportedCorrection)
    }
  }

  if (currentGame === 'pokemon') {
    const reportedCorrection = pokemonReportedNameCorrections[normalizeName(trimmed)]
    const withoutBadge = stripPokemonBadgePrefix(trimmed)

    if (reportedCorrection) {
      variants.add(reportedCorrection)
    }

    if (withoutBadge) {
      variants.add(withoutBadge)
    }
  }

  const camelSpaced = trimmed.replace(/([a-z])([A-Z])/g, '$1 $2')
  variants.add(camelSpaced.replace(/^[Il](?=[A-Z][a-z]{3,}\s)/, ''))
  variants.add(trimmed.replace(/\bkevrune\b/gi, 'Keyrune'))
  variants.add(trimmed)
  variants.add(trimmed.replace(/[\u2018\u2019`\u00b4]/g, "'"))
  variants.add(trimmed.replace(/[\u2010-\u2015]/g, '-'))
  variants.add(trimmed.replace(/[,]/g, ' '))
  variants.add(trimmed.replace(/[-\u2010-\u2015]/g, ' '))
  variants.add(trimmed.replace(/[,]/g, '').replace(/[-\u2010-\u2015]/g, ' '))
  variants.add(trimmed.replace(/[^a-zA-Z0-9']+/g, ' '))
  variants.add(
    trimmed
      .replace(/\bof\s*the\b/gi, 'of the')
      .replace(/\b(?:ot|0f|ol)\s+(?:the|tne|tha)\b/gi, 'of the')
      .replace(/\b(?:tne|tha)\b/gi, 'the'),
  )
  variants.add(
    trimmed.replace(/\b([A-Za-z]{3,})\s+s\b(?=\s+[A-Za-z]{3,}\b)/g, "$1's"),
  )
  variants.add(trimmed.replace(/\b([A-Za-z]{4,})s\s+([A-Za-z]{3,})\b/g, "$1's $2"))
  variants.add(trimmed.replace(/'s\b/gi, 's'))
  variants.add(trimmed.replace(/'/g, ''))
  variants.add(
    trimmed.replace(
      /\b(?:a|an|and|in|of|on|or|the|to)\b/gi,
      ' ',
    ),
  )

  return Array.from(variants)
    .map((variant) => variant.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
}

function stripPokemonBadgePrefix(term: string): string {
  return term
    .replace(/[\u2018\u2019`\u00b4]/g, "'")
    .replace(
      /^(?:BASIC|BASIG|BASICG|BASlC|BASIO|BASIC0|BASlO|BASI|BAS1C|B4SIC)\s*/i,
      '',
    )
    .replace(/^(?:STAGE|STACE|STAGF|S7AGE|STAG|STAGL)\s*[12ilzlt]?\s*/i, '')
    .replace(/^(?:TRAINER|TRAlNER|TR4INER|TRAlNFR)\s+(?:ITEM|lTEM|ITEMS|I[tf]em)?\s*/i, '')
    .replace(/^(?:TRAINER|TRAlNER|TR4INER|TRAlNFR)(?=[A-Z][a-z])/i, '')
    .replace(/^(?:ITEM|lTEM|I[tf]em|Ttem|Stem|ltem)\s*/i, '')
    .replace(/^[A-Z]\s+(?=[A-Z][a-z]{2,}\b)/, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function isNameConnectorWord(word: string, index: number, wordCount: number): boolean {
  return index > 0 && index < wordCount - 1 && nameConnectorWords.has(word)
}

function similarityScore(left: string, right: string): number {
  const distance = levenshteinDistance(left, right)
  const longest = Math.max(left.length, right.length, 1)

  return 1 - distance / longest
}

function levenshteinDistance(left: string, right: string): number {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  const current = Array(right.length + 1).fill(0)

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    current[0] = leftIndex

    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + cost,
      )
    }

    for (let index = 0; index < previous.length; index += 1) {
      previous[index] = current[index]
    }
  }

  return previous[right.length]
}

function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[\u2018\u2019`\u00b4]/g, "'")
    .replace(/'s\b/g, 's')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function selectInputContents(input: HTMLInputElement) {
  if (!input.value) {
    return
  }

  window.setTimeout(() => {
    if (document.activeElement === input) {
      input.select()
    }
  }, 0)
}

function getCenteredCardCrop(videoWidth: number, videoHeight: number) {
  const cardAspect = 2.5 / 3.5
  let height = videoHeight
  let width = Math.round(height * cardAspect)

  if (width > videoWidth) {
    width = videoWidth
    height = Math.round(width / cardAspect)
  }

  return {
    x: Math.max(0, Math.round((videoWidth - width) / 2)),
    y: Math.max(0, Math.round((videoHeight - height) / 2)),
    width,
    height,
  }
}

async function drawCapturedCardToCanvas(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
) {
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Could not read the camera frame.')
  }

  const crop = getCenteredCardCrop(video.videoWidth, video.videoHeight)
  const maximumLongEdge = 2401
  const scale = Math.min(1, maximumLongEdge / Math.max(crop.width, crop.height))
  const outputWidth = Math.max(1, Math.round(crop.width * scale))
  const outputHeight = Math.max(1, Math.round(crop.height * scale))

  canvas.width = outputWidth
  canvas.height = outputHeight
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(
    video,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  )
}

function createOcrCanvas(source: HTMLCanvasElement) {
  const maximumLongEdge = 1401
  const scale = Math.min(1, maximumLongEdge / Math.max(source.width, source.height))
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Could not prepare the card image for reading.')
  }

  canvas.width = Math.max(1, Math.round(source.width * scale))
  canvas.height = Math.max(1, Math.round(source.height * scale))
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  return canvas
}

function createLocalSportsSourceId() {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `local-sports-${id}`
}

function createLocalSportsThumbnail(source: HTMLCanvasElement) {
  const maximumLongEdge = 560
  const scale = Math.min(1, maximumLongEdge / Math.max(source.width, source.height))
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) {
    return ''
  }
  canvas.width = Math.max(1, Math.round(source.width * scale))
  canvas.height = Math.max(1, Math.round(source.height * scale))
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  const image = canvas.toDataURL('image/jpeg', 0.78)
  canvas.width = 1
  canvas.height = 1
  return image
}

async function openHighDetailCameraStream(): Promise<MediaStream> {
  let stream = await requestHighDetailCameraStream()
  let preferredCamera: MediaDeviceInfo | undefined

  try {
    preferredCamera = await findPrimaryRearCamera()
  } catch {
    // Keep the working rear stream when Android does not expose camera labels.
  }

  const activeDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId

  if (preferredCamera?.deviceId && preferredCamera.deviceId !== activeDeviceId) {
    stream.getTracks().forEach((track) => track.stop())

    try {
      stream = await requestHighDetailCameraStream(preferredCamera.deviceId)
    } catch {
      stream = await requestHighDetailCameraStream()
    }
  }

  return stream
}

async function requestHighDetailCameraStream(deviceId?: string): Promise<MediaStream> {
  const cameraSelection = deviceId
    ? { deviceId: { exact: deviceId } }
    : { facingMode: { ideal: 'environment' } }
  const videoConstraints: MediaTrackConstraints & {
    resizeMode?: { ideal: string }
  } = {
    ...cameraSelection,
    width: { min: 1280, ideal: 3840 },
    height: { min: 720, ideal: 2160 },
    frameRate: { ideal: 24, max: 30 },
    resizeMode: { ideal: 'none' },
  }
  const highDetailConstraints: MediaStreamConstraints = {
    video: videoConstraints,
    audio: false,
  }

  try {
    return await navigator.mediaDevices.getUserMedia(highDetailConstraints)
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== 'OverconstrainedError') {
      throw error
    }

    return navigator.mediaDevices.getUserMedia({
      video: {
        ...cameraSelection,
        width: { ideal: 3840 },
        height: { ideal: 2160 },
      },
      audio: false,
    })
  }
}

async function findPrimaryRearCamera(): Promise<MediaDeviceInfo | undefined> {
  const devices = await navigator.mediaDevices.enumerateDevices()
  const rearCameras = devices.filter((device) => {
    if (device.kind !== 'videoinput') {
      return false
    }

    const label = device.label.toLowerCase()
    return (
      (label.includes('back') || label.includes('rear') || label.includes('environment')) &&
      !label.includes('front')
    )
  })

  return rearCameras.sort(
    (left, right) => scoreRearCamera(right.label) - scoreRearCamera(left.label),
  )[0]
}

function scoreRearCamera(label: string) {
  const normalized = label.toLowerCase()
  let score = 0

  if (normalized.includes('main') || normalized.includes('primary')) {
    score += 101
  }
  if (normalized.includes('back') || normalized.includes('rear')) {
    score += 51
  }
  if (/\bcamera2?\s*0\b/.test(normalized)) {
    score += 31
  }
  if (
    normalized.includes('ultra') ||
    normalized.includes('macro') ||
    normalized.includes('tele') ||
    normalized.includes('depth')
  ) {
    score -= 201
  }

  return score
}

async function optimizeCameraTrack(track: MediaStreamTrack | undefined) {
  if (!track) {
    return
  }

  track.contentHint = 'detail'

  const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
    exposureMode?: string[]
    focusMode?: string[]
    resizeMode?: string[]
    sharpness?: { max: number; min: number; step?: number }
    whiteBalanceMode?: string[]
  }
  const maximumWidth = Math.min(capabilities.width?.max ?? 3840, 3840)
  const maximumHeight = Math.min(capabilities.height?.max ?? 2160, 2160)
  const constraints: MediaTrackConstraints & { resizeMode?: { ideal: string } } = {
    width: {
      min: Math.min(maximumWidth, 1280),
      ideal: maximumWidth,
    },
    height: {
      min: Math.min(maximumHeight, 720),
      ideal: maximumHeight,
    },
    frameRate: { ideal: Math.min(capabilities.frameRate?.max ?? 24, 24), max: 30 },
  }

  if (capabilities.resizeMode?.includes('none')) {
    constraints.resizeMode = { ideal: 'none' }
  }

  try {
    await track.applyConstraints(constraints)
  } catch {
    // Keep the initial high-resolution constraints when this combination fails.
  }

  if (capabilities.focusMode?.includes('continuous')) {
    await applyOptionalCameraConstraint(track, { focusMode: 'continuous' })
  }
  if (capabilities.exposureMode?.includes('continuous')) {
    await applyOptionalCameraConstraint(track, { exposureMode: 'continuous' })
  }
  if (capabilities.whiteBalanceMode?.includes('continuous')) {
    await applyOptionalCameraConstraint(track, { whiteBalanceMode: 'continuous' })
  }
  if (
    capabilities.sharpness &&
    Number.isFinite(capabilities.sharpness.min) &&
    Number.isFinite(capabilities.sharpness.max) &&
    capabilities.sharpness.max > capabilities.sharpness.min
  ) {
    const sharpness =
      capabilities.sharpness.min +
      (capabilities.sharpness.max - capabilities.sharpness.min) * 0.85
    await applyOptionalCameraConstraint(track, { sharpness })
  }
}

async function prepareCameraFocus(track: MediaStreamTrack | undefined) {
  if (!track) {
    return
  }

  const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
    focusMode?: string[]
  }

  if (capabilities.focusMode?.includes('continuous')) {
    await applyOptionalCameraConstraint(track, { focusMode: 'continuous' })
  }

  await waitForCameraFocus(1001)
}

async function focusCameraForCapture(track: MediaStreamTrack | undefined) {
  if (!track) {
    return
  }

  const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
    focusMode?: string[]
  }

  if (capabilities.focusMode?.includes('single-shot')) {
    await applyOptionalCameraConstraint(track, { focusMode: 'single-shot' })
    await waitForCameraFocus(1001)
    return
  }

  if (capabilities.focusMode?.includes('continuous')) {
    await applyOptionalCameraConstraint(track, { focusMode: 'continuous' })
    await waitForCameraFocus(751)
    return
  }

  // Android WebView may omit focus capabilities while its camera still autofocuses.
  await waitForCameraFocus(1001)
}

async function applyOptionalCameraConstraint(
  track: MediaStreamTrack,
  constraint: Record<string, string | number>,
) {
  try {
    const currentConstraints = track.getConstraints()
    const currentAdvanced =
      (
        currentConstraints as MediaTrackConstraints & {
          advanced?: MediaTrackConstraintSet[]
        }
      ).advanced ?? []
    const constraintKeys = Object.keys(constraint)
    const preservedAdvanced = currentAdvanced.filter((advancedConstraint) =>
      constraintKeys.every((key) => !(key in advancedConstraint)),
    )

    await track.applyConstraints({
      ...currentConstraints,
      advanced: [...preservedAdvanced, constraint as MediaTrackConstraintSet],
    })
  } catch {
    // Optional camera controls vary by Android device and WebView version.
  }
}

function waitForCameraFocus(delay = 701): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delay)
  })
}

function waitForVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup()
      reject(new Error('Camera video did not become ready.'))
    }, 3000)

    const handleReady = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        cleanup()
        resolve()
      }
    }

    const cleanup = () => {
      window.clearTimeout(timeoutId)
      video.removeEventListener('loadedmetadata', handleReady)
      video.removeEventListener('canplay', handleReady)
    }

    video.addEventListener('loadedmetadata', handleReady)
    video.addEventListener('canplay', handleReady)
  })
}

function groupCollection(
  items: CollectionItem[],
  groupBy: CollectionGroup,
  sortBy: CollectionSort,
) {
  const groups = new Map<string, CollectionItem[]>()

  for (const item of sortCollectionItems(items, sortBy)) {
    const label = getCollectionGroupLabel(item, groupBy)
    const groupItems = groups.get(label) ?? []
    groupItems.push(item)
    groups.set(label, groupItems)
  }

  return Array.from(groups.entries())
    .map(([label, groupItems]) => ({
      label,
      items: groupItems,
      distinctCards: countDistinctCollectionCards(groupItems),
      value: groupItems.reduce(
        (total, item) => total + (item.priceUsd ?? 0) * item.quantity,
        0,
      ),
    }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

function getCoachRecommendationGroups(
  recommendation: CoachRecommendation,
  game: GameFilter,
  collection: CollectionItem[],
): CoachRecommendationGroup[] {
  const explicitGroups = recommendation.groups
    ?.filter((group) => group.cards.length > 0)
    .map((group) => ({
      ...group,
      cards: [...group.cards].sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    }))

  if (explicitGroups?.length) {
    return explicitGroups
  }

  if (recommendation.add.length < 9) {
    return []
  }

  const collectionByName = new Map(
    collection
      .filter((item) => item.game === game)
      .map((item) => [normalizeCardName(item.name), item]),
  )
  const groups = new Map<string, CoachRecommendationGroup>()

  for (const card of recommendation.add) {
    const item = collectionByName.get(normalizeCardName(card.name))
    const label = item ? getCollectionGroupLabel(item, 'color') : 'Other'
    const group = groups.get(label) ?? {
      label,
      totalQuantity: 0,
      distinctCards: 0,
      cards: [],
    }
    group.cards.push(card)
    group.totalQuantity += card.quantity
    group.distinctCards = group.cards.length
    groups.set(label, group)
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      cards: [...group.cards].sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

function sortCollectionItems(items: CollectionItem[], sortBy: CollectionSort) {
  return [...items].sort((left, right) => {
    if (sortBy === 'value') {
      return (right.priceUsd ?? 0) * right.quantity - (left.priceUsd ?? 0) * left.quantity
    }

    if (sortBy === 'quantity') {
      return right.quantity - left.quantity
    }

    if (sortBy === 'newest') {
      return new Date(right.addedAt).getTime() - new Date(left.addedAt).getTime()
    }

    return left.name.localeCompare(right.name)
  })
}

function getCollectionGroupLabel(item: CollectionItem, groupBy: CollectionGroup) {
  if (groupBy === 'set') {
    return item.setName || 'Unknown Set'
  }

  if (groupBy === 'condition') {
    return item.condition || 'Unknown Condition'
  }

  if (groupBy === 'category') {
    return item.category || 'Other'
  }

  if (item.game === 'pokemon') {
    return item.colors?.[0] ?? item.category ?? 'Pokemon'
  }

  if (item.game === 'sports') {
    return item.sport || item.category || 'Sports'
  }

  return getMtgColorLabel(item)
}

function getMtgColorLabel(item: CollectionItem) {
  if (isMtgLand(item)) {
    return 'Land'
  }

  const colors = item.colors ?? []

  if (!colors.length) {
    return 'Colorless'
  }

  if (colors.length > 1) {
    return 'Multicolor'
  }

  const labels: Record<string, string> = {
    W: 'White',
    U: 'Blue',
    B: 'Black',
    R: 'Red',
    G: 'Green',
    C: 'Colorless',
  }

  return labels[colors[0]] ?? 'Other'
}

function isMtgLand(item: CollectionItem) {
  return /\bland\b/i.test(`${item.typeLine ?? ''} ${item.category ?? ''}`)
}

function groupMtgColorSectionItems(section: string, items: CollectionItem[]) {
  if (section === 'Multicolor') {
    return groupMtgItems(items, (item) =>
      getMtgColorCombinationLabel(item.colors ?? []),
    )
  }

  if (section === 'Land') {
    return groupMtgItems(items, getMtgLandColorLabel)
  }

  return groupMtgItems(items, getMtgCardTypeLabel, true)
}

function groupMtgItems(
  items: CollectionItem[],
  getLabel: (item: CollectionItem) => string,
  sortByLabel = false,
) {
  const groups = new Map<string, CollectionItem[]>()

  for (const item of items) {
    const label = getLabel(item)
    const groupItems = groups.get(label) ?? []
    groupItems.push(item)
    groups.set(label, groupItems)
  }

  return Array.from(groups.entries())
    .map(([label, groupItems]) => ({
      label,
      items: groupItems,
      distinctCards: countDistinctCollectionCards(groupItems),
      value: groupItems.reduce(
        (total, item) => total + (item.priceUsd ?? 0) * item.quantity,
        0,
      ),
      sortKey: sortByLabel
        ? label
        : getMtgColorCombinationSortKey(
            sectionColorsForSort(groupItems[0], label),
          ),
    }))
    .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
}

function countDistinctCollectionCards(items: CollectionItem[]) {
  return new Set(items.map((item) => normalizeCardName(item.name))).size
}

function formatDistinctCardCount(count: number) {
  return `${count} different card${count === 1 ? '' : 's'}`
}

function getMtgLandColorLabel(item: CollectionItem) {
  const colors = (item.colorIdentity ?? item.colors ?? []).filter((color) =>
    ['W', 'U', 'B', 'R', 'G'].includes(color),
  )

  if (!colors.length) {
    return 'Colorless'
  }

  return getMtgColorCombinationLabel(colors)
}

function getMtgCardTypeLabel(item: CollectionItem) {
  const typeLine = `${item.typeLine ?? ''} ${item.category ?? ''}`.toLowerCase()

  if (typeLine.includes('artifact creature')) {
    return 'Artifact Creatures'
  }

  const labels: Array<[string, string]> = [
    ['artifact', 'Artifacts'],
    ['creature', 'Creatures'],
    ['planeswalker', 'Planeswalkers'],
    ['enchantment', 'Enchantments'],
    ['instant', 'Instants'],
    ['sorcery', 'Sorceries'],
    ['battle', 'Battles'],
  ]

  return labels.find(([type]) => typeLine.includes(type))?.[1] ?? 'Other'
}

function groupDeckEditorEntries(
  entries: DeckEditorEntry[],
  game: GameFilter,
  commanderName = '',
) {
  const groups = new Map<string, DeckEditorEntry[]>()
  const commanderKey = normalizeCardName(commanderName)

  for (const value of entries) {
    const item = value.item
    let label = 'Other'

    if (
      game === 'mtg' &&
      commanderKey &&
      normalizeCardName(item.name) === commanderKey
    ) {
      label = 'Commander'
    } else if (game === 'mtg') {
      label = isMtgLand(item) ? 'Lands' : getMtgCardTypeLabel(item)
    } else if (game === 'pokemon') {
      const typeText = `${item.category ?? ''} ${item.typeLine ?? ''}`.toLowerCase()

      if (typeText.includes('energy')) {
        label = 'Energy'
      } else if (typeText.includes('trainer')) {
        const trainerTypes: Array<[string, string]> = [
          ['supporter', 'Trainer - Supporters'],
          ['stadium', 'Trainer - Stadiums'],
          ['tool', 'Trainer - Tools'],
          ['item', 'Trainer - Items'],
        ]
        label =
          trainerTypes.find(([type]) => typeText.includes(type))?.[1] ??
          'Trainers'
      } else {
        label = 'Pokemon'
      }
    }

    const group = groups.get(label) ?? []
    group.push(value)
    groups.set(label, group)
  }

  const order = [
    'Commander',
    'Pokemon',
    'Creatures',
    'Artifact Creatures',
    'Planeswalkers',
    'Artifacts',
    'Enchantments',
    'Instants',
    'Sorceries',
    'Battles',
    'Trainer - Items',
    'Trainer - Supporters',
    'Trainer - Stadiums',
    'Trainer - Tools',
    'Trainers',
    'Energy',
    'Lands',
    'Other',
  ]

  return Array.from(groups.entries())
    .map(([label, groupEntries]) => ({
      label,
      entries: groupEntries,
      quantity: groupEntries.reduce(
        (total, value) => total + value.entry.quantity,
        0,
      ),
    }))
    .sort((left, right) => {
      const leftIndex = order.indexOf(left.label)
      const rightIndex = order.indexOf(right.label)
      const leftOrder = leftIndex === -1 ? order.length : leftIndex
      const rightOrder = rightIndex === -1 ? order.length : rightIndex
      return leftOrder - rightOrder || left.label.localeCompare(right.label)
    })
}

function sectionColorsForSort(item: CollectionItem | undefined, label: string) {
  if (!item) {
    return []
  }

  if (isMtgLand(item)) {
    return item.colorIdentity ?? item.colors ?? []
  }

  if (label === 'Colorless') {
    return []
  }

  return item.colors ?? []
}

function getMtgColorCombinationLabel(colors: string[]) {
  const labels: Record<string, string> = {
    W: 'White',
    U: 'Blue',
    B: 'Black',
    R: 'Red',
    G: 'Green',
  }
  const orderedColors = ['W', 'U', 'B', 'R', 'G'].filter((color) =>
    colors.includes(color),
  )

  return orderedColors.map((color) => labels[color]).join(' / ') || 'Other'
}

function getMtgColorCombinationSortKey(colors: string[]) {
  const orderedColors = ['W', 'U', 'B', 'R', 'G'].filter((color) =>
    colors.includes(color),
  )

  return `${orderedColors.length}-${orderedColors
    .map((color) => ['W', 'U', 'B', 'R', 'G'].indexOf(color))
    .join('')}`
}

export default App
